import ResponsiveContainer from "../components/DensityResponsiveContainer";
import { layoutRect, viewportHeight as layoutViewportHeight, viewportWidth as layoutViewportWidth } from "../utils/displayDensity";
/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,

} from "recharts";
import TextInputField from "../components/Fields/TextInputField";
import VirtualizedDropdown from "../components/VirtualizedDropdown/VirtualizedDropdown";

/* ===============================
   Data sources
   =============================== */
const CF_MGD = "https://d1ct3zodbaiefe.cloudfront.net";
const CF_IGDB = "https://d8io2268y22ti.cloudfront.net";

const CALENDAR_BASE_URL = `${CF_MGD}/calendar`;
const CALENDAR_MANIFEST_URL = `${CALENDAR_BASE_URL}/manifest.json`;
const CALENDAR_FILTER_INDEX_URL = `${CALENDAR_BASE_URL}/filter_index.json.gz`;
const CALENDAR_GAME_SEARCH_INDEX_URL = `${CALENDAR_BASE_URL}/game_search_index.json.gz`;
const CALENDAR_YEAR_URL = (year) => `${CALENDAR_BASE_URL}/years/${year}.json.gz`;

const MG_METADATA_URL = `${CF_MGD}/metadata.csv.gz`;
const MG_GAMESTATS_URL = `${CF_MGD}/game_stats.csv.gz`;
const IGDB_METADATA_URL = `${CF_IGDB}/igdb_database.csv.gz`;

const LATEST_FOLLOWERS_URL =
  "https://steam-scrape-production.s3.us-east-1.amazonaws.com/latest_followers.csv.gz";
const FOLLOWERS_GROWTH_URL =
  "https://steam-scrape-production.s3.us-east-1.amazonaws.com/followers_growth.csv.gz";

/* =========================
   CSV helpers
   ========================= */
async function fetchGzipCsvText(url) {
  const res = await fetch(url, { mode: "cors", cache: "no-cache" });
  if (!res.ok) throw new Error(`${url} HTTP ${res.status}`);

  const encoding = res.headers.get("content-encoding") || "";

  if (/gzip|br|deflate/i.test(encoding)) {
    return (await res.text()).trim();
  }

  if (url.endsWith(".gz") && "DecompressionStream" in window && res.body) {
    try {
      const ds = new DecompressionStream("gzip");
      const reader = res.body.pipeThrough(ds).getReader();
      const chunks = [];

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) chunks.push(value);
      }

      return (
        await new Blob(chunks, { type: "text/plain;charset=utf-8" }).text()
      ).trim();
    } catch {
      /* fall through */
    }
  }

  if (url.endsWith(".gz")) {
    const buf = await res.arrayBuffer();
    const u8 = new Uint8Array(buf);
    const looksGzip = u8.length >= 2 && u8[0] === 0x1f && u8[1] === 0x8b;

    if (looksGzip) {
      try {
        const { default: pako } = await import("pako");
        return pako.ungzip(u8, { to: "string" }).trim();
      } catch {
        /* fall through */
      }
    }

    return new TextDecoder("utf-8").decode(u8).trim();
  }

  return (await res.text()).trim();
}

function parseCsvText(text) {
  return new Promise((resolve, reject) => {
    Papa.parse(text, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: ({ data }) => resolve(data),
      error: reject,
    });
  });
}

const csvPromiseCache = new Map();

async function loadCsvOnce(url) {
  if (csvPromiseCache.has(url)) return csvPromiseCache.get(url);

  const p = (async () => {
    const text = await fetchGzipCsvText(url);
    return parseCsvText(text);
  })().catch((e) => {
    csvPromiseCache.delete(url);
    throw e;
  });

  csvPromiseCache.set(url, p);
  return p;
}


const jsonPromiseCache = new Map();

async function fetchGzipJson(url) {
  if (jsonPromiseCache.has(url)) return jsonPromiseCache.get(url);

  const p = (async () => {
    const text = await fetchGzipCsvText(url);
    return JSON.parse(text);
  })().catch((e) => {
    jsonPromiseCache.delete(url);
    throw e;
  });

  jsonPromiseCache.set(url, p);
  return p;
}

function optionArrayFromFilterIndex(raw) {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw
      .map((x) => {
        if (typeof x === "string") return { label: x, value: x, count: 0 };
        const label = x.label || x.name || x.value || x.id || "";
        const value = x.value || x.label || x.name || x.id || "";
        return {
          label: x.count !== undefined ? `${label} (${Number(x.count).toLocaleString()})` : label,
          value,
          count: Number(x.count || 0),
        };
      })
      .filter((x) => x.value)
      .sort((a, b) => (b.count || 0) - (a.count || 0) || String(a.value).localeCompare(String(b.value)));
  }

  if (typeof raw === "object") {
    return Object.entries(raw)
      .map(([key, value]) => {
        if (value && typeof value === "object") {
          const label = value.label || value.name || key;
          const count = Number(value.count || value.total || 0);
          return {
            label: count ? `${label} (${count.toLocaleString()})` : label,
            value: value.value || value.label || value.name || key,
            count,
          };
        }

        const count = Number(value || 0);
        return {
          label: count ? `${key} (${count.toLocaleString()})` : key,
          value: key,
          count,
        };
      })
      .filter((x) => x.value)
      .sort((a, b) => (b.count || 0) - (a.count || 0) || String(a.value).localeCompare(String(b.value)));
  }

  return [];
}


function getCalendarRelativeUrl(baseUrl, maybePath, fallbackUrl = "") {
  const raw = String(maybePath || "").trim();
  if (!raw) return fallbackUrl;
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${baseUrl}/${raw.replace(/^\/+/, "")}`;
}

function normalizeGameSearchIndex(raw) {
  const source = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.games)
      ? raw.games
      : Array.isArray(raw?.items)
        ? raw.items
        : Array.isArray(raw?.titles)
          ? raw.titles
          : raw && typeof raw === "object"
            ? Object.values(raw)
            : [];

  const rows = source
    .map((x, idx) => {
      if (!x) return null;
      const name = String(x.name || x.title || x.game || x.label || "").trim();
      if (!name) return null;

      const rawYears = x.years || x.release_years || x.releaseYears || x.available_years || x.availableYears || [];
      const rawDates = x.release_dates || x.releaseDates || x.dates || [];
      const years = Array.from(
        new Set(
          [
            ...(Array.isArray(rawYears) ? rawYears : String(rawYears || "").split(/[,;| ]+/)),
            ...(Array.isArray(rawDates) ? rawDates : String(rawDates || "").split(/[,;| ]+/))
              .map((d) => String(d || "").slice(0, 4)),
            x.earliest_release_date ? String(x.earliest_release_date).slice(0, 4) : "",
            x.latest_release_date ? String(x.latest_release_date).slice(0, 4) : "",
          ]
            .map((y) => Number(y))
            .filter((y) => Number.isFinite(y) && y > 1969 && y < 2100)
        )
      ).sort((a, b) => a - b);

      const dates = Array.from(new Set([
        ...(Array.isArray(rawDates) ? rawDates : String(rawDates || "").split(/[,;|]+/)),
        x.earliest_release_date,
        x.latest_release_date,
        x.primary_release_date,
      ].map(calendarDateString).filter(Boolean))).sort();

      return {
        ...x,
        id: x.game_key || x.key || x.id || x.steam_appid || x.steam_app_id || x.igdb_id || `${name}-${idx}`,
        name,
        normalizedName: normName(name),
        searchText: normName(`${name} ${x.franchise || ""} ${x.series || ""} ${x.aliases || ""}`),
        years,
        dates,
        earliest_release_date: calendarDateString(x.earliest_release_date) || dates[0] || "",
        latest_release_date: calendarDateString(x.latest_release_date) || dates[dates.length - 1] || "",
        release_count: Number(x.release_count || x.releaseCount || x.event_count || x.eventCount || dates.length || years.length || 0) || 0,
      };
    })
    .filter(Boolean);

  return rows.sort((a, b) => {
    const ac = Number(a.release_count || 0);
    const bc = Number(b.release_count || 0);
    if (bc !== ac) return bc - ac;
    return a.name.localeCompare(b.name);
  });
}

function getSearchIndexYears(row = {}) {
  return Array.from(new Set((row.years || [])
    .map((y) => Number(y))
    .filter((y) => Number.isFinite(y) && y > 1969 && y < 2100)))
    .sort((a, b) => a - b);
}

function getSearchIndexTargetMonth(row = {}, preferLatest = true) {
  const dates = Array.isArray(row.dates) ? row.dates.filter(Boolean).sort() : [];
  const target = preferLatest
    ? (calendarDateString(row.latest_release_date) || dates[dates.length - 1])
    : (calendarDateString(row.earliest_release_date) || dates[0]);
  return target ? target.slice(0, 7) : "";
}


function rawLabelFromOption(raw) {
  if (raw === undefined || raw === null) return "";
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "object") {
    return String(raw.value || raw.label || raw.name || raw.id || raw.type || "").trim();
  }
  return String(raw).trim();
}

function findContentTypeIndex(filterJson) {
  if (!filterJson || typeof filterJson !== "object") return null;

  const directKeys = [
    "content_types",
    "contentTypes",
    "content_type",
    "contentType",
    "content_type_options",
    "contentTypeOptions",
    "release_content_types",
    "releaseContentTypes",
    "types",
    "steam_types",
    "steamTypes",
  ];

  for (const key of directKeys) {
    if (filterJson[key]) return filterJson[key];
  }

  for (const [key, value] of Object.entries(filterJson)) {
    if (/content|steam.*type|type/i.test(key) && value && (Array.isArray(value) || typeof value === "object")) {
      return value;
    }
  }

  return null;
}

function contentTypeOptionsFromFilterJson(filterJson) {
  const counts = new Map();

  const add = (raw, count = 0) => {
    const label = normalizeContentTypeName(rawLabelFromOption(raw));
    if (!label) return;
    counts.set(label, (counts.get(label) || 0) + (Number(count) || 0));
  };

  const ingest = (node, depth = 0) => {
    if (!node || depth > 4) return;

    if (Array.isArray(node)) {
      node.forEach((x) => {
        if (typeof x === "string") add(x, 0);
        else if (x && typeof x === "object") {
          add(x.value || x.label || x.name || x.id || x.type, x.count || x.total || 0);
        }
      });
      return;
    }

    if (typeof node === "object") {
      Object.entries(node).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          ingest(value, depth + 1);
          return;
        }

        if (value && typeof value === "object") {
          const label = value.value || value.label || value.name || value.id || value.type || key;
          const count = value.count || value.total || 0;
          add(label, count);
          return;
        }

        // Object form like { "Main Game": 76723, "DLC": 34000 }
        add(key, value);
      });
    }
  };

  const explicit = findContentTypeIndex(filterJson);
  ingest(explicit);

  // If the helper grabbed only one shallow bucket, scan likely keys everywhere.
  const scan = (node, depth = 0) => {
    if (!node || depth > 3 || typeof node !== "object") return;

    Object.entries(node).forEach(([key, value]) => {
      if (/content|steam.*type|(^|_)type(s)?$/i.test(key)) ingest(value, depth + 1);
      if (value && typeof value === "object") scan(value, depth + 1);
    });
  };
  scan(filterJson);

  const knownOrder = [
    "Main Game",
    "DLC",
    "Demo",
    "Bundle",
    "Expansion",
    "Pack / Addon",
    "Episode",
    "Season",
    "Mod",
    "Music",
    "Video",
    "Tool",
    "Hardware",
    "Remake",
    "Remaster",
    "Standalone Expansion",
    "Port",
  ];

  return Array.from(counts.entries())
    .map(([value, count]) => ({
      value,
      count,
      label: count ? `${value} (${Number(count).toLocaleString()})` : value,
    }))
    .filter((x) => x.value)
    .sort((a, b) => {
      const ai = knownOrder.indexOf(a.value);
      const bi = knownOrder.indexOf(b.value);
      if (ai >= 0 || bi >= 0) return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
      return (b.count || 0) - (a.count || 0) || String(a.value).localeCompare(String(b.value));
    });
}

function normalizeContentTypeName(raw = "") {
  const s = String(raw || "").trim();
  if (!s) return "";

  const clean = s.replace(/\s*\([\d,]+\)\s*$/, "").trim();
  const n = clean.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();

  const map = {
    game: "Main Game",
    main: "Main Game",
    "main game": "Main Game",
    base: "Main Game",
    "base game": "Main Game",
    dlc: "DLC",
    demo: "Demo",
    bundle: "Bundle",
    mod: "Mod",
    expansion: "Expansion",
    "expanded game": "Expanded Game",
    pack: "Pack / Addon",
    addon: "Pack / Addon",
    "add on": "Pack / Addon",
    "pack / addon": "Pack / Addon",
    episode: "Episode",
    season: "Season",
    music: "Music",
    soundtrack: "Music",
    video: "Video",
    tool: "Tool",
    hardware: "Hardware",
    port: "Port",
    remake: "Remake",
    remaster: "Remaster",
    "standalone expansion": "Standalone Expansion",
  };

  return map[n] || clean;
}

function cleanSteamId(raw) {
  if (raw === undefined || raw === null) return "";
  let s = String(raw).trim();
  if (!s || ["nan", "none", "null", "0", "0.0", "-", "--"].includes(s.toLowerCase())) return "";
  if (/^\d+\.0$/.test(s)) s = s.slice(0, -2);
  const m = s.match(/\d+/);
  return m ? m[0] : "";
}

function getEventSteamId(e = {}) {
  return (
    cleanSteamId(e.steam_appid) ||
    cleanSteamId(e.app_id) ||
    cleanSteamId(e.Steam_ID) ||
    cleanSteamId(e.steam_id) ||
    cleanSteamId(e.steamId)
  );
}

function exportedBoolean(value, fallback = false) {
  if (value === true || value === false) return value;
  if (value === undefined || value === null || value === "") return fallback;
  const s = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(s)) return true;
  if (["false", "0", "no", "n"].includes(s)) return false;
  return fallback;
}

function normalizeSelectedContentTypeValue(selected) {
  return normalizeContentTypeName(
    rawLabelFromOption(selected).replace(/\s*\([\d,]+\)\s*$/, "")
  );
}

function inferContentType(e = {}) {
  const candidates = [
    e.igdb_content_type,
    e.igdbContentType,
    e.igdb_type,
    e.igdbType,
    e.release_content_type,
    e.releaseContentType,
    e.content_type_label,
    e.contentTypeLabel,
    e.content_type_raw,
    e.contentTypeRaw,
    e.steam_type,
    e.steamType,
    e.type,
    e.content_type,
    e.contentType,
  ]
    .map((x) => normalizeContentTypeName(rawLabelFromOption(x)))
    .filter(Boolean);

  const nonMain = candidates.find((x) => x && x !== "Main Game");
  if (nonMain) return nonMain;
  if (candidates.length) return candidates[0];

  const text = `${e.name || ""} ${e.slug || ""}`.toLowerCase();
  if (/\bdlc\b|downloadable content|expansion pass|season pass|character pack|supporter pack/.test(text)) return "DLC";
  if (/\bdemo\b/.test(text)) return "Demo";
  if (/\bsoundtrack\b|ost\b/.test(text)) return "Music";

  return "Main Game";
}

function defaultContentVisibility(e = {}, contentType = "") {
  if (e.show_by_default !== undefined) return e.show_by_default !== false;
  if (e.showByDefault !== undefined) return e.showByDefault !== false;

  const cls = String(e.content_class || e.contentClass || "").toLowerCase();
  if (cls && /extra|addon|dlc|demo|mod|bundle|pack|music|video|tool|hardware/.test(cls)) return false;

  return ![
    "DLC",
    "Demo",
    "Bundle",
    "Pack / Addon",
    "Expansion",
    "Episode",
    "Season",
    "Mod",
    "Music",
    "Video",
    "Tool",
    "Hardware",
  ].includes(contentType);
}

function extractPlatformDateList(e = {}) {
  const out = [];

  const addRaw = (raw) => {
    const s = String(raw || "");
    const matches = s.match(/\b\d{4}-\d{1,2}-\d{1,2}\b/g) || [];
    matches.forEach((m) => {
      const d = calendarDateString(m);
      if (d) out.push(d);
    });
  };

  const prd = e.platform_release_dates ?? e.platformReleaseDates;
  if (prd && typeof prd === "object" && !Array.isArray(prd)) {
    Object.values(prd).forEach((v) => addRaw(v));
  } else {
    addRaw(prd);
  }

  addRaw(e.platform_release_dates_text);
  addRaw(e.platformReleaseDatesText);

  return Array.from(new Set(out));
}

function canonicalReleaseDateForEvent(e = {}) {
  const rawDate = calendarDateString(e.date || e.release_date || e.releaseDate);
  if (!rawDate) return "";

  const platformDates = extractPlatformDateList(e);
  if (!platformDates.length || platformDates.includes(rawDate)) return rawDate;

  const rawDay = dayjs(rawDate);
  const nearest = platformDates
    .map((d) => ({ d, diff: Math.abs(dayjs(d).diff(rawDay, "day")) }))
    .sort((a, b) => a.diff - b.diff)[0];

  // Fix Steam/UTC one-day drift without breaking true staggered platform releases.
  if (nearest && nearest.diff <= 1) return nearest.d;

  return rawDate;
}

function isSecondaryReleaseEvent(e = {}) {
  const eventDate = calendarDateString(e.release_date || e.date || e.releaseDate);
  if (!eventDate) return false;

  const dates = extractPlatformDateList(e)
    .map(calendarDateString)
    .filter(Boolean)
    .sort();

  if (!dates.length) return false;

  const earliest = dates[0];
  return eventDate > earliest;
}

function dedupeCalendarEvents(rows = []) {
  const map = new Map();

  const quality = (g = {}) => {
    let q = 0;
    if (g.box_art || g.image) q += 4;
    if (g.video) q += 3;
    if (g.short_description || g.description) q += 2;
    if (g.platformReleaseDatesText || g.platform_release_dates_text) q += 2;
    if (Number(g.followers || g.score || 0) > 0) q += 2;
    if (String(g.release_source || g.source || "").toLowerCase().includes("steam")) q += 1;
    return q;
  };

  for (const g of rows) {
    const steamId = getEventSteamId(g);
    const date = calendarDateString(g.release_date || g.date || g.releaseDate);
    const contentType = normalizeContentTypeName(g.content_type || g.igdb_content_type || g.steam_type || "").toLowerCase();
    const releasePhase = normalizeReleasePhase(g.release_phase || g.releasePhase || "");

    // Systematic final frontend safety net: Steam ID wins over game_key/name.
    // This catches Steam/IGDB/mindGAME copies that represent the same product.
    const idPart = steamId
      ? `steam:${steamId}`
      : `no-steam:${String(g.game_key || g.igdb_id || g.slug || normName(g.name) || "").toLowerCase()}`;

    const key = [idPart, date, contentType, releasePhase].join("|");
    const prev = map.get(key);

    if (!prev) {
      map.set(key, g);
      continue;
    }

    const best = quality(g) >= quality(prev) ? g : prev;
    const other = best === g ? prev : g;

    map.set(key, {
      ...other,
      ...best,
      platformBuckets: Array.from(new Set([...(other.platformBuckets || []), ...(best.platformBuckets || [])])),
      eventPlatformBuckets: Array.from(new Set([...(other.eventPlatformBuckets || []), ...(best.eventPlatformBuckets || [])])),
      platformExclusiveBuckets: Array.from(new Set([...(other.platformExclusiveBuckets || []), ...(best.platformExclusiveBuckets || [])])),
      tags: Array.from(new Set([...(other.tags || []), ...(best.tags || [])])),
      source: [other.source, best.source].filter(Boolean).join("+") || best.source || other.source,
    });
  }

  return Array.from(map.values());
}

function normalizeCalendarEvent(e) {
  const score = Number(e?.score || 0) || 0;
  const steamAppId = getEventSteamId(e);
  const rawDate =
  calendarDateString(
    e?.date ||
    e?.release_date ||
    e?.releaseDate
  );
  const contentType = inferContentType(e);

  const exportedSecondary =
  exportedBoolean(
    e?.is_secondary_platform_release ??
    e?.isSecondaryPlatformRelease,
    false
  );

  const gameId = steamAppId || e?.id || e?.game_key || e?.igdb_id || e?.name;

  return {
    ...e,
    app_id: steamAppId || gameId,
    steam_appid: steamAppId || String(e?.steam_appid || "").trim(),
    isNonSteam: !steamAppId,
    isIgdbOnly: !steamAppId && Boolean(e?.igdb_id),
    name: String(e?.name || "").trim(),
    release_date: rawDate,
    release_phase: normalizeReleasePhase(e?.release_phase || e?.releasePhase),
    release_label: releaseLabelForEvent(e),
    release_source: e?.release_source || e?.releaseSource || e?.source || "",
    isEarlyAccess: isEarlyAccessEvent(e),
    isSecondaryPlatformRelease: exportedSecondary,
    followers: score,
    steam_followers: Number(e?.steam_followers || 0) || 0,
    search_volume: Number(e?.search_volume || 0) || 0,
    score_source: e?.score_source || "none",
    box_art: e?.image || e?.box_art || "",
    short_description: e?.description || e?.short_description || "",
    publishers: e?.publisher || e?.publishers || "",
    developers: e?.developer || e?.developers || "",
    tags: Array.isArray(e?.tags)
      ? e.tags.filter(Boolean)
      : String(e?.tags || "")
          .split(/[,;|]+/)
          .map((t) => t.trim())
          .filter(Boolean),
    adult: Boolean(e?.adult),
    content_type: contentType,
    content_class: e?.content_class || e?.contentClass || (defaultContentVisibility(e, contentType) ? "base_game" : "extra_content"),
    show_by_default: defaultContentVisibility(e, contentType),
    steam_type: e?.steam_type || e?.steamType || e?.type || "",
    video: e?.video || "",
    platformText:
      compactPlatformDisplayText(
        e?.platform_text ||
        (Array.isArray(e?.platforms_raw) ? e.platforms_raw.join(", ") : "")
      ),
    platformReleaseDatesText:
      e?.platform_release_dates_text ||
      (e?.platform_release_dates && typeof e.platform_release_dates === "object"
        ? Object.entries(e.platform_release_dates)
            .map(([platform, date]) => `${platform}: ${date}`)
            .join(" · ")
        : ""),
    platformBuckets: Array.isArray(e?.all_platforms) ? e.all_platforms : [],
    eventPlatformBuckets: Array.isArray(e?.event_platforms) ? e.event_platforms : [],
    platformExclusiveBuckets: Array.isArray(e?.exclusive_platforms) ? e.exclusive_platforms : [],
  };
}

/* ======================
   Helpers
   ====================== */
const normName = (s = "") =>
  s
    .toLowerCase()
    .replace(/[®™]/g, "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

function isNonSteamId(raw) {
  if (raw === undefined || raw === null) return true;
  const s = String(raw).trim();
  if (!s || s === "-" || s === "--") return true;
  const n = parseInt(s, 10);
  return !Number.isFinite(n) || n <= 0;
}

function normalizeCommaSuffixes(raw = "") {
  return raw.replace(
    /,\s*(inc\.?|ltd\.?|llc\.?|corp\.?|corporation|co\.?(?:,\s*ltd\.?)?|gmbh|plc|s\.a\.|s\.r\.l\.|pte\.?\s*ltd\.?|k\.k\.)\b/gi,
    " $1"
  );
}

function splitMultiParty(raw = "") {
  const cleaned = normalizeCommaSuffixes(raw);
  return cleaned
    .split(/[,;|/]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function monthKeyFromDate(d) {
  return dayjs(d).startOf("month").format("YYYY-MM");
}

function monthLabel(key) {
  return dayjs(`${key}-01`).format("MMMM YYYY");
}

function shortMonthLabel(key) {
  return dayjs(`${key}-01`).format("MMM ’YY");
}

function formatScore(n) {
  const v = Number(n) || 0;
  if (v >= 1000000) return `${(v / 1000000).toFixed(v >= 10000000 ? 0 : 1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}K`;
  return v.toLocaleString();
}

function normalizeReleasePhase(raw = "") {
  const s = String(raw || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!s) return "";
  if (s.includes("early_access")) return "early_access";
  if (s.includes("full_release") || s === "full") return "full_release";
  if (s.includes("platform_release")) return "platform_release";
  return s;
}

function releaseLabelForEvent(e = {}) {
  const explicit = cleanMetaText(e.release_label || e.releaseLabel || e.release_phase_label || e.releasePhaseLabel);
  if (explicit) return explicit;

  const phase = normalizeReleasePhase(e.release_phase || e.releasePhase);
  const source = String(e.release_source || e.releaseSource || e.source || "").toLowerCase();

  if (phase === "early_access" || source.includes("early_access")) return "Early Access";
  if (phase === "full_release") return "Full Release";
  if (phase === "platform_release") return "Platform Release";
  return "";
}

function isEarlyAccessEvent(e = {}) {
  return normalizeReleasePhase(e.release_phase || e.releasePhase) === "early_access" ||
    String(e.release_source || e.releaseSource || e.source || "").toLowerCase().includes("early_access");
}

function isGta6Name(name = "") {
  const n = normName(name);
  return (
    n === "grand theft auto vi" ||
    n === "grand theft auto 6" ||
    n === "gta vi" ||
    n === "gta 6" ||
    n === "gta6"
  );
}

function parseReleaseDateSafe(raw) {
  if (raw === undefined || raw === null) return null;
  const s = String(raw).trim();
  if (!s || s === "TBD" || s === "N/A" || s === "-") return null;

  // Important: preserve the calendar date from source files.
  // Some Steam rows arrive as UTC timestamps like "2026-05-05 00:00:00+00:00";
  // parsing those directly in a PT browser shifts them to May 4.
  const ymd = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (ymd) {
    const d = dayjs(`${ymd[1]}-${String(ymd[2]).padStart(2, "0")}-${String(ymd[3]).padStart(2, "0")}`);
    if (d.isValid()) return d;
  }

  const mdyLoose = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (mdyLoose) {
    const yyyy = mdyLoose[3].length === 2 ? `20${mdyLoose[3]}` : mdyLoose[3];
    const d = dayjs(`${yyyy}-${String(mdyLoose[1]).padStart(2, "0")}-${String(mdyLoose[2]).padStart(2, "0")}`);
    if (d.isValid()) return d;
  }

  const direct = dayjs(s);
  if (direct.isValid()) return direct;

  const mdy = dayjs(s, ["M/D/YYYY", "MM/DD/YYYY", "M/D/YY", "MMM D, YYYY", "MMMM D, YYYY"], true);
  return mdy.isValid() ? mdy : null;
}

function calendarDateString(rawOrDayjs) {
  const d = dayjs.isDayjs(rawOrDayjs) ? rawOrDayjs : parseReleaseDateSafe(rawOrDayjs);
  return d && d.isValid() ? d.format("YYYY-MM-DD") : "";
}


/* =====================
   Platform helpers
   ===================== */
const PLATFORM_FILTERS = [
  {
    key: "pc",
    label: "Steam / PC",
    shortLabel: "PC",
    iconSrc: "/Steam Logo Calendar.png",
    iconFallback: "PC",
  },
  {
    key: "playstation",
    label: "PlayStation",
    shortLabel: "PlayStation",
    iconSrc: "/PlayStation Logo.png",
    iconFallback: "PS",
  },
  {
    key: "xbox",
    label: "Xbox",
    shortLabel: "Xbox",
    iconSrc: "/Xbox Logo.png",
    iconFallback: "XB",
  },
  {
    key: "nintendo",
    label: "Nintendo",
    shortLabel: "Nintendo",
    iconSrc: "/Nintendo Logo.png",
    iconFallback: "Nintendo",
  },
  {
    key: "mobile",
    label: "Mobile Releases",
    shortLabel: "Mobile",
    iconSrc: "/Mobile Android iOS Logo.png",
    iconFallback: "Mobile",
  },
];

const PLATFORM_KEYS = PLATFORM_FILTERS.map((p) => p.key);
const TREND_LINE_COLORS = ["#3fa9f5", "#6edc8f", "#ffcf5a", "#ff7aa8", "#b58cff"];

function truthyPlatformValue(v) {
  if (v === true) return true;
  if (v === false || v === undefined || v === null) return false;
  const s = String(v).trim().toLowerCase();
  if (!s) return false;
  return ![
    "0",
    "false",
    "no",
    "n",
    "none",
    "null",
    "nan",
    "-",
    "--",
    "\\n",
    "n/a",
    "na",
    "tbd",
    "unknown",
  ].includes(s);
}

function getRowValue(row, candidates) {
  if (!row) return "";
  const keys = Object.keys(row);
  for (const want of candidates) {
    if (row[want] !== undefined && row[want] !== null && String(row[want]).trim() !== "") {
      return row[want];
    }
    const found = keys.find((k) => k.toLowerCase() === String(want).toLowerCase());
    if (found && row[found] !== undefined && row[found] !== null && String(row[found]).trim() !== "") {
      return row[found];
    }
  }
  return "";
}

function rowHasTruthy(row, candidates) {
  return truthyPlatformValue(getRowValue(row, candidates));
}

function splitPlatformParts(raw = "") {
  return String(raw || "")
    // Keep Xbox Series X|S intact enough for regex matching, but still split
    // normal CSV-style lists safely.
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function addPlatformFromText(target, rawText) {
  const raw = String(rawText || "");
  const text = raw
    .toLowerCase()
    .replace(/x\s*\|\s*s/g, "x s")
    .replace(/series\s*x\s*\/\s*s/g, "series x s");
  if (!text) return;

  // IGDB plain platforms examples:
  // "Linux, Nintendo Switch, PC (Microsoft Windows), PlayStation 5, Xbox Series X|S"
  // mindGAME examples:
  // "Xbox Series X/S", "Switch 2", "PC"
  if (/(pc \(microsoft windows\)|microsoft windows|\bwindows\b|\bpc\b|linux|mac|steam|epic games store|gog)/i.test(text)) {
    target.add("pc");
  }
  if (/(playstation|\bps5\b|\bps4\b|\bps3\b|\bps2\b|ps vita|\bvita\b|psvr|ps vr)/i.test(text)) {
    target.add("playstation");
  }
  if (/(xbox|series x|series s|series x s|xbox one|xbox 360|game pass)/i.test(text)) {
    target.add("xbox");
  }
  if (/(nintendo|switch|switch 2|wii|wii u|3ds|\bds\b|gamecube|n64|game boy|gba)/i.test(text)) {
    target.add("nintendo");
  }
  if (/(mobile|ios|iphone|ipad|android|google play|app store)/i.test(text)) {
    target.add("mobile");
  }
  if (/(legacy|other|arcade|retro|browser|web|nes|snes|genesis|dreamcast|saturn|atari|commodore|amiga)/i.test(text)) {
    target.add("legacy");
  }
}

function addPlatformFieldsFromRow(target, row, candidates) {
  for (const key of candidates) {
    const value = getRowValue(row, [key]);
    if (truthyPlatformValue(value)) addPlatformFromText(target, value);
  }
}

function looseNameKeys(...values) {
  const keys = new Set();
  for (const value of values) {
    const raw = String(value || "").trim();
    if (!raw) continue;
    const n = normName(raw);
    if (!n) continue;
    keys.add(n);
    keys.add(n.replace(/[^a-z0-9]+/g, ""));
    keys.add(n.replace(/[^a-z0-9]+/g, "-"));
    if (n.endsWith("s")) keys.add(n.slice(0, -1));
    if (!n.endsWith("s")) keys.add(`${n}s`);
  }
  return Array.from(keys).filter(Boolean);
}

function addToMultiMap(map, key, row) {
  if (!key || !row) return;
  const arr = map.get(key) || [];
  arr.push(row);
  map.set(key, arr);
}

function uniqueRows(rows = []) {
  const seen = new Set();
  const out = [];
  for (const row of rows || []) {
    if (!row) continue;
    const id = getFirstRowValue(row, ["igdb_id", "id", "ID", "game_id", "Game ID"]);
    const name = getFirstRowValue(row, ["name", "Name", "game", "Game", "title", "Title"]);
    const slug = getFirstRowValue(row, ["slug", "Slug"]);
    const key = id || `${normName(name)}|${slug}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

function getIgdbReleaseDate(row) {
  const raw = getFirstRowValue(row, [
    "first_release_date",
    "First Release Date",
    "release_date",
    "Release Date",
    "date",
    "Date",
  ]);
  return parseReleaseDateSafe(raw);
}

function scoreIgdbCandidate(row, releaseDate = null) {
  let score = 0;
  const buckets = platformBucketsFromIgdbRow(row);
  if (buckets.size) score += 100;
  if (firstCleanRowValue(row, ["platform_release_dates", "platform_release_date", "Platform Release Dates"])) score += 15;

  const rel = releaseDate ? dayjs(releaseDate) : null;
  const igdbRel = getIgdbReleaseDate(row);
  if (rel?.isValid() && igdbRel?.isValid()) {
    const diff = Math.abs(igdbRel.startOf("day").diff(rel.startOf("day"), "day"));
    if (diff <= 2) score += 80;
    else if (diff <= 7) score += 55;
    else if (igdbRel.isSame(rel, "month")) score += 35;
    else score -= Math.min(diff, 90);
  }

  return score;
}

function pickBestIgdbRow(rows = [], releaseDate = null) {
  const candidates = uniqueRows(rows);
  if (!candidates.length) return null;
  return candidates
    .map((row) => ({ row, score: scoreIgdbCandidate(row, releaseDate) }))
    .sort((a, b) => b.score - a.score)[0].row;
}

function getFirstRowValue(row, candidates) {
  const value = getRowValue(row, candidates);
  return value === undefined || value === null ? "" : String(value).trim();
}


function cleanMetaText(value) {
  const s = String(value ?? "").trim();
  if (!s || ["\\N", "N/A", "NA", "null", "undefined", "-", "--"].includes(s)) return "";
  return s;
}

function shortPlatformDisplayLabel(raw = "") {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^pc\s*\(\s*microsoft windows\s*\)$/i.test(s)) return "PC";
  return s.replace(/PC\s*\(\s*Microsoft Windows\s*\)/gi, "PC");
}

function compactPlatformDisplayText(raw = "") {
  return String(raw || "")
    .split(/[,;]+/)
    .map((x) => shortPlatformDisplayLabel(x.trim()))
    .filter(Boolean)
    .join(", ");
}

function platformMatchKey(raw = "") {
  const s = shortPlatformDisplayLabel(raw)
    .toLowerCase()
    .replace(/x\s*\|\s*s/g, "x s")
    .replace(/series\s*x\s*\/\s*s/g, "series x s")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  if (!s) return "";
  if (/^(pc|windows|microsoft windows|linux|mac|steam)$/.test(s)) return "pc";
  if (/^(playstation|ps)$/.test(s)) return "playstation";
  if (/^(ps5|playstation 5)$/.test(s)) return "playstation 5";
  if (/^(ps4|playstation 4)$/.test(s)) return "playstation 4";
  if (/^(xbox)$/.test(s)) return "xbox";
  if (/^(xbox series x s|series x s|xbox series x|xbox series s)$/.test(s)) return "xbox series";
  if (/^(xbox one)$/.test(s)) return "xbox one";
  if (/^(nintendo|switch|nintendo switch)$/.test(s)) return "nintendo switch";
  if (/^(switch 2|nintendo switch 2)$/.test(s)) return "nintendo switch 2";
  if (/^(mobile|ios|iphone|ipad|android)$/.test(s)) return s;
  return s;
}

function parsePlatformReleaseDateEntries(raw = "") {
  const s = cleanMetaText(raw);
  if (!s) return [];

  return s
    .split(/[,;·]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const idx = part.lastIndexOf(":");
      if (idx <= 0) return null;

      const platform = shortPlatformDisplayLabel(part.slice(0, idx).trim());
      const rawDate = part.slice(idx + 1).trim();
      const d = parseReleaseDateSafe(rawDate);
      if (!platform || !d?.isValid()) return null;

      return {
        platform,
        platformKey: platformMatchKey(platform),
        dateISO: d.format("YYYY-MM-DD"),
        dateLabel: d.format("MMM D, YYYY"),
      };
    })
    .filter(Boolean);
}

function platformDrivesDate(platform, itemDateISO, platformEntries = []) {
  const key = platformMatchKey(platform);
  if (!key || !itemDateISO || !platformEntries.length) return false;

  return platformEntries.some((entry) => {
    if (entry.dateISO !== itemDateISO) return false;
    if (entry.platformKey === key) return true;

    // Bucket labels like PlayStation / Xbox / Nintendo should highlight when the
    // date-specific platforms are PS4/PS5, Xbox One/Series, or Switch/Switch 2.
    if (key === "playstation" && entry.platformKey.startsWith("playstation")) return true;
    if (key === "xbox" && entry.platformKey.startsWith("xbox")) return true;
    if (key === "nintendo switch" && entry.platformKey.startsWith("nintendo switch")) return true;
    return false;
  });
}

function firstCleanRowValue(row, candidates) {
  return cleanMetaText(getRowValue(row, candidates));
}

function formatPlatformLabel(key) {
  return ({
    pc: "PC",
    playstation: "PlayStation",
    xbox: "Xbox",
    nintendo: "Nintendo",
    mobile: "Mobile",
    legacy: "Other",
  })[key] || key;
}

function compactPlatformList(raw = "") {
  return compactPlatformDisplayText(raw);
}

function parsePlatformReleaseDatesText(raw = "") {
  const s = cleanMetaText(raw);
  if (!s) return "";
  // IGDB format: Android:2024-10-22, Nintendo Switch:2024-10-22, PC (Microsoft Windows):2024-10-22
  return s
    .split(/[,;]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const idx = part.lastIndexOf(":");
      if (idx <= 0) return part;
      const platform = shortPlatformDisplayLabel(part.slice(0, idx).trim());
      const date = part.slice(idx + 1).trim();
      const d = parseReleaseDateSafe(date);
      return `${platform}: ${d && d.isValid() ? d.format("MMM D, YYYY") : date}`;
    })
    .join(" · ");
}

function getPlatformTextForGame(platformBuckets = [], igdbRow = null, mindgameRow = null) {
  const igdbPlatforms = compactPlatformList(firstCleanRowValue(igdbRow, [
    "platforms", "Platforms", "platform", "Platform", "platform_names", "Platform Names",
    "platform_name", "Platform Name", "igdb_platforms", "IGDB Platforms",
  ]));
  if (igdbPlatforms) return igdbPlatforms;

  const mgPlatforms = compactPlatformList(firstCleanRowValue(mindgameRow, [
    "Platforms", "platforms", "Platform Availability", "Platform_Availability",
    "Platform Availability (PC, PlayStation, Xbox, Switch, Mobile)",
  ]));
  if (mgPlatforms) return mgPlatforms;

  return (platformBuckets || []).map(formatPlatformLabel).join(", ");
}

function getPlatformReleaseDatesForGame(igdbRow = null, mindgameRow = null) {
  const igdbDates = parsePlatformReleaseDatesText(firstCleanRowValue(igdbRow, [
    "platform_release_dates", "platform_release_date", "Platform Release Dates",
    "platforms_release_dates", "release_platforms",
  ]));
  if (igdbDates) return igdbDates;

  return parsePlatformReleaseDatesText(firstCleanRowValue(mindgameRow, [
    "platform_release_dates", "Platform Release Dates", "release_platforms",
  ]));
}

function normalizeVideoUrl(raw = "") {
  const s = cleanMetaText(raw);
  if (!s) return "";
  const first = s.split(/[,\s]+/).find((x) => /^https?:\/\//i.test(x));
  return first || s;
}

function getVideoUrlForGame(steamRow = null, igdbRow = null, mindgameRow = null) {
  return normalizeVideoUrl(firstCleanRowValue(steamRow, [
    "video", "trailer", "video_url", "trailer_url", "steam_video", "Steam Video",
    "movies", "movie", "Movie", "Trailer", "Trailer URL",
  ])) || normalizeVideoUrl(firstCleanRowValue(igdbRow, [
    "video", "videos", "trailer", "trailer_url", "youtube", "youtube_url", "YouTube",
    "Youtube", "YouTube URL", "video_url", "Video URL",
  ])) || normalizeVideoUrl(firstCleanRowValue(mindgameRow, [
    "video", "trailer", "Trailer", "Trailer_URL", "trailer_url", "youtube", "youtube_url",
  ]));
}

function getIgdbImageUrl(row) {
  const raw = firstCleanRowValue(row, [
    "box_art", "boxart", "cover_url", "cover", "image", "image_url", "ImageURL",
    "artwork", "artwork_url", "screenshot", "screenshot_url", "url",
  ]);
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  return raw;
}

function getIgdbId(row, fallback = "") {
  return firstCleanRowValue(row, ["igdb_id", "id", "ID", "game_id", "Game ID"]) || fallback;
}

function getYouTubeEmbedUrl(url = "") {
  const s = cleanMetaText(url);
  if (!s) return "";
  const directId = s.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/i)?.[1];
  const paramId = s.match(/[?&]v=([A-Za-z0-9_-]{6,})/i)?.[1];
  const bareId = /^[A-Za-z0-9_-]{11}$/.test(s) ? s : "";
  const id = directId || paramId || bareId;
  if (!id) return "";
  return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1`;
}

function isDirectVideoUrl(url = "") {
  return /\.(mp4|webm|ogg)(\?|#|$)/i.test(String(url || ""));
}

function platformBucketsFromMindgameRow(row) {
  const buckets = new Set();
  if (!row) return buckets;

  // Explicit mindGAME platform columns. These should win over vague text fields.
  if (
    rowHasTruthy(row, ["PC", "Windows", "Steam_PC", "PC (Microsoft Windows)"])
  ) buckets.add("pc");

  if (
    rowHasTruthy(row, ["PlayStation_5", "PlayStation 5", "PS5"]) ||
    rowHasTruthy(row, ["PlayStation_4", "PlayStation 4", "PS4"]) ||
    rowHasTruthy(row, ["PlayStation", "Sony"])
  ) buckets.add("playstation");

  if (
    rowHasTruthy(row, ["Xbox_Series_S_X", "Xbox Series S/X", "Xbox Series X|S", "Xbox Series X", "Xbox Series S"]) ||
    rowHasTruthy(row, ["Xbox_One", "Xbox One"]) ||
    rowHasTruthy(row, ["Xbox"])
  ) buckets.add("xbox");

  if (
    rowHasTruthy(row, ["Switch_2", "Switch 2", "Nintendo Switch 2"]) ||
    rowHasTruthy(row, ["Switch", "Nintendo Switch"]) ||
    rowHasTruthy(row, ["Nintendo"])
  ) buckets.add("nintendo");

  if (
    rowHasTruthy(row, ["Mobile", "iOS", "Android", "iPhone", "iPad"])
  ) buckets.add("mobile");

  // Use only explicit platform fields from mindGAME metadata. Do NOT use
  // Platform_Group_Filter for actual filtering: values like "Console / PC" are
  // category labels and can incorrectly mark Switch-only games as PC.
  [
    "Platforms",
    "platforms",
    "Platform Availability (PC, PlayStation, Xbox, Switch, Mobile)",
    "Platform Availability",
    "Platform_Availability",
  ].forEach((key) => addPlatformFromText(buckets, getRowValue(row, [key])));

  return buckets;
}

function exclusiveBucketsFromMindgameRow(row) {
  const exclusive = new Set();
  if (!row) return exclusive;

  if (rowHasTruthy(row, ["PC_Exclusive", "PC Exclusive"])) exclusive.add("pc");
  if (rowHasTruthy(row, ["PlayStation_Exclusive", "PlayStation Exclusive", "PS_Exclusive"])) exclusive.add("playstation");
  if (rowHasTruthy(row, ["Xbox_Exclusive", "Xbox Exclusive"])) exclusive.add("xbox");
  if (
    rowHasTruthy(row, ["Switch_Exclusive", "Switch Exclusive"]) ||
    rowHasTruthy(row, ["Switch_2_Exclusive", "Switch 2 Exclusive", "Nintendo_Exclusive", "Nintendo Exclusive"])
  ) exclusive.add("nintendo");
  if (rowHasTruthy(row, ["Mobile_Exclusive", "Mobile Exclusive"])) exclusive.add("mobile");

  return exclusive;
}

function platformBucketsFromIgdbRow(row) {
  const buckets = new Set();
  if (!row) return buckets;

  // Best source: IGDB's plain platforms column. platform_release_dates can be
  // incomplete because it may only include platforms with confirmed dates.
  // Use every common spelling because exports have varied over time.
  addPlatformFieldsFromRow(buckets, row, [
    "platforms",
    "Platforms",
    "platform",
    "Platform",
    "platform_names",
    "Platform Names",
    "platform_name",
    "Platform Name",
    "igdb_platforms",
    "IGDB Platforms",
  ]);

  if (buckets.size > 0) return buckets;

  // Fallback only if the plain IGDB platforms field is missing/blank.
  addPlatformFieldsFromRow(buckets, row, [
    "platform_release_dates",
    "platform_release_date",
    "Platform Release Dates",
    "platforms_release_dates",
    "release_platforms",
  ]);

  return buckets;
}

function makePlatformInfo({ steamRow = null, mindgameRow = null, igdbRow = null, assumeSteamPc = false } = {}) {
  const buckets = new Set();
  const exclusive = new Set();

  if (assumeSteamPc || steamRow) buckets.add("pc");

  const mgBuckets = platformBucketsFromMindgameRow(mindgameRow);
  const igdbBuckets = platformBucketsFromIgdbRow(igdbRow);

  mgBuckets.forEach((b) => buckets.add(b));
  igdbBuckets.forEach((b) => buckets.add(b));

  exclusiveBucketsFromMindgameRow(mindgameRow).forEach((b) => {
    if (buckets.has(b)) exclusive.add(b);
  });

  // Exclusivity is based on the merged platform picture:
  // - Steam/global metadata means the game is at least PC.
  // - IGDB plain platforms column is the best source for all known platforms.
  // - mindGAME explicit platform columns are a backup/override source.
  // If, after merging those sources, the game only has one major platform bucket,
  // it is exclusive to that bucket. This makes Steam-only games count as PC
  // exclusive, while titles with IGDB/mindGAME console rows are not misclassified.
  const mergedMajorBuckets = Array.from(buckets).filter((b) => b !== "legacy");
  if (mergedMajorBuckets.length === 1) {
    exclusive.add(mergedMajorBuckets[0]);
  }

  if (buckets.size === 0) buckets.add("legacy");

  return {
    platformBuckets: Array.from(buckets),
    platformExclusiveBuckets: Array.from(exclusive),
  };
}

function matchesPlatformFilter(game, platformFilter) {
  if (!platformFilter?.key) return true;
  const buckets = new Set(game.platformBuckets || []);
  if (!buckets.has(platformFilter.key)) return false;
  if (platformFilter.mode !== "exclusive") return true;

  return new Set(game.platformExclusiveBuckets || []).has(platformFilter.key);
}

/* =====================
   Loading skeleton
   ===================== */
function SkeletonCalendar() {
  const skeletonDays = Array.from({ length: 31 }, () => "major");

  const renderSkeletonDay = (type, idx) => {
    const dayNum = idx + 1;

    if (type === "empty") {
      return (
        <div key={`skel-${idx}`} className="calendar-cell skeleton-day empty-day">
          <div className="day-badge skeleton-badge">{dayNum}</div>
          <div className="no-games skeleton-no-games" />
        </div>
      );
    }

    if (type === "major") {
      return (
        <div key={`skel-${idx}`} className="calendar-cell skeleton-day has-games major-day">
          <div className="day-badge skeleton-badge">{dayNum}</div>
          <div className="day-stack major-stack skeleton-stack">
            <div className="release-card solo lead-major skeleton-card skeleton-card-lead" />
            <div className="release-card strip skeleton-card" />
            <div className="release-card strip skeleton-card" />
            <div className="expand-day-btn footer-spacer skeleton-footer" />
          </div>
        </div>
      );
    }

    return (
      <div key={`skel-${idx}`} className="calendar-cell skeleton-day has-games">
        <div className="day-badge skeleton-badge">{dayNum}</div>
        <div className="day-stack compact-stack skeleton-stack">
          <div className="release-card strip skeleton-card" />
          <div className="release-card strip skeleton-card" />
          <div className="release-card strip skeleton-card" />
          <div className="release-card strip skeleton-card" />
          <div className="expand-day-btn footer-spacer skeleton-footer" />
        </div>
      </div>
    );
  };

  return (
    <main className="calendar-main skeleton-calendar-main" aria-label="Loading calendar">
      <div className="calendar-year-loader-row top">
        <div className="calendar-year-loader skeleton-pill" />
      </div>

      <section className="calendar-month-section skeleton-month-section">
        <div className="month-title-row skeleton-month-title-row">
          <div className="month-title-left">
            <div className="month-toggle-btn ps-mark skeleton-toggle" />
            <h1 className="skeleton-title-bar" />
          </div>

          <div className="month-platform-section skeleton-platform-section">
            {PLATFORM_FILTERS.map((p) => (
              <div key={p.key} className="month-platform-btn skeleton-platform-btn">
                <div className="skeleton-platform-logo" />
              </div>
            ))}
          </div>

          <div className="month-release-count skeleton-release-count" />
        </div>

        <div className="calendar-grid skeleton-calendar-grid">
          {skeletonDays.map(renderSkeletonDay)}
        </div>
      </section>
    </main>
  );
}

/* =====================
   Calendar
   ===================== */
export default function Calendar() {
  const currentYear = dayjs().year();

  const [manifest, setManifest] = useState(null);
  const [filterIndex, setFilterIndex] = useState(null);
  const [gameSearchIndex, setGameSearchIndex] = useState([]);
  const [timelineSearchIndexLoaded, setTimelineSearchIndexLoaded] = useState(false);
  const [timelineSearchIndexLoading, setTimelineSearchIndexLoading] = useState(false);
  const [timelineSearchFocused, setTimelineSearchFocused] = useState(false);
  const [timelineSearchTerm, setTimelineSearchTerm] = useState("");
  const [timelineGlobalRows, setTimelineGlobalRows] = useState([]);
  const [timelineSearchTerms, setTimelineSearchTerms] = useState([]);
  const [timelineExcludedKeys, setTimelineExcludedKeys] = useState(new Set());
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [yearData, setYearData] = useState({});
  const [loadedYears, setLoadedYears] = useState([currentYear]);
  const [loading, setLoading] = useState(true);
  const [loadingYear, setLoadingYear] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const [selectedDeveloper, setSelectedDeveloper] = useState(null);
  const [includeAdult, setIncludeAdult] = useState(false);
  const [showNonSteamOnly, setShowNonSteamOnly] = useState(false);
  const [showSecondaryPlatformReleases, setShowSecondaryPlatformReleases] = useState(false);
  const [highlightNonSteam, setHighlightNonSteam] = useState(true);
  const [calendarViewStyle, setCalendarViewStyle] = useState("art"); // "art" | "timeline" | "platform_trends"
  const [platformFilter, setPlatformFilter] = useState(null); // { key, mode: "include" | "exclusive" }
  const [trendPlatformModes, setTrendPlatformModes] = useState(() =>
    Object.fromEntries(PLATFORM_FILTERS.map((p) => [p.key, "include"]))
  );
  const [trendEndMonthIndex, setTrendEndMonthIndex] = useState(10);
  const [trendChartType, setTrendChartType] = useState("line"); // "line" | "bar"
  const [bulkYearStart, setBulkYearStart] = useState(currentYear);
  const [bulkYearEnd, setBulkYearEnd] = useState(currentYear);

  const [allTags, setAllTags] = useState([]);
  const [allContentTypes, setAllContentTypes] = useState([]);
  const [allPublishers, setAllPublishers] = useState([]);
  const [allDevelopers, setAllDevelopers] = useState([]);

  const [activeMonth, setActiveMonth] = useState(dayjs().format("YYYY-MM"));
  const [expandedDay, setExpandedDay] = useState(null);
  const [hoverPanel, setHoverPanel] = useState(null);
  const [collapsedMonths, setCollapsedMonths] = useState({});

  const monthRefs = useRef({});
  const scrolledToDefaultRef = useRef(false);

  async function loadCalendarYear(year) {
    const y = Number(year);
    if (!Number.isFinite(y)) return [];
    if (yearData[y]) return yearData[y];

    setLoadingYear(y);

    try {
      const raw = await fetchGzipJson(CALENDAR_YEAR_URL(y));
      const rows = Array.isArray(raw) ? dedupeCalendarEvents(raw.map(normalizeCalendarEvent).filter((g) => g.name && g.release_date)) : [];

      setYearData((prev) => ({
        ...prev,
        [y]: rows,
      }));

      setLoadedYears((prev) => {
        const next = Array.from(new Set([...prev, y])).sort((a, b) => a - b);
        return next;
      });

      return rows;
    } finally {
      setLoadingYear(null);
    }
  }



  async function loadTimelineSearchIndex() {
    if (timelineSearchIndexLoaded || timelineSearchIndexLoading) return gameSearchIndex;

    setTimelineSearchIndexLoading(true);
    try {
      const searchIndexUrl = getCalendarRelativeUrl(
        CALENDAR_BASE_URL,
        manifest?.game_search_index || manifest?.search_index || manifest?.gameSearchIndex,
        CALENDAR_GAME_SEARCH_INDEX_URL
      );
      const searchJson = await fetchGzipJson(searchIndexUrl);
      const rows = normalizeGameSearchIndex(searchJson);
      setGameSearchIndex(rows);
      setTimelineSearchIndexLoaded(true);
      return rows;
    } catch (e) {
      console.warn("Calendar timeline search index load failed:", e);
      setGameSearchIndex([]);
      setTimelineSearchIndexLoaded(false);
      return [];
    } finally {
      setTimelineSearchIndexLoading(false);
    }
  }
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);

      try {
        const [manifestJson, filterJson] = await Promise.all([
          fetch(CALENDAR_MANIFEST_URL, { mode: "cors", cache: "no-cache" }).then((r) => {
            if (!r.ok) throw new Error(`${CALENDAR_MANIFEST_URL} HTTP ${r.status}`);
            return r.json();
          }),
          fetchGzipJson(CALENDAR_FILTER_INDEX_URL),
        ]);

        if (cancelled) return;

        setManifest(manifestJson);
        setFilterIndex(filterJson);

        setAllTags(optionArrayFromFilterIndex(filterJson?.tags));
        setAllContentTypes(contentTypeOptionsFromFilterJson(filterJson));
        setAllPublishers(optionArrayFromFilterIndex(filterJson?.publishers));
        setAllDevelopers(optionArrayFromFilterIndex(filterJson?.developers));

        await loadCalendarYear(currentYear);
      } catch (e) {
        console.error("Calendar JSON load failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // loadCalendarYear intentionally reads yearData; initial boot should run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (loading) return;
    if (calendarViewStyle !== "timeline") return;
    if (timelineSearchIndexLoaded || timelineSearchIndexLoading) return;
    loadTimelineSearchIndex();
    // Timeline lookup is intentionally lazy so Art view initial load stays fast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarViewStyle, loading, timelineSearchIndexLoaded, timelineSearchIndexLoading]);

  const releases = useMemo(() => {
    return loadedYears.flatMap((year) => yearData[year] || []);
  }, [loadedYears, yearData]);

  const tagOptions = useMemo(() => allTags || [], [allTags]);

  const contentTypeOptions = useMemo(() => {
    const counts = new Map();

    for (const opt of allContentTypes || []) {
      const label = normalizeContentTypeName(opt.value || opt.label);
      if (!label) continue;
      counts.set(label, Math.max(Number(opt.count || 0), counts.get(label) || 0));
    }

    for (const g of releases) {
      const label = normalizeContentTypeName(g.content_type || "Main Game");
      if (!label) continue;
      counts.set(label, (counts.get(label) || 0) + 1);
    }

    const knownOrder = [
      "Main Game",
      "DLC",
      "Demo",
      "Bundle",
      "Expansion",
      "Pack / Addon",
      "Episode",
      "Season",
      "Mod",
      "Music",
      "Video",
      "Tool",
      "Hardware",
    ];

    return Array.from(counts.entries())
      .map(([value, count]) => ({
        value,
        count,
        label: `${value} (${Number(count).toLocaleString()})`,
      }))
      .sort((a, b) => {
        const ai = knownOrder.indexOf(a.value);
        const bi = knownOrder.indexOf(b.value);
        if (ai >= 0 || bi >= 0) return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
        return (b.count || 0) - (a.count || 0) || String(a.value).localeCompare(String(b.value));
      });
  }, [allContentTypes, releases]);

  const publisherOptions = useMemo(() => allPublishers || [], [allPublishers]);

  const developerOptions = useMemo(() => allDevelopers || [], [allDevelopers]);

  const filterValueMatches = (raw, selected) => {
    if (!selected?.value) return true;
    const want = String(selected.value).toLowerCase();
    return splitMultiParty(raw || "").some((p) => p.toLowerCase() === want);
  };

  /* ============================
     Filtering
     ============================ */
  const baseFilteredReleases = useMemo(() => {
    let rows = releases;

    if (showNonSteamOnly) rows = rows.filter((g) => g.isNonSteam);

    if (platformFilter?.key) {
      rows = rows.filter((g) => matchesPlatformFilter(g, platformFilter));
    }

    if (!showSecondaryPlatformReleases) {
      rows = rows.filter((g) => {
        if (!g.isSecondaryPlatformRelease) return true;
        const phase = normalizeReleasePhase(g.release_phase || g.releasePhase);
        return phase === "early_access" || phase === "full_release";
      });
    }

    if (selectedContentType?.value) {
      const want = normalizeSelectedContentTypeValue(selectedContentType).toLowerCase();

      rows = rows.filter((g) => {
        const got = normalizeContentTypeName(g.content_type || "Main Game").toLowerCase();
        return got === want;
      });
    } else {
      // Default calendar view: hide extra content like demos/DLC unless a content
      // type is explicitly selected from the dropdown.
      rows = rows.filter((g) => g.show_by_default !== false);
    }

    if (!includeAdult) {
      rows = rows.filter((g) => {
        if (g.adult) return false;
        const t = Array.isArray(g.tags) ? g.tags : [];
        const hasSexual = t.includes("Sexual Content");
        const hasNudity = t.includes("Nudity");
        return !(hasSexual && hasNudity);
      });
    }

    if (selectedTag?.value) {
      const tag = selectedTag.value;
      rows = rows.filter((g) => Array.isArray(g.tags) && g.tags.includes(tag));
    }

    if (selectedPublisher?.value) {
      rows = rows.filter((g) => filterValueMatches(g.publishers || "", selectedPublisher));
    }

    if (selectedDeveloper?.value) {
      rows = rows.filter((g) => filterValueMatches(g.developers || "", selectedDeveloper));
    }

    return rows;
  }, [
    releases,
    includeAdult,
    selectedTag,
    selectedContentType,
    selectedPublisher,
    selectedDeveloper,
    showNonSteamOnly,
    showSecondaryPlatformReleases,
    platformFilter,
  ]);

  const filteredReleases = useMemo(() => {
    let rows = baseFilteredReleases;

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter((g) => g.name?.toLowerCase().includes(q));
    }

    return rows;
  }, [baseFilteredReleases, searchTerm]);

  const timelineGlobalSearchMatches = useMemo(() => {
    const q = normName(timelineSearchTerm);
    if (!q || q.length < 2 || !gameSearchIndex.length) return [];

    const compactQ = q.replace(/[^a-z0-9]+/g, "");

    return gameSearchIndex
      .filter((row) => {
        const text = row.searchText || row.normalizedName || normName(row.name);
        const compactText = text.replace(/[^a-z0-9]+/g, "");
        return text.includes(q) || compactText.includes(compactQ);
      })
      .map((row) => {
        const text = row.searchText || row.normalizedName || normName(row.name);
        let rank = 4;
        if (text === q) rank = 0;
        else if (text.startsWith(q)) rank = 1;
        else if (text.split(" ").some((part) => part.startsWith(q))) rank = 2;
        else if (text.includes(q)) rank = 3;
        return { ...row, searchRank: rank };
      })
      .sort((a, b) =>
        a.searchRank - b.searchRank ||
        Number(b.release_count || 0) - Number(a.release_count || 0) ||
        a.name.localeCompare(b.name)
      )
      .slice(0, 18);
  }, [timelineSearchTerm, gameSearchIndex]);

  const timelineSearchYearCount = useMemo(() => {
    if (!timelineGlobalSearchMatches.length) return 0;
    const years = new Set();
    timelineGlobalSearchMatches.forEach((row) => getSearchIndexYears(row).forEach((y) => years.add(y)));
    return years.size;
  }, [timelineGlobalSearchMatches]);

  const releasesByDate = useMemo(() => {
    const map = new Map();

    for (const g of filteredReleases) {
      const key = calendarDateString(g.release_date);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(g);
    }

    for (const [key, arr] of map.entries()) {
      arr.sort((a, b) => (Number(b.followers) || 0) - (Number(a.followers) || 0));
      map.set(key, arr);
    }

    return map;
  }, [filteredReleases]);


  const timelineData = useMemo(() => {
    if (calendarViewStyle !== "timeline") return [];

    const map = new Map();

    baseFilteredReleases
      .filter((g) => releaseMatchesTimelineTerms(g))
      .filter((g) => !timelineExcludedKeys.has(timelineItemKey(g)))
      .forEach((g) => {
        const dateISO = calendarDateString(g.release_date);
        if (!dateISO) return;
        if (!map.has(dateISO)) {
          map.set(dateISO, {
            dateISO,
            dateLabel: dayjs(dateISO).format("MMM D, YYYY"),
            year: dayjs(dateISO).format("YYYY"),
            items: [],
          });
        }
        map.get(dateISO).items.push(g);
      });

    return Array.from(map.values())
      .map((bucket) => ({
        ...bucket,
        items: bucket.items.sort((a, b) => (Number(b.followers) || 0) - (Number(a.followers) || 0)),
      }))
      .sort((a, b) => a.dateISO.localeCompare(b.dateISO));
  }, [calendarViewStyle, baseFilteredReleases, timelineGlobalRows, timelineSearchTerms, timelineExcludedKeys]);

  const hasActiveSpecificFilter =
    Boolean(searchTerm) ||
    Boolean(selectedTag?.value) ||
    Boolean(selectedContentType?.value) ||
    Boolean(selectedPublisher?.value) ||
    Boolean(selectedDeveloper?.value);

  const calendarMonthKeys = useMemo(() => {
    const years = loadedYears.length ? [...loadedYears].sort((a, b) => a - b) : [currentYear];
    const keys = [];

    for (const year of years) {
      for (let month = 1; month <= 12; month += 1) {
        keys.push(`${year}-${String(month).padStart(2, "0")}`);
      }
    }

    return keys;
  }, [loadedYears, currentYear]);

  const monthOptions = useMemo(
    () => calendarMonthKeys.map((m) => ({ label: shortMonthLabel(m), value: m })),
    [calendarMonthKeys]
  );

  const availableCalendarYears = useMemo(() => {
    const manifestYears = Array.isArray(manifest?.years)
      ? manifest.years
      : Array.isArray(manifest?.available_years)
        ? manifest.available_years
        : Array.isArray(manifest?.availableYears)
          ? manifest.availableYears
          : [];

    const years = manifestYears
      .map((y) => Number(y))
      .filter((y) => Number.isFinite(y))
      .sort((a, b) => a - b);

    if (years.length) return Array.from(new Set(years));

    // Fallback if manifest only has current/export metadata: expose a practical window.
    return Array.from({ length: 11 }, (_, idx) => currentYear - 5 + idx);
  }, [manifest, currentYear]);

  const sidebarMonthOptions = useMemo(
    () => Array.from({ length: 12 }, (_, idx) => ({
      value: idx + 1,
      label: dayjs().month(idx).format("MMM"),
    })),
    []
  );

  const activeJumpYear = useMemo(() => {
    const y = Number(String(activeMonth || "").split("-")[0]);
    return Number.isFinite(y) ? y : currentYear;
  }, [activeMonth, currentYear]);

  const activeJumpMonth = useMemo(() => {
    const m = Number(String(activeMonth || "").split("-")[1]);
    return Number.isFinite(m) && m >= 1 && m <= 12 ? m : dayjs().month() + 1;
  }, [activeMonth]);

  const loadedYearSet = useMemo(() => new Set(loadedYears), [loadedYears]);

  const unloadedCalendarYears = useMemo(
    () => availableCalendarYears.filter((year) => !loadedYearSet.has(year)),
    [availableCalendarYears, loadedYearSet]
  );

  const loadedYearRangeLabel = useMemo(() => {
    if (!loadedYears.length) return String(currentYear);
    const sorted = [...loadedYears].sort((a, b) => a - b);
    return sorted.length === 1 ? String(sorted[0]) : `${sorted[0]}–${sorted[sorted.length - 1]}`;
  }, [loadedYears, currentYear]);

  const firstLoadedYearGap = useMemo(() => {
    const years = loadedYears.length ? [...loadedYears].sort((a, b) => a - b) : [currentYear];
    for (let i = 0; i < years.length - 1; i += 1) {
      if (years[i + 1] > years[i] + 1) return years[i] + 1;
    }
    return null;
  }, [loadedYears, currentYear]);

  const hasLoadedYearGap = firstLoadedYearGap !== null;

  const platformTrendData = useMemo(() => {
    const rows = new Map();
    const monthKeys = calendarMonthKeys;

    monthKeys.forEach((month) => {
      const base = { month, monthLabel: shortMonthLabel(month) };
      PLATFORM_FILTERS.forEach((p) => {
        base[p.key] = 0;
        base[`${p.key}Cumulative`] = 0;
      });
      rows.set(month, base);
    });

    filteredReleases.forEach((g) => {
      const month = monthKeyFromDate(g.release_date);
      if (!rows.has(month)) return;

      PLATFORM_FILTERS.forEach((p) => {
        const mode = trendPlatformModes[p.key] || "include";
        if (mode === "off") return;
        const matched = matchesPlatformFilter(g, {
          key: p.key,
          mode: mode === "exclusive" ? "exclusive" : "include",
        });
        if (matched) rows.get(month)[p.key] += 1;
      });
    });

    const totals = Object.fromEntries(PLATFORM_FILTERS.map((p) => [p.key, 0]));
    return monthKeys.map((month) => {
      const row = rows.get(month);
      PLATFORM_FILTERS.forEach((p) => {
        totals[p.key] += row[p.key] || 0;
        row[`${p.key}Cumulative`] = totals[p.key];
      });
      return row;
    });
  }, [filteredReleases, calendarMonthKeys, trendPlatformModes]);

  const platformTrendSummary = useMemo(() => {
    const last = platformTrendData[platformTrendData.length - 1] || {};
    return PLATFORM_FILTERS.map((p) => ({
      ...p,
      mode: trendPlatformModes[p.key] || "include",
      total: last[`${p.key}Cumulative`] || 0,
    }));
  }, [platformTrendData, trendPlatformModes]);

  const visiblePlatformTrendData = useMemo(() => {
    if (!platformTrendData.length) return [];
    const end = trendEndMonthIndex === null
      ? platformTrendData.length - 1
      : Math.max(0, Math.min(platformTrendData.length - 1, Number(trendEndMonthIndex)));
    return platformTrendData.slice(0, end + 1);
  }, [platformTrendData, trendEndMonthIndex]);

  const visibleTrendEndIndex = visiblePlatformTrendData.length
    ? visiblePlatformTrendData.length - 1
    : 0;

  const steamCount = useMemo(
    () => filteredReleases.filter((g) => !g.isNonSteam).length,
    [filteredReleases]
  );

  const nonSteamCount = useMemo(
    () => filteredReleases.filter((g) => g.isNonSteam).length,
    [filteredReleases]
  );

  function sizeClassFor(followers) {
    const f = Number(followers) || 0;
    if (f >= 150000) return "score-xl";
    if (f >= 50000) return "score-lg";
    if (f >= 10000) return "score-md";
    if (f >= 1000) return "score-sm";
    if (f >= 100) return "score-xs";
    return "score-xxs";
  }

  function scrollToMonth(key) {
    setActiveMonth(key);
    requestAnimationFrame(() => {
      monthRefs.current[key]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  async function loadCalendarYearAndScroll(year, targetMonth = 1) {
    const y = Number(year);
    if (!Number.isFinite(y)) return;

    await loadCalendarYear(y);

    const month = Math.max(1, Math.min(12, Number(targetMonth) || 1));
    const key = `${y}-${String(month).padStart(2, "0")}`;

    // Wait for React to render the newly loaded year's month sections before scrolling.
    setTimeout(() => {
      scrollToMonth(key);
    }, 120);
  }

  async function jumpToMonthYear(year, month) {
    const y = Number(year);
    const m = Math.max(1, Math.min(12, Number(month) || 1));
    if (!Number.isFinite(y)) return;

    const key = `${y}-${String(m).padStart(2, "0")}`;
    if (loadedYearSet.has(y)) {
      scrollToMonth(key);
      return;
    }

    await loadCalendarYearAndScroll(y, m);
  }

  async function loadCalendarYears(years = [], scrollTarget = null, options = {}) {
    const cleanYears = Array.from(new Set(
      years.map((y) => Number(y)).filter((y) => Number.isFinite(y))
    )).sort((a, b) => a - b);

    if (!cleanYears.length) return;

    setLoadingYear(cleanYears[0]);
    try {
      await Promise.all(cleanYears.map((year) => loadCalendarYear(year)));
    } finally {
      setLoadingYear(null);
    }

    if (options.scroll === false) return;

    const target = scrollTarget || cleanYears[0];
    const month = target < currentYear ? 12 : 1;
    setTimeout(() => {
      scrollToMonth(`${target}-${String(month).padStart(2, "0")}`);
    }, 140);
  }


  function timelineItemKey(item = {}) {
    return [
      getEventSteamId(item) || item.app_id || item.id || item.game_key || item.igdb_id || normName(item.name),
      calendarDateString(item.release_date || item.date || item.releaseDate),
      normalizeReleasePhase(item.release_phase || item.releasePhase || ""),
      normalizeContentTypeName(item.content_type || "Main Game"),
    ].join("|");
  }

  function releaseMatchesTimelineTerms(game = {}, rows = timelineGlobalRows, terms = timelineSearchTerms) {
    const gameName = normName(game.name || "");
    const wanted = [
      ...(Array.isArray(terms) ? terms : []),
      ...(Array.isArray(rows) ? rows.map((r) => r.name) : []),
    ]
      .map(normName)
      .filter(Boolean);

    if (!wanted.length && timelineSearchTerm) wanted.push(normName(timelineSearchTerm));
    if (!wanted.length) return false;

    return wanted.some((term) => gameName.includes(term) || term.includes(gameName));
  }

  function removeTimelineItem(item) {
    const key = timelineItemKey(item);
    setTimelineExcludedKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }

  function clearTimelineExclusions() {
    setTimelineExcludedKeys(new Set());
  }

  function collapseMonthsToSearchRows(rows = []) {
    const targetMonths = new Set(
      rows
        .flatMap((row) => Array.isArray(row.dates) ? row.dates : [])
        .map(calendarDateString)
        .filter(Boolean)
        .map((d) => d.slice(0, 7))
    );

    const years = Array.from(new Set(
      rows.flatMap((row) => getSearchIndexYears(row))
    )).sort((a, b) => a - b);

    if (!targetMonths.size || !years.length) return;

    const next = {};
    years.forEach((year) => {
      for (let month = 1; month <= 12; month += 1) {
        const key = `${year}-${String(month).padStart(2, "0")}`;
        next[key] = !targetMonths.has(key);
      }
    });

    setCollapsedMonths((prev) => ({ ...prev, ...next }));
  }

  function clearTimelineSelection() {
    setTimelineGlobalRows([]);
    setTimelineSearchTerms([]);
    setTimelineExcludedKeys(new Set());
  }

  async function loadTimelineSearchResult(row, preferLatest = true) {
    if (!row) return;
    const years = getSearchIndexYears(row);
    const targetMonth = getSearchIndexTargetMonth(row, preferLatest);

    setTimelineLoading(true);
    try {
      await loadCalendarYears(years, null, { scroll: false });
      setTimelineSearchTerm(row.name);
      setTimelineGlobalRows([row]);
      setTimelineSearchTerms([row.name]);
      setTimelineExcludedKeys(new Set());
      collapseMonthsToSearchRows([row]);
      setTimelineSearchFocused(false);

      if (targetMonth) {
        setTimeout(() => scrollToMonth(targetMonth), 160);
      } else if (years.length) {
        const targetYear = preferLatest ? years[years.length - 1] : years[0];
        setTimeout(() => scrollToMonth(`${targetYear}-${preferLatest ? "12" : "01"}`), 160);
      }
    } finally {
      setTimelineLoading(false);
    }
  }

  async function loadTimelineGlobalMatches() {
    const matches = timelineGlobalSearchMatches;
    const term = timelineSearchTerm.trim();
    if (!matches.length || !term) return;

    const years = Array.from(new Set(matches.flatMap((row) => getSearchIndexYears(row)))).sort((a, b) => a - b);
    if (!years.length) return;

    setTimelineLoading(true);
    try {
      await loadCalendarYears(years, null, { scroll: false });
      setTimelineGlobalRows(matches);
      setTimelineSearchTerms([term]);
      setTimelineExcludedKeys(new Set());
      collapseMonthsToSearchRows(matches);
      setTimelineSearchFocused(false);

      const latestDate = matches
        .flatMap((row) => Array.isArray(row.dates) ? row.dates : [])
        .map(calendarDateString)
        .filter(Boolean)
        .sort()
        .pop();

      if (latestDate) {
        setTimeout(() => scrollToMonth(latestDate.slice(0, 7)), 160);
      }
    } finally {
      setTimelineLoading(false);
    }
  }

  async function addTimelineGlobalMatches() {
    const matches = timelineGlobalSearchMatches;
    const term = timelineSearchTerm.trim();
    if (!matches.length || !term) return;

    const years = Array.from(new Set(matches.flatMap((row) => getSearchIndexYears(row)))).sort((a, b) => a - b);
    setTimelineLoading(true);
    try {
      await loadCalendarYears(years, null, { scroll: false });
      setTimelineGlobalRows((prev) => {
        const byId = new Map(prev.map((row) => [row.id, row]));
        matches.forEach((row) => byId.set(row.id, row));
        return Array.from(byId.values());
      });
      setTimelineSearchTerms((prev) => Array.from(new Set([...prev, term])));
      setTimelineSearchFocused(false);
    } finally {
      setTimelineLoading(false);
    }
  }

  function loadBulkYearRange() {
    const start = Math.min(Number(bulkYearStart), Number(bulkYearEnd));
    const end = Math.max(Number(bulkYearStart), Number(bulkYearEnd));
    const years = availableCalendarYears.filter((year) => year >= start && year <= end && !loadedYearSet.has(year));
    loadCalendarYears(years, start, { scroll: false });
  }

  useEffect(() => {
    if (loading) return;
    if (calendarViewStyle !== "platform_trends") return;
    if (loadingYear !== null) return;

    const start = Math.min(Number(bulkYearStart), Number(bulkYearEnd));
    const end = Math.max(Number(bulkYearStart), Number(bulkYearEnd));
    if (!Number.isFinite(start) || !Number.isFinite(end)) return;

    const years = availableCalendarYears.filter((year) => year >= start && year <= end && !loadedYearSet.has(year));
    if (!years.length) return;

    loadCalendarYears(years, null, { scroll: false });
    // Auto-load the selected trend range only; do not scroll the calendar cards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkYearStart, bulkYearEnd, calendarViewStyle, loading, availableCalendarYears, loadedYearSet, loadingYear]);

  useEffect(() => {
    if (loading) return;
    if (scrolledToDefaultRef.current) return;

    const nowKey = dayjs().format("YYYY-MM");
    const defaultKey = calendarMonthKeys.includes(nowKey)
      ? nowKey
      : calendarMonthKeys[0];

    if (!defaultKey) return;

    scrolledToDefaultRef.current = true;

    setTimeout(() => {
      scrollToMonth(defaultKey);
    }, 80);
  }, [loading, calendarMonthKeys]);

  useEffect(() => {
    if (loading || !calendarMonthKeys.length) return;

    const nodes = calendarMonthKeys
      .map((key) => monthRefs.current[key])
      .filter(Boolean);

    if (!nodes.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible?.target?.dataset?.monthKey) {
          setActiveMonth(visible.target.dataset.monthKey);
        }
      },
      {
        root: null,
        threshold: [0.2, 0.35, 0.5, 0.65],
        rootMargin: "-90px 0px -55% 0px",
      }
    );

    nodes.forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, [loading, calendarMonthKeys]);

  function openHover(e, item) {
    const r = layoutRect(e.currentTarget);
    const vw = layoutViewportWidth();
    const vh = layoutViewportHeight();
    const gap = 12;
    const panelW = Math.min(620, Math.max(320, vw - 32));
    const estimatedH = item?.video ? 430 : 280;

    const canRight = r.right + gap + panelW <= vw - 12;
    const canLeft = r.left - gap - panelW >= 12;

    let left;
    if (canRight) left = r.right + gap;
    else if (canLeft) left = r.left - gap - panelW;
    else left = Math.max(12, Math.min(vw - panelW - 12, r.left + (r.width / 2) - (panelW / 2)));

    let top = r.top;
    if (top + estimatedH > vh - 12) top = Math.max(12, r.bottom - estimatedH);
    if (top < 12) top = Math.min(12, Math.max(12, vh - estimatedH - 12));

    setHoverPanel({
      item,
      pos: { top, left },
      w: panelW,
    });
  }

  function closeHover() {
    setHoverPanel(null);
  }

  function cyclePlatformFilter(key) {
    setPlatformFilter((prev) => {
      if (!prev || prev.key !== key) return { key, mode: "include" };
      if (prev.mode === "include") return { key, mode: "exclusive" };
      return null;
    });
  }

  function toggleTrendPlatformLine(key) {
    setTrendPlatformModes((prev) => {
      const current = prev[key] || "include";
      return { ...prev, [key]: current === "off" ? "include" : "off" };
    });
  }

  function toggleTrendPlatformExclusive(key) {
    setTrendPlatformModes((prev) => {
      const current = prev[key] || "include";
      return { ...prev, [key]: current === "exclusive" ? "include" : "exclusive" };
    });
  }


  function platformButtonTitle(p) {
    const active = platformFilter?.key === p.key;
    if (!active) return `Filter to ${p.label}`;
    if (platformFilter.mode === "include") return `${p.label}: included. Click again for exclusives only.`;
    return `${p.label}: exclusives only. Click again to clear.`;
  }

  function renderPlatformListMetadata(item) {
    const text = item?.platformText || "";
    const parts = String(text || "")
      .split(/[,;]+/)
      .map((p) => shortPlatformDisplayLabel(p.trim()))
      .filter(Boolean);

    if (!parts.length) return null;

    const itemDateISO = calendarDateString(item?.release_date || item?.date || item?.releaseDate);
    const entries = parsePlatformReleaseDateEntries(item?.platformReleaseDatesText || item?.platform_release_dates_text || "");

    return parts.map((platform, idx) => {
      const driving = platformDrivesDate(platform, itemDateISO, entries);
      return (
        <React.Fragment key={`${platform}-${idx}`}>
          {idx > 0 ? ", " : ""}
          <span className={driving ? "hover-platform-driving" : ""}>
            {platform}
          </span>
        </React.Fragment>
      );
    });
  }

  function renderPlatformDateMetadata(item) {
    const raw = item?.platformReleaseDatesText || item?.platform_release_dates_text || "";
    const entries = parsePlatformReleaseDateEntries(raw);
    if (!entries.length) return raw || null;

    const itemDateISO = calendarDateString(item?.release_date || item?.date || item?.releaseDate);

    return entries.map((entry, idx) => {
      const driving = entry.dateISO === itemDateISO;
      return (
        <React.Fragment key={`${entry.platform}-${entry.dateISO}-${idx}`}>
          {idx > 0 ? " · " : ""}
          <span className={driving ? "hover-platform-driving" : ""}>
            {entry.platform}
          </span>
          {`: ${entry.dateLabel}`}
        </React.Fragment>
      );
    });
  }

  function renderPlatformReleaseSummary(item) {
    const itemDateISO = calendarDateString(item?.release_date || item?.date || item?.releaseDate);
    const entries = parsePlatformReleaseDateEntries(item?.platformReleaseDatesText || item?.platform_release_dates_text || "");
    const platformParts = String(item?.platformText || "")
      .split(/[,;]+/)
      .map((p) => shortPlatformDisplayLabel(p.trim()))
      .filter(Boolean);

    if (!entries.length && !platformParts.length) return null;

    if (!entries.length) {
      return platformParts.map((platform, idx) => (
        <React.Fragment key={`${platform}-${idx}`}>
          {idx > 0 ? " · " : ""}
          <span className="platform-release-chip undated">{platform}</span>
        </React.Fragment>
      ));
    }

    const datedKeys = new Set(entries.map((entry) => entry.platformKey).filter(Boolean));
    const datedKeyList = Array.from(datedKeys);

    const missingPlatforms = platformParts.filter((platform) => {
      const key = platformMatchKey(platform);
      if (!key) return false;
      if (datedKeys.has(key)) return false;
      if (key === "playstation" && datedKeyList.some((k) => k.startsWith("playstation"))) return false;
      if (key === "xbox" && datedKeyList.some((k) => k.startsWith("xbox"))) return false;
      if ((key === "nintendo" || key === "nintendo switch") && datedKeyList.some((k) => k.startsWith("nintendo"))) return false;
      return true;
    });

    const combined = [
      ...entries.map((entry) => ({ type: "dated", ...entry })),
      ...missingPlatforms.map((platform) => ({
        type: "undated",
        platform,
        platformKey: platformMatchKey(platform),
        dateISO: "",
        dateLabel: "date TBA",
      })),
    ];

    return combined.map((entry, idx) => {
      const driving = entry.type === "dated" && entry.dateISO === itemDateISO;

      return (
        <React.Fragment key={`${entry.platform}-${entry.dateISO || "undated"}-${idx}`}>
          {idx > 0 ? " · " : ""}
          <span className={["platform-release-chip", driving ? "hover-platform-driving" : ""].join(" ")}>
            <span className="platform-release-platform">{entry.platform}</span>
            <span className="platform-release-date">{entry.dateLabel}</span>
          </span>
        </React.Fragment>
      );
    });
  }

  function renderPlatformSelector() {
    const hasActivePlatform = Boolean(platformFilter?.key);

    return (
      <div className="month-platform-section" aria-label="Platform filters">
        {PLATFORM_FILTERS.map((p) => {
          const active = platformFilter?.key === p.key;
          const exclusive = active && platformFilter.mode === "exclusive";
          const dimmed = hasActivePlatform && !active;

          return (
            <button
              key={p.key}
              type="button"
              className={[
                "month-platform-btn",
                active ? "active" : "",
                exclusive ? "exclusive" : "",
                dimmed ? "dimmed" : "",
              ].join(" ")}
              onClick={() => cyclePlatformFilter(p.key)}
              title={platformButtonTitle(p)}
              aria-pressed={active}
            >
              {p.iconSrc ? (
                <img
                  className="month-platform-logo"
                  src={p.iconSrc}
                  alt={p.label}
                  draggable="false"
                />
              ) : (
                <span className="month-platform-text-logo">{p.iconFallback}</span>
              )}
              {exclusive && <span className="month-exclusive-pill">EX</span>}
            </button>
          );
        })}
      </div>
    );
  }

  function renderGameCard(g, mode = "strip", extraClass = "") {
    const score = Number(g.followers) || 0;
    const scoreClass = sizeClassFor(score);
    const isSolo = mode === "solo";
    const isHero = mode === "hero";
    const isModal = mode === "modal";
    const showScore = isSolo || isHero || isModal || score >= 50000;
    const hasImage = Boolean(g.box_art);
    const canBulgePreview =
      hasImage &&
      !isModal &&
      (mode === "strip" || mode === "solo" || score >= 50000);
    const releaseLabel = releaseLabelForEvent(g);
    const isEarlyAccess = isEarlyAccessEvent(g);

    const content = (
      <div
        className={[
          "release-card",
          mode,
          scoreClass,
          score >= 50000 ? "gold-release" : "",
          score >= 50000 ? "major-release" : "",
          canBulgePreview ? "hover-expandable-art" : "",
          g.isNonSteam ? "nonsteam" : "steam",
          highlightNonSteam && g.isNonSteam ? "highlight-nonsteam" : "",
          score < 500 ? "low-score-release" : "",
          releaseLabel ? "has-release-label" : "",
          isEarlyAccess ? "early-access-release" : "",
          extraClass,
        ].join(" ")}
        onMouseEnter={(e) => openHover(e, g)}
        onMouseLeave={closeHover}
      >
        <div className="release-image-wrap">
          {hasImage ? (
            <>
              <div
                className="release-bg"
                style={{ backgroundImage: `url(${g.box_art})` }}
              />
              <img className="release-img" src={g.box_art} alt={g.name} />
            </>
          ) : (
            <div className="release-missing">No Art</div>
          )}

          <div className="release-grad" />

          <div className="release-title-row">
            <div className="release-title" title={g.name}>
              {g.name}
            </div>

            {showScore && <div className="release-score">{formatScore(score)}</div>}
          </div>

          {g.isNonSteam && <div className="source-pill">MG</div>}
          {releaseLabel && (
            <div className={["release-phase-badge", isEarlyAccess ? "early" : ""].join(" ")}>
              {releaseLabel}
            </div>
          )}
        </div>

        {canBulgePreview && (
          <div className="release-hover-art" aria-hidden="true">
            <div
              className="release-hover-art-bg"
              style={{ backgroundImage: `url(${g.box_art})` }}
            />
            <img className="release-hover-art-img" src={g.box_art} alt="" />
            <div className="release-hover-art-title">{g.name}</div>
          </div>
        )}

        {!g.isNonSteam && (
          <Link
            to={`/game/${g.app_id}`}
            className="release-link"
            aria-label={`Open ${g.name}`}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
    );

    return content;
  }

  function renderBlockCell(g, idx, dateISO) {
    const score = Number(g.followers) || 0;
    return (
      <div
        key={`${dateISO}-block-${g.app_id}-${idx}`}
        className={[
          "release-block",
          score >= 100000 ? "gold-block" : "",
          score >= 50000 ? "major-block" : "",
          score >= 10000 ? "mid-block" : "",
          g.isNonSteam ? "nonsteam-block" : "",
        ].join(" ")}
        title={`${g.name} — ${formatScore(score)}`}
        onMouseEnter={(e) => openHover(e, g)}
        onMouseLeave={closeHover}
      >
        <span>{score >= 50000 ? formatScore(score) : ""}</span>
        {!g.isNonSteam && (
          <Link
            to={`/game/${g.app_id}`}
            className="release-link"
            aria-label={`Open ${g.name}`}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
    );
  }

  function renderDayCell(monthKey, dayNum) {
    const date = dayjs(`${monthKey}-01`).date(dayNum);
    const dateISO = date.format("YYYY-MM-DD");
    const items = releasesByDate.get(dateISO) || [];
    const hasGames = items.length > 0;

    // Keep low-score days visual instead of turning them into blank "+releases" cells.
    // <500 score cards render in grayscale, while stronger cards stay full-color.
    const topGame = items[0];
    const topScore = Number(topGame?.followers) || 0;
    // Cap 100K+ games at the same visual size as 50K+ lead cards.
    // This keeps huge releases from becoming full-cell hero art, which over-crops the artwork.
    const hasHeroGame = false;
    const hasGoldGame = items.some((g) => (Number(g.followers) || 0) >= 50000);
    const hasMajorGame = topScore >= 50000;

    const artVisibleCount = hasHeroGame ? 1 : hasMajorGame ? 3 : 4;
    const visible = items.slice(0, artVisibleCount);
    const hiddenCount = Math.max(0, items.length - visible.length);

    return (
      <div
        key={dateISO}
        className={[
          "calendar-cell",
          hasGames ? "has-games" : "empty-day",
          hasMajorGame ? "major-day" : "",
          hasGoldGame ? "gold-day" : "",
          hasGames && items.every((g) => (Number(g.followers) || 0) < 500) ? "low-only-day" : "",
          hasGames && items.length === 1 && topScore < 50000 ? "single-small-day" : "",
          calendarViewStyle === "blocks" ? "blocks-view" : "art-view",
        ].join(" ")}
      >
        <div className="day-badge">{dayNum}</div>

        {!hasGames ? (
          <div className="no-games">No games</div>
        ) : calendarViewStyle === "blocks" ? (
          <div className="block-cloud">
            {items.map((g, idx) => renderBlockCell(g, idx, dateISO))}
          </div>
) : hasHeroGame ? (
          <>
            {renderGameCard(topGame, "hero")}
            {hiddenCount > 0 && (
              <button
                className="expand-day-btn hero-more"
                onClick={() =>
                  setExpandedDay({
                    dateISO,
                    label: date.format("MMMM D, YYYY"),
                    items,
                  })
                }
              >
                +{hiddenCount} more
              </button>
            )}
          </>
        ) : hasMajorGame ? (
          <div className="day-stack major-stack">
            {visible.map((g, idx) => (
              <React.Fragment key={g.id || `${dateISO}-${g.app_id}-${idx}`}>
                {renderGameCard(g, idx === 0 ? "solo" : "strip", idx === 0 ? "lead-major" : "")}
              </React.Fragment>
            ))}

            <button
              className={["expand-day-btn", hiddenCount <= 0 ? "footer-spacer" : ""].join(" ")}
              disabled={hiddenCount <= 0}
              aria-hidden={hiddenCount <= 0 ? "true" : undefined}
              onClick={hiddenCount > 0 ? () =>
                setExpandedDay({
                  dateISO,
                  label: date.format("MMMM D, YYYY"),
                  items,
                }) : undefined}
            >
              {hiddenCount > 0 ? `+${hiddenCount} more` : ""}
            </button>
          </div>
        ) : (
          <div className="day-stack compact-stack">
            {visible.map((g, idx) => (
              <React.Fragment key={g.id || `${dateISO}-${g.app_id}-${idx}`}>
                {renderGameCard(g, "strip")}
              </React.Fragment>
            ))}

            <button
              className={["expand-day-btn", hiddenCount <= 0 ? "footer-spacer" : ""].join(" ")}
              disabled={hiddenCount <= 0}
              aria-hidden={hiddenCount <= 0 ? "true" : undefined}
              onClick={hiddenCount > 0 ? () =>
                setExpandedDay({
                  dateISO,
                  label: date.format("MMMM D, YYYY"),
                  items,
                }) : undefined}
            >
              {hiddenCount > 0 ? `+${hiddenCount} more` : ""}
            </button>
          </div>
        )}
      </div>
    );
  }

  function toggleMonth(monthKey) {
    setCollapsedMonths((prev) => ({
      ...prev,
      [monthKey]: !prev[monthKey],
    }));
  }

  function renderPlatformTrendView() {
    const activeLines = platformTrendSummary.filter((p) => p.mode !== "off");
    const chartRows = visiblePlatformTrendData;
    const chartIsBar = trendChartType === "bar";

    const renderTrendSeries = (cumulative = false) => {
      if (chartIsBar) {
        return activeLines.map((p, idx) => (
          <Bar
            key={`${p.key}-${cumulative ? "cum" : "monthly"}`}
            dataKey={cumulative ? `${p.key}Cumulative` : p.key}
            name={`${p.shortLabel}${p.mode === "exclusive" ? " exclusives" : ""}${cumulative ? " cumulative" : ""}`}
            fill={TREND_LINE_COLORS[idx % TREND_LINE_COLORS.length]}
            radius={[4, 4, 0, 0]}
          />
        ));
      }

      return activeLines.map((p, idx) => (
        <Line
          key={`${p.key}-${cumulative ? "cum" : "monthly"}`}
          type="monotone"
          dataKey={cumulative ? `${p.key}Cumulative` : p.key}
          name={`${p.shortLabel}${p.mode === "exclusive" ? " exclusives" : ""}${cumulative ? " cumulative" : ""}`}
          stroke={TREND_LINE_COLORS[idx % TREND_LINE_COLORS.length]}
          strokeWidth={p.mode === "exclusive" ? 3 : 2}
          dot={false}
          activeDot={{ r: 4 }}
        />
      ));
    };

    const TrendChartComponent = chartIsBar ? BarChart : LineChart;

    return (
      <section className="platform-trends-view">
        <div className="platform-trends-header">
          <div>
            <h1>Monthly releases by platform</h1>
            <p>Monthly totals and cumulative release counts across the currently filtered slate.</p>
          </div>
          <div className="platform-trends-total">
            {filteredReleases.length.toLocaleString()} releases · {loadedYearRangeLabel}
          </div>
        </div>

        <div className="trend-year-panel">
          <div className="trend-year-summary">
            <strong>Loaded years: {loadedYearRangeLabel}</strong>
            <span>Pick two years to auto-load the trend range.</span>
          </div>

          <div className="platform-trend-controls" aria-label="Platform trend line controls">
          {platformTrendSummary.map((p) => {
            const visibleLast = chartRows[chartRows.length - 1] || {};
            const visibleTotal = visibleLast[`${p.key}Cumulative`] || 0;
            return (
            <button
              key={p.key}
              type="button"
              className={[
                "platform-trend-toggle",
                p.mode !== "off" ? "active" : "off",
                p.mode === "exclusive" ? "exclusive" : "",
              ].join(" ")}
              onClick={() => toggleTrendPlatformLine(p.key)}
              onDoubleClick={(e) => {
                e.preventDefault();
                toggleTrendPlatformExclusive(p.key);
              }}
              title={`${p.label}: ${p.mode === "exclusive" ? "exclusives only" : p.mode === "off" ? "hidden" : "all releases"}. Click to show/hide; double-click for exclusives.`}
            >
              <span className="platform-trend-dot" />
              <span>{p.shortLabel}</span>
              <strong>{Number(visibleTotal || 0).toLocaleString()}</strong>
              {p.mode === "exclusive" && <em>EX</em>}
            </button>
          );
          })}
          </div>

          <div className="trend-year-actions">
            <button type="button" disabled={loadingYear !== null} onClick={() => loadCalendarYears([Math.min(...loadedYears) - 1], Math.min(...loadedYears) - 1, { scroll: false })}>
              − Year
            </button>
            <button type="button" disabled={loadingYear !== null} onClick={() => loadCalendarYears([Math.max(...loadedYears) + 1], Math.max(...loadedYears) + 1, { scroll: false })}>
              + Year
            </button>
          </div>

          <div className="trend-range-loader">
            <select value={bulkYearStart} onChange={(e) => setBulkYearStart(Number(e.target.value))}>
              {availableCalendarYears.map((year) => <option key={`start-${year}`} value={year}>{year}</option>)}
            </select>
            <span>to</span>
            <select value={bulkYearEnd} onChange={(e) => setBulkYearEnd(Number(e.target.value))}>
              {availableCalendarYears.map((year) => <option key={`end-${year}`} value={year}>{year}</option>)}
            </select>
          </div>
        </div>

        <div className="platform-chart-card">
          <div className="platform-chart-title-row">
            <div className="platform-chart-title-left">
              <div>Monthly releases</div>
              <div className="trend-chart-mode-toggle inline" role="group" aria-label="Trend chart type">
                <button
                  type="button"
                  className={trendChartType === "line" ? "active" : ""}
                  onClick={() => setTrendChartType("line")}
                  title="Line chart"
                >
                  <span className="trend-icon-line" /> Line
                </button>
                <button
                  type="button"
                  className={trendChartType === "bar" ? "active" : ""}
                  onClick={() => setTrendChartType("bar")}
                  title="Bar chart"
                >
                  <span className="trend-icon-bar"><i /><i /><i /></span> Bar
                </button>
              </div>
            </div>
            <span>Click platform chips to toggle all / exclusives / off</span>
          </div>
          <div className="platform-chart-body">
            <ResponsiveContainer width="100%" height="100%">
            <TrendChartComponent data={chartRows} margin={{ top: 8, right: 22, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="monthLabel" stroke="#8fa3b6" tick={{ fontSize: 11 }} />
              <YAxis stroke="#8fa3b6" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#10151d", border: "1px solid rgba(63,169,245,0.25)", borderRadius: 10, color: "#fff" }} />
              {renderTrendSeries(false)}
            </TrendChartComponent>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="platform-chart-card">
          <div className="platform-chart-title-row">
            <div>Cumulative releases</div>
            <span>Same platform toggles, cumulative through loaded months</span>
          </div>
          <div className="platform-chart-body">
            <ResponsiveContainer width="100%" height="100%">
            <TrendChartComponent data={chartRows} margin={{ top: 8, right: 22, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="monthLabel" stroke="#8fa3b6" tick={{ fontSize: 11 }} />
              <YAxis stroke="#8fa3b6" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#10151d", border: "1px solid rgba(63,169,245,0.25)", borderRadius: 10, color: "#fff" }} />
              {renderTrendSeries(true)}
            </TrendChartComponent>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="platform-period-slider-wrap">
          <div className="platform-period-slider-labels">
            <span>{calendarMonthKeys[0] ? shortMonthLabel(calendarMonthKeys[0]) : ""}</span>
            <strong>
              Through {chartRows[chartRows.length - 1]?.monthLabel || "loaded months"}
            </strong>
            <span>{calendarMonthKeys[calendarMonthKeys.length - 1] ? shortMonthLabel(calendarMonthKeys[calendarMonthKeys.length - 1]) : ""}</span>
          </div>
          <input
            className="platform-period-slider"
            type="range"
            min="0"
            max={Math.max(0, calendarMonthKeys.length - 1)}
            value={trendEndMonthIndex === null ? Math.max(0, calendarMonthKeys.length - 1) : visibleTrendEndIndex}
            onChange={(e) => setTrendEndMonthIndex(Number(e.target.value))}
          />
          <div className="platform-period-rail">
            {calendarMonthKeys.map((month, idx) => (
              <button
                key={month}
                type="button"
                className={idx <= visibleTrendEndIndex ? "active" : ""}
                onClick={() => setTrendEndMonthIndex(idx)}
                onDoubleClick={() => scrollToMonth(month)}
              >
                {shortMonthLabel(month)}
              </button>
            ))}
          </div>
        </div>
      </section>
    );
  }

  function renderMonth(monthKey) {
    const daysInMonth = dayjs(`${monthKey}-01`).daysInMonth();
    const isCollapsed = Boolean(collapsedMonths[monthKey]);

    return (
      <section
        key={monthKey}
        ref={(el) => (monthRefs.current[monthKey] = el)}
        data-month-key={monthKey}
        className={["calendar-month-section", isCollapsed ? "month-collapsed" : ""].join(" ")}
      >
        <div className="month-title-row">
          <div className="month-title-left">
            <button
              type="button"
              className="month-toggle-btn ps-mark"
              onClick={() => toggleMonth(monthKey)}
              aria-label={`${isCollapsed ? "Open" : "Close"} ${monthLabel(monthKey)}`}
              aria-expanded={!isCollapsed}
            >
              {isCollapsed ? "+" : "−"}
            </button>
            <h1>{monthLabel(monthKey)}</h1>
          </div>

          {renderPlatformSelector()}

          <div className="month-release-count">
            {(Array.from({ length: daysInMonth }, (_, i) => i + 1).reduce(
              (sum, d) =>
                sum +
                ((releasesByDate.get(
                  dayjs(`${monthKey}-01`).date(d).format("YYYY-MM-DD")
                ) || []).length),
              0
            ) || 0).toLocaleString()}{" "}
            releases
          </div>
        </div>

        {!isCollapsed && (
          <div className="calendar-grid">
            {Array.from({ length: daysInMonth }, (_, i) => renderDayCell(monthKey, i + 1))}
          </div>
        )}
      </section>
    );
  }


  function renderTimelineSearchPanel() {
    return (
      <div className="filter-block global-search-block timeline-search-block">
        <TextInputField
          placeholder="Search timeline games..."
          value={timelineSearchTerm}
          onFocus={() => {
            setTimelineSearchFocused(true);
            loadTimelineSearchIndex();
          }}
          onChange={(e) => {
            setTimelineSearchTerm(e.target.value);
            setTimelineSearchFocused(true);
          }}
        />

        {timelineSearchFocused && timelineSearchTerm.trim().length >= 2 && (
          <div className="global-search-panel">
            <div className="global-search-panel-head">
              <span>
                {timelineSearchIndexLoading
                  ? "Loading timeline lookup..."
                  : gameSearchIndex.length
                    ? `${timelineGlobalSearchMatches.length.toLocaleString()} global matches`
                    : "Timeline lookup unavailable"}
              </span>
              <button type="button" onClick={() => setTimelineSearchFocused(false)}>×</button>
            </div>

            {timelineGlobalSearchMatches.length > 1 && (
              <button
                type="button"
                className="global-search-load-all"
                disabled={timelineLoading || timelineSearchYearCount <= 0}
                onClick={timelineGlobalRows.length ? addTimelineGlobalMatches : loadTimelineGlobalMatches}
              >
                {timelineLoading
                  ? "Loading years..."
                  : timelineGlobalRows.length
                    ? `Add to timeline (${timelineSearchYearCount} years)`
                    : `Load matching years (${timelineSearchYearCount})`}
              </button>
            )}

            <div className="global-search-results">
              {timelineGlobalSearchMatches.map((row) => {
                const years = getSearchIndexYears(row);
                const yearLabel = years.length > 1
                  ? `${years[0]}–${years[years.length - 1]}`
                  : years.length === 1
                    ? String(years[0])
                    : "No year";

                return (
                  <button
                    key={row.id}
                    type="button"
                    className="global-search-result"
                    disabled={timelineLoading}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => loadTimelineSearchResult(row, true)}
                    title="Load all years for this title and jump to latest release"
                  >
                    <span className="global-search-name">{row.name}</span>
                    <span className="global-search-meta">
                      {yearLabel}
                      {row.release_count ? ` · ${Number(row.release_count).toLocaleString()} releases` : ""}
                    </span>
                  </button>
                );
              })}

              {gameSearchIndex.length > 0 && !timelineGlobalSearchMatches.length && !timelineSearchIndexLoading && (
                <div className="global-search-empty">No global matches</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderTimelineView() {
    const rows = timelineData;
    const totalCards = rows.reduce((sum, bucket) => sum + bucket.items.length, 0);

    return (
      <section className="global-search-timeline-view">
        <div className="global-search-timeline-header">
          <div>
            <div className="global-search-kicker">Search timeline</div>
            <h1>{timelineSearchTerms.length ? timelineSearchTerms.join(" + ") : "Timeline"}</h1>
            <p>
              {rows.length.toLocaleString()} dated release point{rows.length === 1 ? "" : "s"} · {totalCards.toLocaleString()} matching card{totalCards === 1 ? "" : "s"}
            </p>
          </div>

          <div className="global-search-view-actions">
            <button type="button" onClick={() => setCalendarViewStyle("art")}>Art View</button>
            <button type="button" onClick={clearTimelineExclusions} disabled={timelineExcludedKeys.size <= 0}>
              Restore Hidden
            </button>
            <button type="button" onClick={() => collapseMonthsToSearchRows(timelineGlobalRows.length ? timelineGlobalRows : timelineGlobalSearchMatches)}>
              Collapse Non-Matches
            </button>
          </div>
        </div>

        {!timelineGlobalRows.length && !timelineSearchTerms.length ? (
          <div className="global-search-timeline-empty">
            Use the Timeline search in the sidebar to load a game, series, or search term. The global lookup and extra years only load in this tab.
          </div>
        ) : !rows.length ? (
          <div className="global-search-timeline-empty">
            No loaded calendar cards match this timeline yet. Search for a title and load its matching years.
          </div>
        ) : (
          <div className="global-search-timeline">
            {rows.map((bucket, idx) => {
              const lead = bucket.items[0];
              const side = idx % 2 === 0 ? "left" : "right";
              const releaseLabel = releaseLabelForEvent(lead);

              return (
                <div key={bucket.dateISO} className={["timeline-node-row", side].join(" ")}>
                  <div className="timeline-date-pill">
                    <span>{dayjs(bucket.dateISO).format("D MMM")}</span>
                    <em>{bucket.year}</em>
                  </div>

                  <button
                    type="button"
                    className="timeline-release-card"
                    onClick={() =>
                      setExpandedDay({
                        dateISO: bucket.dateISO,
                        label: dayjs(bucket.dateISO).format("MMMM D, YYYY"),
                        items: bucket.items,
                      })
                    }
                    onMouseEnter={(e) => lead && openHover(e, lead)}
                    onMouseLeave={closeHover}
                  >
                    <span
                      className="timeline-remove-card"
                      title="Hide this release until the timeline is reloaded"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (lead) removeTimelineItem(lead);
                      }}
                    >
                      ×
                    </span>
                    <div className="timeline-release-count">
                      {bucket.items.length.toLocaleString()} release{bucket.items.length === 1 ? "" : "s"}
                    </div>

                    {lead?.box_art ? (
                      <img src={lead.box_art} alt={lead.name} />
                    ) : (
                      <div className="timeline-missing-art">No Art</div>
                    )}

                    <div className="timeline-card-footer">
                      <strong>{lead?.name || "Release"}</strong>
                      <span>
                        {dayjs(bucket.dateISO).format("MMM D, YYYY")}
                        {releaseLabel ? ` · ${releaseLabel}` : ""}
                      </span>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  }

  function renderCalendarContent() {
    const years = loadedYears.length ? [...loadedYears].sort((a, b) => a - b) : [currentYear];
    const nodes = [];

    for (let yearIdx = 0; yearIdx < years.length; yearIdx += 1) {
      const year = years[yearIdx];

      for (let month = 1; month <= 12; month += 1) {
        nodes.push(renderMonth(`${year}-${String(month).padStart(2, "0")}`));
      }

      const nextLoadedYear = years[yearIdx + 1];
      const nextSequentialYear = year + 1;
      const hasGapAfterYear = nextLoadedYear !== undefined && nextLoadedYear > nextSequentialYear;

      if (hasGapAfterYear) {
        nodes.push(
          <div className="calendar-year-loader-row gap" key={`gap-loader-${nextSequentialYear}`}>
            <button
              type="button"
              className="calendar-year-loader"
              disabled={loadingYear !== null}
              onClick={() => loadCalendarYearAndScroll(nextSequentialYear, 1)}
            >
              {loadingYear === nextSequentialYear
                ? `Loading ${loadingYear}...`
                : `+ Open ${nextSequentialYear}`}
            </button>
          </div>
        );

        // Stop rendering after the first unloaded year gap. This prevents a far
        // jump like Dec 2024 from immediately showing the already-loaded 2026
        // calendar below it. Users can progressively open 2025, then 2026, etc.
        break;
      }
    }

    return nodes;
  }

  return (
    <div className="calendar-page">
      <style>{calendarCss}</style>

      <aside className={`calendar-sidebar ${loading ? "loading-side" : ""}`}>
        <div className="side-inner">
          <div className="side-logo-wrap" aria-label="Calendar">
            <img
              className="side-logo"
              src={`${process.env.PUBLIC_URL}/Calendar Logo.png`}
              alt="Calendar"
              draggable="false"
            />
          </div>

          <div className="sidebar-section-title">
            <span>Jump to Month</span>
          </div>

          <div className="jump-select-row">
            <select
              className="month-select jump-month-select"
              value={activeJumpMonth}
              onChange={(e) => jumpToMonthYear(activeJumpYear, Number(e.target.value))}
            >
              {sidebarMonthOptions.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>

            <select
              className="month-select jump-year-select"
              value={activeJumpYear}
              onChange={(e) => jumpToMonthYear(Number(e.target.value), activeJumpMonth)}
            >
              {availableCalendarYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="calendar-view-toggle" role="group" aria-label="Calendar display style">
            <button
              type="button"
              className={calendarViewStyle === "art" ? "active" : ""}
              onClick={() => setCalendarViewStyle("art")}
              title="Artwork cards"
            >
              🖼️ Art
            </button>
            <button
              type="button"
              className={calendarViewStyle === "timeline" ? "active" : ""}
              onClick={() => setCalendarViewStyle("timeline")}
              title="Timeline lookup"
            >
              ⟡ Timeline
            </button>
            <button
              type="button"
              className={calendarViewStyle === "platform_trends" ? "active" : ""}
              onClick={() => setCalendarViewStyle("platform_trends")}
              title="Platform release trends"
            >
              ⟠ Trends
            </button>
          </div>

          <div className="sidebar-section-title filters-title">
            <span>Filters</span>
          </div>

          {calendarViewStyle === "timeline" ? renderTimelineSearchPanel() : (
            <div className="filter-block">
              <TextInputField
                placeholder="Search games..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}

          <div className="filter-block">
            <VirtualizedDropdown
              placeholder="All Tags"
              value={selectedTag}
              onChange={setSelectedTag}
              options={tagOptions}
            />
          </div>

          <div className="filter-block">
            <VirtualizedDropdown
              placeholder="Content Type: Default View"
              value={selectedContentType}
              onChange={setSelectedContentType}
              options={contentTypeOptions}
            />
          </div>

          <div className="filter-block">
            <VirtualizedDropdown
              placeholder="All Publishers"
              value={selectedPublisher}
              onChange={setSelectedPublisher}
              options={publisherOptions}
            />
          </div>

          <div className="filter-block">
            <VirtualizedDropdown
              placeholder="All Developers"
              value={selectedDeveloper}
              onChange={setSelectedDeveloper}
              options={developerOptions}
            />
          </div>

          <label className="check-row">
            <input
              type="checkbox"
              checked={showNonSteamOnly}
              onChange={(e) => setShowNonSteamOnly(e.target.checked)}
            />
            <span>Show Non-Steam Only</span>
          </label>

          <label className="check-row">
            <input
              type="checkbox"
              checked={showSecondaryPlatformReleases}
              onChange={(e) => setShowSecondaryPlatformReleases(e.target.checked)}
            />
            <span>Show Secondary Platform Releases</span>
          </label>

          <label className="check-row">
            <input
              type="checkbox"
              checked={highlightNonSteam}
              onChange={(e) => setHighlightNonSteam(e.target.checked)}
            />
            <span>Highlight Non-Steam Cards</span>
          </label>

          <label className="check-row">
            <input
              type="checkbox"
              checked={includeAdult}
              onChange={(e) => setIncludeAdult(e.target.checked)}
            />
            <span>Include Adult Content Games</span>
          </label>

          <div className="side-sub side-sub-bottom">
            Release slate view{" "}
            <span>
              ({steamCount.toLocaleString()} Steam ·{" "}
              {nonSteamCount.toLocaleString()} non-Steam)
            </span>
          </div>

          <button
            className="reset-btn"
            onClick={() => {
              setSearchTerm("");
              setSelectedTag(null);
              setSelectedContentType(null);
              setSelectedPublisher(null);
              setSelectedDeveloper(null);
              setPlatformFilter(null);
              clearTimelineSelection();
              setTimelineSearchTerm("");
              setTimelineSearchFocused(false);
              setShowNonSteamOnly(false);
              setShowSecondaryPlatformReleases(false);
              setIncludeAdult(false);
            }}
          >
            Reset Filters
          </button>
        </div>
      </aside>

      {loading ? (
        <SkeletonCalendar />
      ) : (
        <main className="calendar-main">
          {calendarViewStyle !== "platform_trends" && calendarViewStyle !== "timeline" && (
            <div className="calendar-year-loader-row top">
              <button
                type="button"
                className="calendar-year-loader"
                disabled={loadingYear !== null}
                onClick={() => loadCalendarYearAndScroll(Math.min(...loadedYears) - 1, 12)}
              >
                {loadingYear === Math.min(...loadedYears) - 1
                  ? `Loading ${loadingYear}...`
                  : `+ Open ${Math.min(...loadedYears) - 1}`}
              </button>
            </div>
          )}

          {calendarViewStyle === "platform_trends"
            ? renderPlatformTrendView()
            : calendarViewStyle === "timeline"
              ? renderTimelineView()
              : renderCalendarContent()}

          {calendarViewStyle !== "platform_trends" && calendarViewStyle !== "timeline" && !hasLoadedYearGap && (
            <div className="calendar-year-loader-row bottom">
              <button
                type="button"
                className="calendar-year-loader"
                disabled={loadingYear !== null}
                onClick={() => loadCalendarYearAndScroll(Math.max(...loadedYears) + 1, 1)}
              >
                {loadingYear === Math.max(...loadedYears) + 1
                  ? `Loading ${loadingYear}...`
                  : `+ Open ${Math.max(...loadedYears) + 1}`}
              </button>
            </div>
          )}
        </main>
      )}

      {expandedDay && (
        <div className="day-modal-backdrop" onClick={() => setExpandedDay(null)}>
          <div className="day-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-kicker">All releases</div>
                <div className="modal-title">{expandedDay.label}</div>
              </div>

              <button className="modal-close" onClick={() => setExpandedDay(null)}>
                ×
              </button>
            </div>

            <div className="modal-grid">
              {expandedDay.items.map((g, idx) => (
                <React.Fragment key={g.id || `${expandedDay.dateISO}-modal-${g.app_id}-${idx}`}>
                  {renderGameCard(g, "modal")}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {hoverPanel && (
        <div
          className="hover-panel"
          style={{
            top: hoverPanel.pos.top,
            left: hoverPanel.pos.left,
            width: hoverPanel.w,
          }}
          onMouseLeave={closeHover}
        >
          <div className="hover-inner">
            {hoverPanel.item.video && getYouTubeEmbedUrl(hoverPanel.item.video) && (
              <iframe
                className="hover-video"
                src={getYouTubeEmbedUrl(hoverPanel.item.video)}
                title={`${hoverPanel.item.name} trailer`}
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            )}

            {hoverPanel.item.video && !getYouTubeEmbedUrl(hoverPanel.item.video) && isDirectVideoUrl(hoverPanel.item.video) && (
              <video
                className="hover-video"
                src={hoverPanel.item.video}
                autoPlay
                muted
                loop
                playsInline
              />
            )}

            <div className="hover-title">
              {hoverPanel.item.name}{" "}
              {hoverPanel.item.isNonSteam ? (
                <em className="hover-ns">(non-Steam)</em>
              ) : null}
            </div>

            <div className="hover-meta-row">
              <span>{dayjs(hoverPanel.item.release_date).format("MMM D, YYYY")}</span>

              {hoverPanel.item.content_type && (
                <>
                  <i>|</i>
                  <span>{hoverPanel.item.content_type}</span>
                </>
              )}

              {releaseLabelForEvent(hoverPanel.item) && (
                <>
                  <i>|</i>
                  <span className={isEarlyAccessEvent(hoverPanel.item) ? "hover-release-label early" : "hover-release-label"}>
                    {releaseLabelForEvent(hoverPanel.item)}
                  </span>
                </>
              )}
            </div>

            <div className="hover-platform-release-spacer" />

            {(hoverPanel.item.platformText || hoverPanel.item.platformReleaseDatesText) && (
              <div className="hover-platform-release-row">
                {renderPlatformReleaseSummary(hoverPanel.item)}
              </div>
            )}

            <div className="hover-inline-stats">
              <span><strong>Search:</strong> {formatScore(hoverPanel.item.search_volume)}</span>
              <span><strong>Steam Followers:</strong> {formatScore(hoverPanel.item.steam_followers)}</span>
            </div>

            {hoverPanel.item.short_description && (
              <div className="hover-desc">{hoverPanel.item.short_description}</div>
            )}

            {hoverPanel.item.publishers && (
              <div className="hover-kv">
                <strong>Publisher:</strong> {hoverPanel.item.publishers}
              </div>
            )}

            {hoverPanel.item.developers && (
              <div className="hover-kv">
                <strong>Developer:</strong> {hoverPanel.item.developers}
              </div>
            )}

            {Array.isArray(hoverPanel.item.tags) &&
              hoverPanel.item.tags.length > 0 && (
                <div className="hover-kv">
                  <strong>Tags:</strong> {hoverPanel.item.tags.join(", ")}
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================
   CSS
   ========================= */
const calendarCss = `
  .calendar-page {
    display: grid;
    grid-template-columns: 260px 1fr;
    min-height: calc(var(--mg-vh) * 100);
    background:
      radial-gradient(1200px 700px at 50% -10%, rgba(63,169,245,0.17), transparent 55%),
      linear-gradient(180deg, #05070b 0%, #080b10 50%, #050607 100%);
    color: #fff;
    font-family: Segoe UI, system-ui, -apple-system, sans-serif;
  }

  .calendar-sidebar {
    position: sticky;
    top: 0;
    height: calc(var(--mg-vh) * 100);
    z-index: 40;
    background:
      linear-gradient(180deg, rgba(0,0,0,0.98), rgba(2,8,14,0.94) 65%, rgba(0,0,0,0.88)),
      radial-gradient(500px 500px at 20% 0%, rgba(63,169,245,0.16), transparent 65%);
    border-right: 1px solid rgba(63,169,245,0.16);
    box-shadow: 18px 0 50px rgba(0,0,0,0.36);
  }

  .loading-side {
    pointer-events: none;
    opacity: 0.6;
  }

  .side-inner {
    padding: 20px 16px;
  }

  .side-logo-wrap {
    display: flex;
    justify-content: center;
    margin: 2px auto 14px auto;
  }

  .side-logo {
    width: 170px;
    height: auto;
    display: block;
    filter: drop-shadow(0 6px 16px rgba(0,0,0,0.75));
    user-select: none;
  }

  .side-header {
    font-size: 24px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: 0.2px;
    margin-bottom: 6px;
  }

  .side-sub {
    font-size: 12.5px;
    color: #9fb7c9;
    margin-bottom: 16px;
    line-height: 1.35;
  }

  .side-sub span {
    color: #44c2ff;
  }

  .side-sub-bottom {
    margin-top: auto !important;
    margin-bottom: 2px !important;
    padding-top: 12px;
    border-top: 1px solid rgba(255,255,255,0.08);
  }

  .drop-label {
    display: block;
    font-size: 12px;
    color: #9fb7c9;
    margin-bottom: 6px;
    font-weight: 700;
  }

  .month-select {
    width: 100%;
    background:
      linear-gradient(180deg, rgba(17,64,93,0.28), rgba(12,41,59,0.12));
    border: 1px solid #0b3b54;
    color: #44c2ff;
    border-radius: 9px;
    padding: 10px 12px;
    font-size: 15px;
    font-weight: 900;
    outline: none;
    cursor: pointer;
    margin-bottom: 14px;
  }

  .filter-block {
    margin-bottom: 10px;
  }

  .check-row {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 9px;
    color: #cfd8e3;
    font-size: 13.5px;
    cursor: pointer;
  }

  .check-row input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  .reset-btn {
    width: 100%;
    margin-top: 10px;
    border: 1px solid rgba(63,169,245,0.42);
    background: rgba(63,169,245,0.10);
    color: #9fd8ff;
    border-radius: 10px;
    padding: 10px 12px;
    font-weight: 900;
    cursor: pointer;
  }

  .reset-btn:hover {
    background: rgba(63,169,245,0.18);
  }

  .calendar-main {
    padding: 22px 24px 90px 24px;
    min-width: 0;
  }

  .calendar-topbar {
    position: sticky;
    top: 0;
    z-index: 30;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 18px;
    padding: 10px 0 16px 0;
    margin-bottom: 4px;
    background:
      linear-gradient(180deg, rgba(5,7,11,0.98), rgba(5,7,11,0.88) 72%, rgba(5,7,11,0));
    backdrop-filter: blur(12px);
  }

  .eyebrow {
    color: #44c2ff;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 11px;
    font-weight: 900;
    margin-bottom: 3px;
  }

  .active-month-label {
    font-size: 26px;
    font-weight: 950;
    letter-spacing: 0.2px;
  }

  .topbar-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .topbar-actions button {
    border: 1px solid rgba(63,169,245,0.35);
    background: rgba(6,21,33,0.74);
    color: #dff3ff;
    border-radius: 10px;
    padding: 9px 12px;
    font-weight: 900;
    cursor: pointer;
  }

  .topbar-actions button:hover:not(:disabled) {
    background: rgba(63,169,245,0.18);
  }

  .topbar-actions button:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  .calendar-month-section {
    scroll-margin-top: 86px;
    margin-bottom: 44px;
    padding: 14px 0 6px 0;
  }

  .month-title-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 18px;
    margin: 0 0 10px 0;
    padding: 0 2px 10px 2px;
    border-bottom: 2px solid rgba(63,169,245,0.58);
    box-shadow: 0 1px 0 rgba(255,255,255,0.08);
  }

  .month-title-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .ps-mark {
    width: 26px;
    height: 26px;
    border-radius: 7px;
    display: grid;
    place-items: center;
    color: #00111d;
    background: linear-gradient(180deg, #55ccff, #0a8dca);
    box-shadow: 0 0 20px rgba(63,169,245,0.45);
    font-size: 12px;
    transform: skew(-8deg);
  }

  .month-title-row h1 {
    margin: 0;
    font-size: clamp(26px, calc(var(--mg-vw) * 3.1), 44px);
    line-height: 1;
    font-weight: 950;
    letter-spacing: -0.04em;
    text-shadow: 0 10px 30px rgba(0,0,0,0.65);
  }

  .month-release-count {
    color: #9fb7c9;
    font-size: 13px;
    font-weight: 800;
    margin-bottom: 4px;
  }

  .calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(116px, 1fr));
    gap: 8px;
  }

  .calendar-cell {
    position: relative;
    min-height: 154px;
    border-radius: 9px;
    overflow: hidden;
    background:
      linear-gradient(135deg, rgba(255,255,255,0.035) 25%, transparent 25%) 0 0 / 10px 10px,
      linear-gradient(135deg, transparent 75%, rgba(255,255,255,0.025) 75%) 0 0 / 10px 10px,
      rgba(19,24,31,0.92);
    border: 1px solid rgba(255,255,255,0.045);
    box-shadow:
      inset 0 0 0 1px rgba(0,0,0,0.28),
      0 12px 26px rgba(0,0,0,0.16);
  }

  .calendar-cell.has-games {
    background:
      linear-gradient(180deg, rgba(11,19,29,0.98), rgba(7,10,15,0.98));
    border-color: rgba(63,169,245,0.13);
  }

  .calendar-cell.major-day {
    box-shadow:
      inset 0 0 0 1px rgba(63,169,245,0.23),
      0 0 30px rgba(63,169,245,0.08),
      0 14px 30px rgba(0,0,0,0.24);
  }

  .day-badge {
    position: absolute;
    top: 6px;
    left: 6px;
    z-index: 12;
    min-width: 21px;
    height: 20px;
    padding: 0 5px;
    display: grid;
    place-items: center;
    border-radius: 5px;
    background: linear-gradient(180deg, #36c9ff, #0289ca);
    color: #00111d;
    font-size: 12px;
    line-height: 1;
    font-weight: 950;
    box-shadow: 0 5px 14px rgba(0,0,0,0.42);
  }

  .no-games {
    height: 100%;
    min-height: 154px;
    display: grid;
    place-items: center;
    color: rgba(205,218,230,0.58);
    font-size: 11px;
    font-weight: 800;
  }

  .day-stack {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 30px 5px 6px 5px;
    height: 100%;
  }

  .release-card {
    position: relative;
    overflow: hidden;
    border-radius: 7px;
    background: #05070a;
    box-shadow:
      0 6px 14px rgba(0,0,0,0.42),
      inset 0 0 0 1px rgba(255,255,255,0.05);
    transform: translateZ(0);
  }

  .release-card:hover {
    z-index: 20;
    transform: translateY(-1px);
    box-shadow:
      0 14px 30px rgba(0,0,0,0.56),
      0 0 0 1px rgba(63,169,245,0.32),
      0 0 26px rgba(63,169,245,0.16);
  }

  .release-card.strip {
    height: 28px;
    min-height: 28px;
  }

  .release-card.strip.top-strip.score-xl,
  .release-card.strip.top-strip.score-lg {
    height: 44px;
  }

  .release-card.strip.score-xl,
  .release-card.strip.score-lg {
    height: 36px;
  }

  .release-card.strip.score-md {
    height: 32px;
  }

  .release-card.solo {
    height: 100%;
    min-height: 154px;
    border-radius: 9px;
  }

  .release-card.solo.score-xl,
  .release-card.solo.score-lg {
    min-height: 188px;
  }

  .release-card.modal {
    height: 118px;
    border-radius: 10px;
  }

  .release-image-wrap {
    position: absolute;
    inset: 0;
    overflow: hidden;
  }

  .release-bg {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    filter: blur(12px) brightness(0.42) saturate(120%);
    transform: scale(1.14);
  }

  .release-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .release-card.nonsteam .release-img {
    object-fit: cover;
  }

  .release-grad {
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.18) 35%, rgba(0,0,0,0.82) 100%),
      radial-gradient(90% 80% at 50% 0%, rgba(255,255,255,0.10), transparent 60%);
    pointer-events: none;
  }

  .release-card.strip .release-grad {
    background:
      linear-gradient(90deg, rgba(0,0,0,0.10), rgba(0,0,0,0.72) 88%),
      linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.62));
  }

  .release-title-row {
    position: absolute;
    z-index: 5;
    left: 7px;
    right: 6px;
    bottom: 5px;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 7px;
  }

  .release-card.strip .release-title-row {
    left: 6px;
    bottom: 5px;
  }

  .release-title {
    color: #fff;
    font-weight: 900;
    text-shadow: 0 2px 8px rgba(0,0,0,0.95);
    font-size: 10.5px;
    line-height: 1.05;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  .release-card.solo .release-title {
    font-size: 13px;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .release-card.modal .release-title {
    font-size: 13px;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .release-score {
    flex: 0 0 auto;
    font-size: 10px;
    color: #d9eefb;
    font-weight: 900;
    text-shadow: 0 2px 7px rgba(0,0,0,0.85);
    opacity: 0.88;
  }

  .source-pill {
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 8;
    border-radius: 5px;
    background: rgba(68,255,194,0.18);
    border: 1px solid rgba(68,255,194,0.42);
    color: #93ffe0;
    font-size: 9px;
    font-weight: 950;
    padding: 2px 4px;
  }

  .release-card.strip .source-pill {
    display: none;
  }

  .highlight-nonsteam {
    box-shadow:
      0 6px 14px rgba(0,0,0,0.42),
      inset 0 0 0 1px rgba(68,255,194,0.32);
  }

  .release-missing {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, #1d2430, #070a0f);
    color: rgba(255,255,255,0.5);
    font-size: 11px;
    font-weight: 800;
  }

  .release-link {
    position: absolute;
    inset: 0;
    z-index: 10;
  }

  .expand-day-btn {
    margin-top: auto;
    height: 25px;
    width: 100%;
    border: 1px solid rgba(63,169,245,0.42);
    background: rgba(63,169,245,0.12);
    color: #ccefff;
    border-radius: 7px;
    font-size: 11px;
    font-weight: 950;
    cursor: pointer;
  }

  .expand-day-btn:hover {
    background: rgba(63,169,245,0.24);
  }

  .day-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 5000;
    background: rgba(0,0,0,0.72);
    backdrop-filter: blur(8px);
    display: grid;
    place-items: center;
    padding: 28px;
  }

  .day-modal {
    width: min(1180px, calc(var(--mg-vw) * 96));
    max-height: calc(var(--mg-vh) * 88);
    overflow: auto;
    border-radius: 18px;
    background:
      radial-gradient(900px 500px at 50% 0%, rgba(63,169,245,0.18), transparent 60%),
      linear-gradient(180deg, rgba(12,18,26,0.98), rgba(3,5,8,0.98));
    border: 1px solid rgba(63,169,245,0.22);
    box-shadow: 0 40px 100px rgba(0,0,0,0.78);
    padding: 18px;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
    margin-bottom: 16px;
  }

  .modal-kicker {
    color: #44c2ff;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-weight: 950;
    margin-bottom: 4px;
  }

  .modal-title {
    font-size: 28px;
    line-height: 1;
    font-weight: 950;
  }

  .modal-close {
    width: 36px;
    height: 36px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.18);
    background: rgba(255,255,255,0.06);
    color: #fff;
    font-size: 26px;
    line-height: 1;
    cursor: pointer;
  }

  .modal-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
    gap: 12px;
  }

  .hover-panel {
    position: absolute;
    z-index: 6000;
    pointer-events: auto;
  }

  .hover-inner {
    background:
      radial-gradient(500px 260px at 50% 0%, rgba(63,169,245,0.12), transparent 55%),
      rgba(15,18,24,0.98);
    border: 1px solid rgba(63,169,245,0.18);
    border-radius: 14px;
    color: #fff;
    box-shadow: 0 24px 60px rgba(0,0,0,0.65);
    padding: 13px;
    width: 100%;
    max-width: 460px;
  }

  .hover-title {
    font-weight: 950;
    font-size: 16px;
    margin-bottom: 5px;
  }

  .hover-ns {
    color: #44ffc2;
    font-style: normal;
    font-size: 12px;
  }

  .hover-date {
    font-size: 12.5px;
    color: #9fb7c9;
    font-weight: 800;
    margin-bottom: 8px;
  }

  .hover-desc {
    font-size: 13px;
    color: #d7e2ea;
    line-height: 1.35;
    margin-bottom: 10px;
  }

  .hover-kv {
    font-size: 12.5px;
    color: #c6d2dc;
    margin-bottom: 5px;
    line-height: 1.35;
  }

  .hover-platform-driving {
    color: #fff;
    font-weight: 950;
    background: rgba(63,169,245,0.18);
    border: 1px solid rgba(63,169,245,0.42);
    border-radius: 4px;
    padding: 1px 4px;
    box-shadow: 0 0 10px rgba(63,169,245,0.16);
  }

  @container mg-viewport (max-width: 1200px) {
    .calendar-grid {
      grid-template-columns: repeat(4, minmax(128px, 1fr));
    }
  }

  @container mg-viewport (max-width: 860px) {
    .calendar-page {
      grid-template-columns: 1fr;
    }

    .calendar-sidebar {
      position: relative;
      height: auto;
    }

    .calendar-main {
      padding: 16px 12px 80px 12px;
    }

    .calendar-topbar {
      flex-direction: column;
      align-items: flex-start;
    }

    .calendar-grid {
      grid-template-columns: repeat(2, minmax(140px, 1fr));
    }
  }

  /* ===== 2026 release-calendar refinement overrides ===== */
  .calendar-view-toggle {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin: 0 0 14px 0;
  }

  .calendar-view-toggle button {
    border: 1px solid rgba(63,169,245,0.34);
    background: rgba(6,21,33,0.68);
    color: #bfeaff;
    border-radius: 10px;
    padding: 8px 8px;
    font-size: 12px;
    font-weight: 950;
    cursor: pointer;
  }

  .calendar-view-toggle button.active {
    background: linear-gradient(180deg, rgba(63,169,245,0.95), rgba(10,137,202,0.88));
    color: #00111d;
    border-color: rgba(95,220,255,0.95);
    box-shadow: 0 0 18px rgba(63,169,245,0.25);
  }

  .calendar-grid {
    grid-template-columns: repeat(7, minmax(160px, 1fr));
    gap: 10px;
  }

  .calendar-cell {
    min-height: 282px;
    border-radius: 10px;
  }

  .calendar-cell.gold-day {
    border-color: rgba(255,206,79,0.72);
    box-shadow:
      0 0 0 1px rgba(255,219,111,0.42),
      0 0 28px rgba(255,194,57,0.35),
      0 0 70px rgba(255,151,31,0.16),
      inset 0 0 34px rgba(255,194,57,0.10);
  }

  .calendar-cell.major-day:not(.gold-day) {
    min-height: 302px;
  }

  .day-badge {
    top: 7px;
    left: 7px;
    z-index: 22;
  }

  .no-games {
    min-height: 282px;
  }

  .day-stack {
    gap: 6px;
    padding: 31px 0 0 0;
    height: 100%;
  }

  .compact-stack {
    justify-content: flex-start;
  }

  .major-stack {
    gap: 7px;
  }

  .release-card {
    border-radius: 0;
    width: 100%;
  }

  .release-card.strip {
    height: 54px;
    min-height: 54px;
    border-radius: 0;
  }

  .release-card.strip.score-xl,
  .release-card.strip.score-lg,
  .release-card.strip.top-strip.score-xl,
  .release-card.strip.top-strip.score-lg {
    height: 72px;
    min-height: 72px;
  }

  .release-card.strip.score-md {
    height: 62px;
    min-height: 62px;
  }

  .release-card.solo {
    height: 182px;
    min-height: 182px;
    border-radius: 0;
  }

  .release-card.solo.score-xl,
  .release-card.solo.score-lg {
    height: 205px;
    min-height: 205px;
  }

  .release-card.hero {
    position: absolute;
    inset: 0;
    height: 100%;
    min-height: 100%;
    border-radius: 10px;
    z-index: 4;
  }

  .release-card.hero .release-title {
    font-size: 17px;
    line-height: 1.05;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .release-card.hero .release-title-row {
    left: 12px;
    right: 12px;
    bottom: 12px;
  }

  .release-card.hero .release-score {
    font-size: 13px;
    color: #ffe8a3;
  }

  .release-card.gold-release {
    box-shadow:
      0 0 0 1px rgba(255,219,111,0.55),
      0 0 26px rgba(255,194,57,0.42),
      0 0 70px rgba(255,151,31,0.18),
      inset 0 0 32px rgba(255,215,92,0.10);
  }

  .release-card.gold-release::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 6;
    pointer-events: none;
    background:
      radial-gradient(220px 130px at 50% 0%, rgba(255,226,137,0.30), transparent 58%),
      linear-gradient(180deg, rgba(255,214,92,0.10), transparent 35%);
    mix-blend-mode: screen;
  }

  .release-image-wrap,
  .release-bg,
  .release-img,
  .release-grad {
    inset: 0;
  }

  .release-img {
    object-fit: cover;
  }

  .release-title-row {
    left: 8px;
    right: 7px;
    bottom: 7px;
  }

  .release-title {
    font-size: 11.5px;
  }

  .release-card.strip .release-title {
    font-size: 11px;
  }

  .release-card.solo .release-title {
    font-size: 13.5px;
  }

  .expand-day-btn {
    height: 31px;
    min-height: 31px;
    border-radius: 0 0 8px 8px;
    margin: 0;
    background: rgba(8,42,62,0.92);
  }

  .expand-day-btn.hero-more {
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: 8px;
    width: calc(100% - 16px);
    z-index: 24;
    border-radius: 8px;
    background: rgba(0,0,0,0.72);
    border-color: rgba(255,222,119,0.55);
    color: #ffe8a3;
  }

  .calendar-cell.blocks-view {
    min-height: 282px;
  }

  .block-cloud {
    position: absolute;
    inset: 33px 8px 8px 8px;
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: 5px;
    overflow: hidden;
  }

  .release-block {
    position: relative;
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
    border-radius: 5px;
    background: linear-gradient(180deg, rgba(63,169,245,0.75), rgba(5,89,133,0.70));
    border: 1px solid rgba(127,220,255,0.34);
    box-shadow: 0 4px 10px rgba(0,0,0,0.26);
    cursor: pointer;
    overflow: hidden;
  }

  .release-block:hover {
    z-index: 20;
    transform: scale(1.28);
    box-shadow: 0 0 18px rgba(63,169,245,0.45);
  }

  .release-block.mid-block {
    background: linear-gradient(180deg, rgba(122,199,255,0.85), rgba(31,108,171,0.78));
  }

  .release-block.major-block {
    width: 46px;
    height: 46px;
    border-radius: 9px;
    display: grid;
    place-items: center;
    background: linear-gradient(180deg, rgba(63,169,245,0.95), rgba(5,103,161,0.88));
  }

  .release-block.gold-block {
    width: 68px;
    height: 68px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: linear-gradient(180deg, #ffe28a, #d88d1f);
    border-color: rgba(255,235,158,0.95);
    box-shadow:
      0 0 0 1px rgba(255,219,111,0.40),
      0 0 22px rgba(255,194,57,0.48),
      0 8px 20px rgba(0,0,0,0.35);
  }

  .release-block.nonsteam-block {
    border-color: rgba(68,255,194,0.50);
  }

  .release-block span {
    color: #03101a;
    font-size: 9px;
    font-weight: 950;
    line-height: 1;
    text-shadow: none;
    pointer-events: none;
  }

  .release-block.major-block span {
    color: #eaf8ff;
    text-shadow: 0 1px 4px rgba(0,0,0,0.5);
  }

  .release-block.gold-block span {
    color: #2a1600;
  }

  .modal-grid .release-card.modal {
    height: 138px;
  }

  @container mg-viewport (max-width: 1500px) {
    .calendar-cell { min-height: 250px; }
    .calendar-cell.major-day:not(.gold-day) { min-height: 270px; }
    .release-card.strip { height: 48px; min-height: 48px; }
    .release-card.strip.score-xl,
    .release-card.strip.score-lg { height: 62px; min-height: 62px; }
    .release-card.solo { height: 158px; min-height: 158px; }
  }

  @container mg-viewport (max-width: 860px) {
    .calendar-grid {
      grid-template-columns: repeat(2, minmax(150px, 1fr));
    }
  }

  /* ===== Calendar card correction pass ===== */
  .calendar-cell {
    overflow: hidden;
  }

  .day-badge {
    top: 0;
    left: 0;
    border-radius: 0 0 6px 0;
    min-width: 26px;
    height: 22px;
    padding: 0 7px;
    z-index: 32;
  }

  .day-stack {
    padding: 0;
    gap: 6px;
  }

  .release-card {
    overflow: hidden;
  }

  .release-card.strip:first-child,
  .release-card.solo:first-child,
  .release-card.lead-major {
    margin-top: 0;
  }

  .release-image-wrap,
  .release-bg,
  .release-img,
  .release-grad {
    inset: 0;
  }

  .release-bg {
    background-size: cover;
    background-position: center top;
    filter: blur(10px) brightness(0.34) saturate(125%);
    transform: scale(1.08);
  }

  .release-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
  }

  .release-card.hero .release-img,
  .release-card.solo.score-xl .release-img,
  .release-card.solo.score-lg .release-img {
    object-fit: cover;
    object-position: center top;
  }

  .release-card.hero {
    inset: 0;
    border-radius: 10px;
  }

  .release-card.hero .release-image-wrap {
    height: 100%;
  }

  .release-card.gold-release,
  .release-card.major-release.gold-release {
    box-shadow:
      0 0 0 1px rgba(255,219,111,0.62),
      0 0 30px rgba(255,194,57,0.46),
      0 0 80px rgba(255,151,31,0.20),
      inset 0 0 34px rgba(255,215,92,0.11);
  }

  .release-card.gold-release::after {
    z-index: 4;
  }

  .release-title-row {
    z-index: 8;
  }

  .release-score {
    align-self: flex-end;
    font-size: 10px;
    color: #d9eefb;
    background: rgba(0,0,0,0.54);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 5px;
    padding: 2px 4px;
  }

  .release-card.gold-release .release-score {
    color: #ffe8a3;
    border-color: rgba(255,222,119,0.28);
  }

  .only-more {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    border-radius: 0 0 8px 8px;
  }

  .hover-panel {
    transform: translateY(-100%);
  }


  /* ===== Calendar v4 focused corrections: no layout reset, just card behavior ===== */

  /* Date badge flush to top-left, as requested. */
  .day-badge {
    top: 0;
    left: 0;
    border-radius: 0 0 6px 0;
    min-width: 26px;
    height: 22px;
    padding: 0 7px;
    z-index: 34;
  }

  /* Keep the day content using the original wide-card layout, but let artwork rows
     stretch when there would otherwise be dead space above the +more button. */
  .day-stack {
    height: 100%;
    min-height: inherit;
    padding: 28px 0 0 0;
    gap: 5px;
    display: flex;
    flex-direction: column;
  }

  .day-stack .release-card.strip {
    flex: 1 1 0;
    height: auto;
    min-height: 46px;
  }

  .major-stack .release-card.lead-major {
    flex: 2.2 1 0;
    height: auto;
    min-height: 120px;
  }

  .major-stack .release-card.strip {
    flex: 1 1 0;
    height: auto;
    min-height: 50px;
  }

  .expand-day-btn {
    flex: 0 0 31px;
    height: 31px;
    min-height: 31px;
    margin-top: auto;
    border-radius: 0 0 8px 8px;
  }

  .expand-day-btn.hero-more {
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: 8px;
    width: calc(100% - 16px);
    z-index: 30;
    border-radius: 8px;
  }

  /* Full-bleed art, but less zoomed-in than the prior pass. */
  .release-bg {
    background-size: cover;
    background-position: center center;
    filter: blur(10px) brightness(0.34) saturate(120%);
    transform: scale(1.08);
  }

  .release-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
    transform: none;
  }

  /* Large cards use a subtle unzoom with the blurred art still filling the edges.
     This avoids the "tiny box art" issue while reducing harsh cropping. */
  .release-card.hero .release-img,
  .release-card.solo.score-xl .release-img,
  .release-card.solo.score-lg .release-img,
  .release-card.modal.score-xl .release-img,
  .release-card.modal.score-lg .release-img {
    transform: scale(0.94);
  }

  .release-card.hero .release-image-wrap,
  .release-card.solo .release-image-wrap {
    background: #05070a;
  }

  /* <500 score games still display, but are visually de-emphasized. */
  .release-card.low-score-release .release-img,
  .release-card.low-score-release .release-bg {
    filter: grayscale(1) brightness(0.42) contrast(0.95);
  }

  .release-card.low-score-release .release-grad {
    background:
      linear-gradient(180deg, rgba(0,0,0,0.26) 0%, rgba(0,0,0,0.40) 42%, rgba(0,0,0,0.88) 100%),
      linear-gradient(90deg, rgba(0,0,0,0.18), rgba(0,0,0,0.72));
  }

  /* 50K+ gets the gold aura, including medium cards. */
  .release-card.gold-release {
    box-shadow:
      0 0 0 1px rgba(255,219,111,0.58),
      0 0 28px rgba(255,194,57,0.42),
      0 0 78px rgba(255,151,31,0.18),
      inset 0 0 34px rgba(255,215,92,0.10);
  }

  .calendar-cell.gold-day {
    border-color: rgba(255,206,79,0.70);
    box-shadow:
      0 0 0 1px rgba(255,219,111,0.34),
      0 0 26px rgba(255,194,57,0.28),
      0 0 70px rgba(255,151,31,0.12),
      inset 0 0 30px rgba(255,194,57,0.08);
  }

  /* Keep score in the lower title band, not top-left. */
  .release-score {
    align-self: flex-end;
    font-size: 10px;
    color: #d9eefb;
    background: rgba(0,0,0,0.55);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 5px;
    padding: 2px 4px;
  }

  .release-card.gold-release .release-score {
    color: #ffe8a3;
    border-color: rgba(255,222,119,0.30);
  }

  /* Hover popout bottom-aligns to the triggering card. */
  .hover-panel {
    transform: translateY(-100%);
  }


  /* ===== Calendar v8 focused fixes ===== */

  /* Only individual 50K+ game cards get gold. The day/cell itself should not glow. */
  .calendar-cell.gold-day {
    border-color: rgba(63,169,245,0.13) !important;
    box-shadow:
      inset 0 0 0 1px rgba(0,0,0,0.28),
      0 12px 26px rgba(0,0,0,0.16) !important;
  }

  .calendar-cell.major-day {
    box-shadow:
      inset 0 0 0 1px rgba(63,169,245,0.16),
      0 14px 30px rgba(0,0,0,0.24) !important;
  }

  /* Put the artwork rows back flush to the top/left/right of the calendar cell.
     The date badge overlays the art instead of creating a top gutter. */
  .day-stack {
    padding: 0 !important;
    gap: 5px;
  }

  .release-card,
  .release-card.strip,
  .release-card.solo,
  .release-card.lead-major {
    margin-top: 0 !important;
  }

  /* Restore full-bleed artwork for all cards. The earlier scale(0.94) made big
     cards look too small and created visible dead space. */
  .release-img {
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    object-position: center center !important;
    transform: none !important;
  }

  .release-card.hero .release-img,
  .release-card.solo.score-xl .release-img,
  .release-card.solo.score-lg .release-img,
  .release-card.modal.score-xl .release-img,
  .release-card.modal.score-lg .release-img {
    transform: none !important;
    object-fit: cover !important;
    object-position: center center !important;
  }

  .release-card.hero .release-image-wrap,
  .release-card.solo .release-image-wrap {
    background: transparent !important;
  }

  /* If a day only has one non-big game, keep it as a normal smaller rectangle.
     Don't stretch it into a big card just because it's alone. */
  .calendar-cell.single-small-day .compact-stack {
    justify-content: flex-start !important;
  }

  .calendar-cell.single-small-day .compact-stack .release-card.strip {
    flex: 0 0 54px !important;
    height: 54px !important;
    min-height: 54px !important;
  }

  @container mg-viewport (max-width: 1500px) {
    .calendar-cell.single-small-day .compact-stack .release-card.strip {
      flex-basis: 48px !important;
      height: 48px !important;
      min-height: 48px !important;
    }
  }



  /* ===== Calendar v9 gray-only day + full gold-card border ===== */

  /* Days where every visible release is below the popularity threshold should read
     as low-intensity days: gray date badge, gray cell border, gray +more button. */
  .calendar-cell.low-only-day {
    border-color: rgba(160,170,180,0.24) !important;
    box-shadow:
      inset 0 0 0 1px rgba(180,190,200,0.08),
      0 12px 26px rgba(0,0,0,0.16) !important;
  }

  .calendar-cell.low-only-day .day-badge {
    background: linear-gradient(180deg, #9ba6af, #59636b) !important;
    color: #06090d !important;
    box-shadow: 0 5px 14px rgba(0,0,0,0.36) !important;
  }

  .calendar-cell.low-only-day .expand-day-btn {
    border-color: rgba(165,176,186,0.34) !important;
    background: rgba(78,88,98,0.66) !important;
    color: #d6dde3 !important;
  }

  .calendar-cell.low-only-day .expand-day-btn:hover {
    background: rgba(98,110,122,0.78) !important;
  }

  /* Individual 50K+ releases get the full gold treatment. Use outline + inset
     so the border wraps all four sides even when cards are flush against neighbors. */
  .release-card.gold-release {
    outline: 1px solid rgba(255,219,111,0.82) !important;
    outline-offset: -1px !important;
    box-shadow:
      inset 0 0 0 1px rgba(255,236,154,0.50),
      0 0 28px rgba(255,194,57,0.42),
      0 0 78px rgba(255,151,31,0.18) !important;
  }

  .release-card.gold-release::after {
    inset: 0 !important;
    border: 1px solid rgba(255,219,111,0.72);
    box-sizing: border-box;
  }


  /* ===== Calendar v10 refinements ===== */

  /* Hide names for low-score/grayed-out games only in the main day cards.
     Modal cards and hover details still show the title as normal. */
  .calendar-cell .release-card.low-score-release:not(.modal) .release-title {
    display: none !important;
  }

  /* Any day containing a 50K+ gold game gets a gold date badge, but the day cell
     itself still does not receive a full gold aura. */
  .calendar-cell.gold-day .day-badge {
    background: linear-gradient(180deg, #ffe58a, #d99624) !important;
    color: #211200 !important;
    box-shadow:
      0 0 0 1px rgba(255,235,158,0.52),
      0 5px 14px rgba(0,0,0,0.40),
      0 0 18px rgba(255,194,57,0.42) !important;
  }

  /* +more footer is always the neutral/gray treatment for consistency. */
  .expand-day-btn,
  .expand-day-btn.hero-more {
    border-color: rgba(165,176,186,0.34) !important;
    background: rgba(78,88,98,0.66) !important;
    color: #d6dde3 !important;
  }

  .expand-day-btn:hover,
  .expand-day-btn.hero-more:hover {
    background: rgba(98,110,122,0.78) !important;
  }

  /* Put the gold border as a foreground overlay, above the image/gradient/link,
     so the entire card shape is outlined instead of only reading at the bottom. */
  .release-card.gold-release {
    z-index: 6;
    box-shadow:
      0 0 0 1px rgba(255,219,111,0.58),
      0 0 28px rgba(255,194,57,0.42),
      0 0 78px rgba(255,151,31,0.18),
      inset 0 0 34px rgba(255,215,92,0.10) !important;
  }

  .release-card.gold-release::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 16;
    pointer-events: none;
    border: 2px solid rgba(255,219,111,0.92);
    box-sizing: border-box;
    border-radius: inherit;
    box-shadow:
      inset 0 0 0 1px rgba(70,38,0,0.35),
      0 0 16px rgba(255,194,57,0.44);
  }


  /* ===== Calendar v11 refinements ===== */

  /* Low-only days keep the regular blue date badge. Only days with gold games use gold. */
  .calendar-cell.low-only-day .day-badge {
    background: linear-gradient(180deg, #36c9ff, #0289ca) !important;
    color: #00111d !important;
    box-shadow: 0 5px 14px rgba(0,0,0,0.42) !important;
  }

  .calendar-cell.gold-day .day-badge {
    background: linear-gradient(180deg, #ffe58a, #d99624) !important;
    color: #211200 !important;
    box-shadow:
      0 0 0 1px rgba(255,235,158,0.52),
      0 5px 14px rgba(0,0,0,0.40),
      0 0 18px rgba(255,194,57,0.42) !important;
  }

  /* If a day is only low-score/gray releases, keep each visible artwork strip at
     the normal 1-of-4 height instead of stretching 2–3 cards to fill the day. */
  .calendar-cell.low-only-day .compact-stack {
    justify-content: flex-start !important;
  }

  .calendar-cell.low-only-day .compact-stack .release-card.strip {
    flex: 0 0 54px !important;
    height: 54px !important;
    min-height: 54px !important;
  }

  @container mg-viewport (max-width: 1500px) {
    .calendar-cell.low-only-day .compact-stack .release-card.strip {
      flex-basis: 48px !important;
      height: 48px !important;
      min-height: 48px !important;
    }
  }


  /* ===== Calendar v12 fixed quarter strips + permanent footer rail ===== */

  /* Every art day gets the same neutral footer rail. When there are no hidden
     releases it renders as an empty, disabled design element so card geometry
     stays consistent. */
  .expand-day-btn.footer-spacer {
    cursor: default !important;
    pointer-events: none !important;
    color: transparent !important;
  }

  .expand-day-btn.footer-spacer:hover {
    background: rgba(78,88,98,0.66) !important;
  }

  /* Quarter-card strips should always be the same height, whether the day shows
     1, 2, 3, or 4 small releases. Empty space stays above the footer instead of
     stretching the artwork rows. */
  .calendar-cell .compact-stack {
    justify-content: flex-start !important;
  }

  .calendar-cell .compact-stack .release-card.strip {
    flex: 0 0 54px !important;
    height: 54px !important;
    min-height: 54px !important;
  }

  @container mg-viewport (max-width: 1500px) {
    .calendar-cell .compact-stack .release-card.strip {
      flex-basis: 48px !important;
      height: 48px !important;
      min-height: 48px !important;
    }
  }



  /* ===== Calendar v13 exact face alignment ===== */

  /* Treat the gray footer as part of every day card, leaving the artwork face
     above it at a predictable height. */
  .calendar-cell .day-stack {
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 3px !important;
    padding: 0 !important;
  }

  .calendar-cell .expand-day-btn {
    flex: 0 0 31px !important;
    height: 31px !important;
    min-height: 31px !important;
    margin-top: auto !important;
  }

  /* Small/quarter artwork rows: four rows + small gaps + footer exactly fill
     the card. If there are only 2 or 3 rows, they keep the same row height and
     the remaining space stays above the footer. */
  .calendar-cell .compact-stack .release-card.strip {
    flex: 0 0 calc((100% - 31px - 9px) / 4) !important;
    height: calc((100% - 31px - 9px) / 4) !important;
    min-height: 0 !important;
  }

  /* Major layout: one lead card plus two secondary rows fills the same artwork
     face above the footer. */
  .calendar-cell .major-stack .release-card.lead-major {
    flex: 0 0 calc(((100% - 31px - 6px) * 0.56)) !important;
    height: calc(((100% - 31px - 6px) * 0.56)) !important;
    min-height: 0 !important;
  }

  .calendar-cell .major-stack .release-card.strip {
    flex: 0 0 calc(((100% - 31px - 6px) * 0.22)) !important;
    height: calc(((100% - 31px - 6px) * 0.22)) !important;
    min-height: 0 !important;
  }

  /* Remove older fixed-height overrides so the proportional face sizing wins. */
  .calendar-cell.single-small-day .compact-stack .release-card.strip,
  .calendar-cell.low-only-day .compact-stack .release-card.strip {
    flex-basis: calc((100% - 31px - 9px) / 4) !important;
    height: calc((100% - 31px - 9px) / 4) !important;
    min-height: 0 !important;
  }

  @container mg-viewport (max-width: 1500px) {
    .calendar-cell .compact-stack .release-card.strip,
    .calendar-cell.single-small-day .compact-stack .release-card.strip,
    .calendar-cell.low-only-day .compact-stack .release-card.strip {
      flex-basis: calc((100% - 31px - 9px) / 4) !important;
      height: calc((100% - 31px - 9px) / 4) !important;
      min-height: 0 !important;
    }

    .calendar-cell .major-stack .release-card.lead-major {
      flex-basis: calc(((100% - 31px - 6px) * 0.56)) !important;
      height: calc(((100% - 31px - 6px) * 0.56)) !important;
      min-height: 0 !important;
    }

    .calendar-cell .major-stack .release-card.strip {
      flex-basis: calc(((100% - 31px - 6px) * 0.22)) !important;
      height: calc(((100% - 31px - 6px) * 0.22)) !important;
      min-height: 0 !important;
    }
  }


  /* ===== Calendar v14 fixed slot sizing + collapsible months ===== */

  .month-toggle-btn.ps-mark {
    border: 0;
    padding: 0;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
    font-weight: 950;
    color: #00111d;
  }

  .month-toggle-btn.ps-mark:hover {
    filter: brightness(1.14);
    transform: skew(-8deg) translateY(-1px);
    box-shadow:
      0 0 0 1px rgba(126,225,255,0.32),
      0 0 26px rgba(63,169,245,0.62);
  }

  .month-collapsed {
    margin-bottom: 18px !important;
  }

  .month-collapsed .month-title-row {
    margin-bottom: 0;
  }

  /* Use fixed artwork slots instead of dynamic percentage stretching.
     4 small cards fill the face; 1/2/3 small-card days leave empty slots.
     Major days use 2 slots for lead + 1 + 1 slots for secondary cards. */
  .calendar-cell {
    --footer-h: 31px;
    --slot-gap: 3px;
    --art-face-h: calc(100% - var(--footer-h));
    --quarter-slot-h: calc((var(--art-face-h) - (var(--slot-gap) * 3)) / 4);
  }

  .calendar-cell .day-stack {
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    gap: var(--slot-gap) !important;
    padding: 0 !important;
  }

  .calendar-cell .expand-day-btn {
    flex: 0 0 var(--footer-h) !important;
    height: var(--footer-h) !important;
    min-height: var(--footer-h) !important;
    margin-top: auto !important;
  }

  .calendar-cell .compact-stack .release-card.strip,
  .calendar-cell.single-small-day .compact-stack .release-card.strip,
  .calendar-cell.low-only-day .compact-stack .release-card.strip {
    flex: 0 0 var(--quarter-slot-h) !important;
    height: var(--quarter-slot-h) !important;
    min-height: 0 !important;
  }

  .calendar-cell .major-stack .release-card.lead-major {
    flex: 0 0 calc((var(--quarter-slot-h) * 2) + var(--slot-gap)) !important;
    height: calc((var(--quarter-slot-h) * 2) + var(--slot-gap)) !important;
    min-height: 0 !important;
  }

  .calendar-cell .major-stack .release-card.strip {
    flex: 0 0 var(--quarter-slot-h) !important;
    height: var(--quarter-slot-h) !important;
    min-height: 0 !important;
  }

  @container mg-viewport (max-width: 1500px) {
    .calendar-cell .compact-stack .release-card.strip,
    .calendar-cell.single-small-day .compact-stack .release-card.strip,
    .calendar-cell.low-only-day .compact-stack .release-card.strip,
    .calendar-cell .major-stack .release-card.strip {
      flex-basis: var(--quarter-slot-h) !important;
      height: var(--quarter-slot-h) !important;
      min-height: 0 !important;
    }

    .calendar-cell .major-stack .release-card.lead-major {
      flex-basis: calc((var(--quarter-slot-h) * 2) + var(--slot-gap)) !important;
      height: calc((var(--quarter-slot-h) * 2) + var(--slot-gap)) !important;
      min-height: 0 !important;
    }
  }


  /* ===== Calendar v15 topbar + Twitch-style sidebar polish ===== */

  .calendar-page {
    grid-template-columns: 280px 1fr !important;
    background: #000 !important;
  }

  .calendar-sidebar {
    width: 280px !important;
    min-width: 280px !important;
    padding: 0 !important;
    background: #111 !important;
    border-right: 1px solid #333 !important;
    box-shadow: none !important;
  }

  .side-inner {
    padding: 20px !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
    box-sizing: border-box !important;
    height: 100% !important;
  }

  .side-logo-wrap {
    display: flex !important;
    justify-content: center !important;
    margin: 2px auto 14px auto !important;
  }

  .side-logo {
    width: 170px !important;
    height: auto !important;
    display: block !important;
    filter: drop-shadow(0 6px 16px rgba(0,0,0,0.75)) !important;
  }

  .side-header {
    font-size: 24px !important;
    line-height: 1 !important;
    font-weight: 950 !important;
    letter-spacing: 0.2px !important;
    margin: 0 0 2px 0 !important;
  }

  .side-sub {
    font-size: 12.5px !important;
    color: #9fb7c9 !important;
    margin: 0 0 10px 0 !important;
    line-height: 1.35 !important;
  }

  .drop-label {
    color: #777 !important;
    font-size: 11px !important;
    font-weight: 800 !important;
    letter-spacing: 0.12em !important;
    text-transform: uppercase !important;
    margin: 8px 0 0 0 !important;
  }

  .month-select,
  .calendar-sidebar input[type="text"],
  .calendar-sidebar .filter-block input {
    width: 100% !important;
    min-height: 36px !important;
    box-sizing: border-box !important;
    background: #111 !important;
    border: 1px solid #444 !important;
    border-radius: 6px !important;
    color: #bbb !important;
    padding: 8px 10px !important;
    font-size: 16px !important;
    line-height: 1.3 !important;
    font-family: inherit !important;
    font-weight: 400 !important;
    outline: none !important;
    margin: 0 !important;
    transition: border-color 0.15s ease, color 0.15s ease !important;
  }

  .month-select:hover,
  .month-select:focus,
  .calendar-sidebar input[type="text"]:hover,
  .calendar-sidebar input[type="text"]:focus,
  .calendar-sidebar .filter-block:hover * {
    border-color: rgba(255,255,255,0.82) !important;
  }

  .filter-block {
    margin: 0 !important;
  }

  .filter-block,
  .filter-block * {
    cursor: pointer !important;
  }

  .calendar-view-toggle {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 8px !important;
    margin: 2px 0 2px 0 !important;
  }

  .calendar-view-toggle button,
  .reset-btn {
    height: 36px !important;
    min-height: 36px !important;
    background: #111 !important;
    color: rgba(187,187,187,0.75) !important;
    border: 1px solid #444 !important;
    border-radius: 6px !important;
    font-size: 16px !important;
    font-weight: 400 !important;
    font-family: inherit !important;
    line-height: 1.3 !important;
    text-align: center !important;
    cursor: pointer !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    white-space: nowrap !important;
    transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease !important;
    box-shadow: none !important;
    padding: 0 10px !important;
    margin: 0 !important;
  }

  .calendar-view-toggle button.active {
    background: rgba(63,169,245,0.10) !important;
    color: #3fa9f5 !important;
    border-color: #3fa9f5 !important;
    box-shadow: 0 0 0 1px rgba(63,169,245,0.12), 0 0 12px rgba(63,169,245,0.10) !important;
  }

  .calendar-view-toggle button:hover,
  .reset-btn:hover {
    border-color: rgba(255,255,255,0.82) !important;
    color: rgba(187,187,187,0.9) !important;
    background: #111 !important;
  }

  .check-row {
    margin: 0 !important;
    color: #cfd8e3 !important;
    font-size: 13.5px !important;
    min-height: 22px !important;
  }

  .check-row input {
    width: 16px !important;
    height: 16px !important;
    accent-color: #3fa9f5;
  }

  .compact-calendar-topbar {
    min-height: 44px !important;
    padding: 8px 0 8px 0 !important;
    margin-bottom: 6px !important;
    border-bottom: 1px solid rgba(255,255,255,0.10);
  }

  .topbar-title-line {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    white-space: nowrap;
  }

  .inline-eyebrow {
    margin: 0 !important;
    font-size: 11px !important;
    line-height: 1 !important;
  }

  .compact-active-month {
    font-size: 18px !important;
    line-height: 1 !important;
    font-weight: 950 !important;
    color: #fff;
  }

  .topbar-total-count {
    color: #9fb7c9;
    font-size: 13px;
    font-weight: 800;
  }

  .topbar-divider {
    color: #555;
    font-size: 14px;
    line-height: 1;
  }

  .topbar-actions {
    gap: 8px !important;
  }

  .topbar-actions button {
    height: 32px !important;
    border-radius: 7px !important;
    border: 1px solid #444 !important;
    background: #111 !important;
    color: #cfd8e3 !important;
    font-size: 12px !important;
    font-weight: 800 !important;
    padding: 0 12px !important;
    box-shadow: none !important;
  }

  .topbar-actions button:hover:not(:disabled) {
    border-color: rgba(255,255,255,0.82) !important;
    background: #111 !important;
  }

  .month-title-row {
    margin-top: 0 !important;
    padding-bottom: 8px !important;
  }

  .calendar-main {
    padding-top: 14px !important;
  }

  @container mg-viewport (max-width: 860px) {
    .calendar-page {
      grid-template-columns: 1fr !important;
    }

    .calendar-sidebar {
      width: 100% !important;
      min-width: 0 !important;
    }

    .compact-calendar-topbar {
      align-items: flex-start !important;
      gap: 8px !important;
    }

    .topbar-title-line {
      flex-wrap: wrap;
      white-space: normal;
    }
  }

  /* ===== Calendar v16 medium-card hover full-art bulge ===== */

  /* Allow the expanded preview to sit above neighboring day cards instead of
     getting clipped by the card/day/grid containers. */
  .calendar-grid,
  .calendar-cell,
  .calendar-cell .day-stack,
  .release-card.hover-expandable-art {
    overflow: visible !important;
  }

  .calendar-cell:hover {
    z-index: 80;
  }

  .release-card.hover-expandable-art {
    isolation: isolate;
  }

  .release-card.hover-expandable-art:hover {
    z-index: 999 !important;
    transform: translateY(-2px) scale(1.015) !important;
    box-shadow:
      0 18px 38px rgba(0,0,0,0.76),
      0 0 0 1px rgba(63,169,245,0.54),
      0 0 34px rgba(63,169,245,0.28) !important;
  }

  .release-hover-art {
    position: absolute;
    left: 50%;
    top: 50%;
    width: min(360px, 178%);
    aspect-ratio: 16 / 9;
    border-radius: 10px;
    overflow: hidden;
    opacity: 0;
    pointer-events: none;
    transform: translate(-50%, -50%) scale(0.72);
    transform-origin: center center;
    transition:
      opacity 130ms ease,
      transform 170ms cubic-bezier(.2,.8,.2,1),
      box-shadow 170ms ease;
    z-index: 60;
    background: #05070a;
    border: 1px solid rgba(126,225,255,0.72);
    box-shadow:
      0 22px 52px rgba(0,0,0,0.82),
      0 0 0 1px rgba(0,0,0,0.65),
      0 0 34px rgba(63,169,245,0.30);
  }

  .release-card.hover-expandable-art:hover .release-hover-art {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }

  .release-hover-art-bg {
    position: absolute;
    inset: -10px;
    background-size: cover;
    background-position: center center;
    filter: blur(13px) brightness(0.42) saturate(1.2);
    transform: scale(1.06);
  }

  .release-hover-art-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center center;
    display: block;
    filter: saturate(1.08) brightness(1.04);
  }

  .release-hover-art::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(90% 70% at 50% 0%, rgba(255,255,255,0.10), transparent 58%),
      linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.16) 58%, rgba(0,0,0,0.72));
  }

  .release-hover-art-title {
    position: absolute;
    left: 9px;
    right: 9px;
    bottom: 8px;
    z-index: 2;
    color: #fff;
    font-size: 12px;
    line-height: 1.05;
    font-weight: 950;
    text-shadow: 0 2px 9px rgba(0,0,0,0.95);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Keep the actual clickable game link active while the visual preview stays
     pointer-events:none above it. */
  .release-card.hover-expandable-art .release-link {
    z-index: 25;
  }




  /* ===== Calendar v17 platform filter fix ===== */

  .compact-calendar-topbar {
    display: grid !important;
    grid-template-columns: minmax(260px, auto) 1fr auto !important;
    align-items: center !important;
    gap: 14px !important;
  }

  .platform-filter-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-width: 0;
    flex-wrap: wrap;
  }

  .platform-filter-btn {
    height: 24px;
    border-radius: 5px;
    border: 1px solid #444;
    background: #26282d;
    color: #d7dde5;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0 8px;
    font-size: 11px;
    line-height: 1;
    font-weight: 850;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: none;
    transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  }

  .platform-filter-btn:hover {
    border-color: rgba(255,255,255,0.82);
    background: #30333a;
  }

  .platform-filter-btn.active {
    border-color: #3fa9f5;
    color: #3fa9f5;
    background: rgba(63,169,245,0.10);
    box-shadow: 0 0 0 1px rgba(63,169,245,0.12), 0 0 12px rgba(63,169,245,0.10);
  }

  .platform-filter-btn.exclusive {
    border-color: rgba(255,219,111,0.86);
    color: #ffe58a;
    background: rgba(255,194,57,0.12);
    box-shadow: 0 0 0 1px rgba(255,219,111,0.12), 0 0 14px rgba(255,194,57,0.12);
  }

  .platform-icon {
    font-size: 11px;
    line-height: 1;
  }

  .exclusive-pill {
    margin-left: 2px;
    padding: 1px 3px;
    border-radius: 3px;
    background: rgba(255,219,111,0.22);
    color: #ffe58a;
    font-size: 9px;
    font-weight: 950;
  }

  @container mg-viewport (max-width: 1280px) {
    .compact-calendar-topbar {
      grid-template-columns: 1fr auto !important;
    }

    .platform-filter-bar {
      grid-column: 1 / -1;
      justify-content: flex-start;
      order: 3;
    }
  }


  /* ===== Calendar v22 sidebar platform selector with public-folder logos ===== */

  .compact-calendar-topbar {
    grid-template-columns: minmax(260px, auto) auto !important;
  }

  .sidebar-platform-section {
    margin-top: 34px;
    padding: 0 6px 0 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
  }

  .sidebar-platform-btn {
    position: relative;
    width: 165px;
    min-height: 30px;
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0.78;
    filter: grayscale(1) brightness(1.42);
    transition:
      opacity 0.15s ease,
      filter 0.15s ease,
      transform 0.15s ease,
      box-shadow 0.15s ease;
  }

  .sidebar-platform-btn:hover {
    opacity: 1;
    filter: grayscale(0.35) brightness(1.55);
    transform: translateY(-1px);
  }

  .sidebar-platform-btn.dimmed {
    opacity: 0.22;
    filter: grayscale(1) brightness(0.8);
  }

  .sidebar-platform-btn.active {
    opacity: 1;
    filter: grayscale(0) brightness(1.18);
    transform: translateY(-1px);
  }

  .sidebar-platform-btn.exclusive {
    opacity: 1;
    filter: grayscale(0) sepia(0.28) saturate(1.35) brightness(1.18);
  }

  .sidebar-platform-btn.exclusive::before {
    content: "";
    position: absolute;
    inset: -5px -7px;
    border-radius: 8px;
    border: 1px solid rgba(255,219,111,0.72);
    background: rgba(255,194,57,0.08);
    box-shadow:
      0 0 0 1px rgba(255,219,111,0.10),
      0 0 16px rgba(255,194,57,0.22);
    pointer-events: none;
  }

  .sidebar-platform-logo {
    display: block;
    max-width: 158px;
    max-height: 32px;
    width: auto;
    height: auto;
    object-fit: contain;
    user-select: none;
    pointer-events: none;
  }

  .sidebar-platform-text-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 136px;
    height: 30px;
    color: #bfc4ca;
    font-size: 18px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    text-shadow: 0 1px 0 rgba(255,255,255,0.06);
  }

  .sidebar-platform-btn.active .sidebar-platform-text-logo {
    color: #e8f6ff;
  }

  .sidebar-platform-btn.exclusive .sidebar-platform-text-logo {
    color: #ffe58a;
  }

  .sidebar-exclusive-pill {
    position: absolute;
    right: -2px;
    top: -6px;
    z-index: 3;
    height: 16px;
    min-width: 23px;
    padding: 0 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    background: rgba(255,219,111,0.95);
    color: #241200;
    border: 1px solid rgba(255,241,180,0.88);
    box-shadow: 0 0 12px rgba(255,194,57,0.34);
    font-size: 9px;
    line-height: 1;
    font-weight: 950;
  }

  .platform-filter-bar {
    display: none !important;
  }

  @media (max-height: 820px) {
    .sidebar-platform-section {
      margin-top: 18px;
      gap: 12px;
    }

    .sidebar-platform-logo {
      max-height: 27px;
    }
  }


  /* ===== Calendar v24 remove top banner + sticky month platform header ===== */

  .calendar-topbar,
  .compact-calendar-topbar {
    display: none !important;
  }

  .calendar-main {
    padding-top: 0 !important;
  }

  .calendar-month-section {
    scroll-margin-top: 0 !important;
    padding-top: 0 !important;
  }

  .month-title-row {
    position: sticky !important;
    top: 0 !important;
    z-index: 120 !important;
    display: grid !important;
    grid-template-columns: minmax(270px, 1fr) auto minmax(170px, 1fr) !important;
    align-items: center !important;
    gap: 18px !important;
    min-height: 62px !important;
    margin: 0 0 10px 0 !important;
    padding: 8px 2px 8px 2px !important;
    background:
      linear-gradient(180deg, rgba(0,0,0,0.98), rgba(0,0,0,0.93) 78%, rgba(0,0,0,0.78)) !important;
    backdrop-filter: blur(10px) !important;
    border-bottom: 2px solid rgba(63,169,245,0.70) !important;
    box-shadow:
      0 1px 0 rgba(255,255,255,0.08),
      0 10px 22px rgba(0,0,0,0.32) !important;
  }

  .month-title-left {
    min-width: 0 !important;
  }

  .month-title-row h1 {
    white-space: nowrap !important;
  }

  .month-release-count {
    justify-self: end !important;
    margin-bottom: 0 !important;
    white-space: nowrap !important;
  }

  .month-platform-section {
    justify-self: center !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 16px !important;
    min-width: 0 !important;
    max-width: 720px !important;
  }

  .month-platform-btn {
    position: relative !important;
    width: auto !important;
    min-width: 62px !important;
    height: 34px !important;
    border: 0 !important;
    background: transparent !important;
    padding: 0 3px !important;
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    opacity: 0.78 !important;
    filter: grayscale(1) brightness(1.42) !important;
    transition:
      opacity 0.15s ease,
      filter 0.15s ease,
      transform 0.15s ease,
      box-shadow 0.15s ease !important;
  }

  .month-platform-btn:hover {
    opacity: 1 !important;
    filter: grayscale(0.35) brightness(1.55) !important;
    transform: translateY(-1px) !important;
  }

  .month-platform-btn.dimmed {
    opacity: 0.22 !important;
    filter: grayscale(1) brightness(0.8) !important;
  }

  .month-platform-btn.active {
    opacity: 1 !important;
    filter: grayscale(0) brightness(1.18) !important;
    transform: translateY(-1px) !important;
  }

  .month-platform-btn.exclusive {
    opacity: 1 !important;
    filter: grayscale(0) sepia(0.28) saturate(1.35) brightness(1.18) !important;
  }

  .month-platform-btn.exclusive::before {
    content: "";
    position: absolute;
    inset: -4px -7px;
    border-radius: 8px;
    border: 1px solid rgba(255,219,111,0.72);
    background: rgba(255,194,57,0.08);
    box-shadow:
      0 0 0 1px rgba(255,219,111,0.10),
      0 0 16px rgba(255,194,57,0.22);
    pointer-events: none;
  }

  .month-platform-logo {
    display: block !important;
    max-width: 116px !important;
    max-height: 28px !important;
    width: auto !important;
    height: auto !important;
    object-fit: contain !important;
    user-select: none !important;
    pointer-events: none !important;
  }

  .month-platform-text-logo {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    min-width: 84px !important;
    height: 28px !important;
    color: #bfc4ca !important;
    font-size: 14px !important;
    line-height: 1 !important;
    font-weight: 950 !important;
    letter-spacing: 0.02em !important;
    text-transform: uppercase !important;
    text-shadow: 0 1px 0 rgba(255,255,255,0.06) !important;
  }

  .month-platform-btn.active .month-platform-text-logo {
    color: #e8f6ff !important;
  }

  .month-platform-btn.exclusive .month-platform-text-logo {
    color: #ffe58a !important;
  }

  .month-exclusive-pill {
    position: absolute !important;
    right: -8px !important;
    top: -7px !important;
    z-index: 3 !important;
    height: 16px !important;
    min-width: 23px !important;
    padding: 0 4px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 4px !important;
    background: rgba(255,219,111,0.95) !important;
    color: #241200 !important;
    border: 1px solid rgba(255,241,180,0.88) !important;
    box-shadow: 0 0 12px rgba(255,194,57,0.34) !important;
    font-size: 9px !important;
    line-height: 1 !important;
    font-weight: 950 !important;
  }

  /* Sidebar no longer owns platform controls. */
  .sidebar-platform-section {
    display: none !important;
  }

  @container mg-viewport (max-width: 1280px) {
    .month-title-row {
      grid-template-columns: 1fr auto !important;
      row-gap: 8px !important;
    }

    .month-platform-section {
      grid-column: 1 / -1 !important;
      order: 3 !important;
      justify-self: center !important;
      flex-wrap: wrap !important;
      gap: 12px !important;
    }
  }

  @container mg-viewport (max-width: 860px) {
    .month-title-row {
      position: relative !important;
      grid-template-columns: 1fr !important;
      align-items: flex-start !important;
    }

    .month-release-count {
      justify-self: start !important;
    }

    .month-platform-section {
      justify-self: start !important;
    }
  }


  /* ===== Calendar v25 final polish: header expander, gold hover, modal aspect ===== */

  .month-toggle-btn.ps-mark {
    width: 26px !important;
    height: 22px !important;
    min-width: 26px !important;
    border-radius: 0 0 6px 0 !important;
    transform: none !important;
    font-size: 18px !important;
    line-height: 1 !important;
    display: grid !important;
    place-items: center !important;
    align-self: flex-start !important;
    margin-top: 2px !important;
  }

  .month-toggle-btn.ps-mark:hover {
    transform: translateY(-1px) !important;
  }

  .release-card.gold-release.hover-expandable-art:hover {
    z-index: 1000 !important;
    transform: translateY(-2px) scale(1.015) !important;
    box-shadow:
      0 18px 38px rgba(0,0,0,0.76),
      0 0 0 1px rgba(255,219,111,0.86),
      0 0 34px rgba(255,194,57,0.34),
      0 0 78px rgba(255,151,31,0.18) !important;
  }

  .release-card.gold-release.hover-expandable-art .release-hover-art {
    border-color: rgba(255,219,111,0.86) !important;
    box-shadow:
      0 22px 52px rgba(0,0,0,0.82),
      0 0 0 1px rgba(0,0,0,0.65),
      0 0 34px rgba(255,194,57,0.34) !important;
  }

  .modal-grid {
    grid-template-columns: repeat(auto-fill, minmax(270px, 1fr)) !important;
    gap: 12px !important;
  }

  .modal-grid .release-card.modal,
  .release-card.modal {
    height: 132px !important;
    aspect-ratio: 16 / 7.4;
  }

  @container mg-viewport (max-width: 860px) {
    .modal-grid {
      grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)) !important;
    }
  }


  /* ===== Calendar v26 expander alignment + gold full-art hover ===== */

  .month-title-left {
    align-items: center !important;
  }

  .month-toggle-btn.ps-mark {
    width: 26px !important;
    height: 22px !important;
    min-width: 26px !important;
    padding: 0 7px !important;
    box-sizing: border-box !important;
    border-radius: 0 0 6px 0 !important;
    align-self: center !important;
    margin-top: 0 !important;
    font-size: 12px !important;
    line-height: 1 !important;
    font-weight: 950 !important;
    transform: none !important;
  }

  .month-toggle-btn.ps-mark:hover {
    transform: translateY(-1px) !important;
  }

  .release-card.gold-release.hover-expandable-art {
    overflow: visible !important;
    isolation: isolate;
  }

  .release-card.gold-release.hover-expandable-art:hover {
    z-index: 1200 !important;
    transform: translateY(-2px) scale(1.015) !important;
    box-shadow:
      0 18px 38px rgba(0,0,0,0.76),
      0 0 0 1px rgba(255,219,111,0.88),
      0 0 34px rgba(255,194,57,0.38),
      0 0 78px rgba(255,151,31,0.20) !important;
  }

  .release-card.gold-release.hover-expandable-art:hover .release-hover-art {
    opacity: 1 !important;
    transform: translate(-50%, -50%) scale(1) !important;
  }

  .release-card.gold-release.hover-expandable-art .release-hover-art {
    border-color: rgba(255,219,111,0.88) !important;
    box-shadow:
      0 22px 52px rgba(0,0,0,0.82),
      0 0 0 1px rgba(0,0,0,0.65),
      0 0 34px rgba(255,194,57,0.36) !important;
  }

  .release-card.solo.hover-expandable-art .release-hover-art {
    width: min(390px, 160%) !important;
  }


  /* ===== Calendar v27 header nudge + first-row hover above sticky header ===== */

  .month-title-left {
    align-items: center !important;
    gap: 16px !important;
  }

  .month-toggle-btn.ps-mark {
    position: relative !important;
    top: auto !important;
    margin-top: 0 !important;
    transform: translateY(3px) !important;
  }

  .month-toggle-btn.ps-mark:hover {
    transform: translateY(2px) !important;
  }

  .calendar-month-section,
  .calendar-grid,
  .calendar-cell,
  .calendar-cell .day-stack,
  .release-card.hover-expandable-art {
    overflow: visible !important;
  }

  .month-title-row {
    z-index: 90 !important;
  }

  .calendar-grid {
    position: relative !important;
    z-index: 180 !important;
  }

  .calendar-cell:hover {
    z-index: 220 !important;
  }

  .release-card.hover-expandable-art:hover {
    z-index: 5000 !important;
  }

  .release-card.hover-expandable-art .release-hover-art {
    z-index: 7000 !important;
  }


  /* ===== Calendar v28 smart metadata popout + video/platform details ===== */

  .hover-panel {
    position: fixed !important;
    z-index: 9000 !important;
    transform: none !important;
    pointer-events: auto !important;
  }

  .hover-inner {
    max-width: none !important;
    width: 100% !important;
    padding: 14px !important;
    border-radius: 14px !important;
    background:
      radial-gradient(680px 320px at 50% 0%, rgba(63,169,245,0.16), transparent 58%),
      rgba(13,18,25,0.985) !important;
    border: 1px solid rgba(63,169,245,0.24) !important;
    box-shadow: 0 28px 72px rgba(0,0,0,0.74) !important;
  }

  .hover-video {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    border: 0;
    border-radius: 10px;
    overflow: hidden;
    background: #05070a;
    margin-bottom: 12px;
    box-shadow:
      0 0 0 1px rgba(255,255,255,0.08),
      0 12px 28px rgba(0,0,0,0.34);
  }

  .hover-title {
    font-size: 17px !important;
    line-height: 1.1 !important;
  }

  .hover-desc {
    font-size: 13.5px !important;
    line-height: 1.38 !important;
  }

  .hover-kv {
    font-size: 12.8px !important;
    line-height: 1.38 !important;
  }

  .hover-platforms,
  .hover-platform-dates {
    color: #d9e9f5 !important;
  }

  .hover-platform-dates {
    max-height: 54px;
    overflow: hidden;
  }



  /* ===== Calendar v29 IGDB coverage + header stacking correction ===== */

  /* Normal scrolling cards should disappear behind the sticky month/platform header.
     Only active hover art/card previews rise above it. */
  .month-title-row {
    z-index: 700 !important;
  }

  .calendar-grid {
    position: relative !important;
    z-index: 100 !important;
  }

  .calendar-cell {
    z-index: 1;
  }

  .calendar-cell:hover {
    z-index: 850 !important;
  }

  .release-card.hover-expandable-art:hover {
    z-index: 9000 !important;
  }

  .release-card.hover-expandable-art:hover .release-hover-art {
    z-index: 9500 !important;
  }

  .hover-panel {
    z-index: 10000 !important;
  }

  /* ===== Calendar JSON year loader controls ===== */

  .calendar-year-loader-row {
    display: flex;
    justify-content: center;
    padding: 16px 0 20px 0;
  }

  .calendar-year-loader-row.top {
    padding-top: 12px;
  }

  .calendar-year-loader {
    height: 34px;
    min-width: 170px;
    padding: 0 18px;
    border-radius: 9px;
    border: 1px solid rgba(63,169,245,0.38);
    background: rgba(6,21,33,0.86);
    color: #bfeaff;
    font-size: 12px;
    font-weight: 950;
    cursor: pointer;
    box-shadow: 0 0 20px rgba(63,169,245,0.08);
  }

  .calendar-year-loader:hover:not(:disabled) {
    border-color: rgba(126,225,255,0.82);
    background: rgba(63,169,245,0.14);
  }

  .calendar-year-loader:disabled {
    opacity: 0.48;
    cursor: wait;
  }

  /* ===== Calendar v30 requested fixes: year-open scroll + unified platform releases ===== */

  .hover-platform-releases {
    display: block;
    margin-top: 7px !important;
    margin-bottom: 8px !important;
  }

  .hover-platform-releases strong {
    display: block;
    margin-bottom: 6px;
    color: #ffffff;
  }

  .platform-release-chip {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    margin: 0 3px 5px 0;
    padding: 2px 5px;
    border-radius: 5px;
    background: rgba(255,255,255,0.045);
    border: 1px solid rgba(255,255,255,0.08);
    color: #cfd8e3;
    white-space: nowrap;
  }

  .platform-release-chip.undated {
    color: #aeb9c4;
  }

  .platform-release-platform {
    font-weight: 850;
    color: inherit;
  }

  .platform-release-date {
    color: #9fb7c9;
    font-size: 11.5px;
    font-weight: 700;
  }

  .platform-release-chip.hover-platform-driving {
    color: #fff !important;
    background: rgba(63,169,245,0.20) !important;
    border-color: rgba(63,169,245,0.52) !important;
    box-shadow: 0 0 12px rgba(63,169,245,0.18) !important;
  }

  .platform-release-chip.hover-platform-driving .platform-release-date {
    color: #dff3ff;
    font-weight: 900;
  }

  .hover-platforms,
  .hover-platform-dates {
    display: none !important;
  }


  /* ===== Calendar v31 tag rollups + matching card skeleton ===== */

  .skeleton-calendar-main {
    pointer-events: none;
  }

  .skeleton-month-title-row {
    opacity: 1;
  }

  .skeleton-title-bar,
  .skeleton-release-count,
  .skeleton-pill,
  .skeleton-platform-logo,
  .skeleton-card,
  .skeleton-footer,
  .skeleton-no-games {
    position: relative;
    overflow: hidden;
    background:
      linear-gradient(90deg, rgba(255,255,255,0.055), rgba(255,255,255,0.12), rgba(255,255,255,0.055));
    background-size: 220% 100%;
    animation: calendarSkeletonPulse 1.35s ease-in-out infinite;
  }

  .skeleton-title-bar {
    width: 240px;
    height: 42px;
    border-radius: 8px;
    margin: 0;
  }

  .skeleton-release-count {
    justify-self: end;
    width: 96px;
    height: 15px;
    border-radius: 999px;
  }

  .skeleton-pill {
    width: 170px;
    height: 34px;
    border-radius: 9px;
    border: 1px solid rgba(63,169,245,0.18);
  }

  .skeleton-toggle {
    color: transparent !important;
    box-shadow: none !important;
  }

  .skeleton-platform-btn {
    opacity: 0.36 !important;
    filter: none !important;
    cursor: default !important;
  }

  .skeleton-platform-logo {
    width: 78px;
    height: 18px;
    border-radius: 999px;
  }

  .skeleton-day {
    overflow: hidden !important;
    background:
      linear-gradient(180deg, rgba(16,21,28,0.98), rgba(7,10,15,0.98)) !important;
    border-color: rgba(255,255,255,0.07) !important;
    box-shadow:
      inset 0 0 0 1px rgba(0,0,0,0.30),
      0 12px 26px rgba(0,0,0,0.16) !important;
  }

  .skeleton-badge {
    color: rgba(0,17,29,0.24) !important;
    background: linear-gradient(180deg, rgba(54,201,255,0.42), rgba(2,137,202,0.34)) !important;
    box-shadow: none !important;
  }

  .skeleton-stack {
    overflow: hidden !important;
  }

  .skeleton-card {
    border: 0 !important;
    box-shadow: none !important;
    background:
      linear-gradient(90deg, rgba(255,255,255,0.05), rgba(255,255,255,0.12), rgba(255,255,255,0.05)) !important;
    background-size: 220% 100% !important;
  }

  .skeleton-card::before,
  .skeleton-card::after {
    display: none !important;
  }

  .skeleton-card-lead {
    background:
      linear-gradient(90deg, rgba(63,169,245,0.055), rgba(63,169,245,0.14), rgba(63,169,245,0.055)) !important;
    background-size: 220% 100% !important;
  }

  .skeleton-footer {
    border-color: rgba(165,176,186,0.20) !important;
    background:
      linear-gradient(90deg, rgba(78,88,98,0.42), rgba(98,110,122,0.58), rgba(78,88,98,0.42)) !important;
    background-size: 220% 100% !important;
  }

  .skeleton-no-games {
    width: 62px;
    height: 12px;
    border-radius: 999px;
    opacity: 0.42;
  }

  .skeleton-day .release-card,
  .skeleton-day .expand-day-btn,
  .skeleton-no-games,
  .skeleton-title-bar,
  .skeleton-release-count,
  .skeleton-pill,
  .skeleton-platform-logo {
    animation: calendarSkeletonPulse 1.35s ease-in-out infinite;
  }

  @keyframes calendarSkeletonPulse {
    0% { background-position: 120% 0; opacity: 0.58; }
    50% { opacity: 0.92; }
    100% { background-position: -120% 0; opacity: 0.58; }
  }


  /* ===== Calendar platform trend analytics + popout stats ===== */

  .calendar-view-toggle {
    grid-template-columns: 1fr 1fr 1fr !important;
  }

  .calendar-view-toggle button {
    font-size: 13px !important;
    padding: 0 6px !important;
  }

  .platform-trends-view {
    min-height: calc(calc(var(--mg-vh) * 100) - 120px);
    padding: 22px 22px 34px 22px;
    border: 1px solid rgba(63,169,245,0.16);
    border-radius: 16px;
    background:
      radial-gradient(900px 420px at 50% 0%, rgba(63,169,245,0.14), transparent 62%),
      linear-gradient(180deg, rgba(14,18,25,0.98), rgba(5,7,10,0.98));
    box-shadow: 0 24px 70px rgba(0,0,0,0.38);
  }

  .platform-trends-header {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    align-items: flex-start;
    margin-bottom: 18px;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba(255,255,255,0.10);
  }


  .platform-trends-header h1 {
    margin: 0 0 6px 0;
    font-size: clamp(28px, calc(var(--mg-vw) * 3), 44px);
    line-height: 1;
    font-weight: 950;
    letter-spacing: -0.04em;
  }

  .platform-trends-header p {
    margin: 0;
    color: #9fb7c9;
    font-size: 13px;
    font-weight: 700;
  }

  .platform-trends-total {
    flex: 0 0 auto;
    color: #dff3ff;
    border: 1px solid rgba(63,169,245,0.28);
    background: rgba(63,169,245,0.08);
    border-radius: 999px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 950;
  }

  .platform-trend-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 0 16px 0;
  }

  .platform-trend-toggle {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 34px;
    border-radius: 999px;
    border: 1px solid rgba(63,169,245,0.30);
    background: rgba(8,20,31,0.84);
    color: #d7e8f5;
    padding: 0 10px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 850;
  }

  .platform-trend-toggle.off {
    opacity: 0.35;
    border-color: rgba(255,255,255,0.12);
    color: #8c98a3;
  }

  .platform-trend-toggle.exclusive {
    border-color: rgba(255,219,111,0.76);
    background: rgba(255,194,57,0.10);
    color: #ffe58a;
  }

  .platform-trend-toggle strong {
    color: #fff;
    font-weight: 950;
  }

  .platform-trend-toggle em {
    font-style: normal;
    font-size: 9px;
    padding: 2px 4px;
    border-radius: 4px;
    background: rgba(255,219,111,0.22);
    color: #ffe58a;
  }

  .platform-trend-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #3fa9f5;
    box-shadow: 0 0 10px rgba(63,169,245,0.45);
  }

  .platform-chart-card {
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 14px;
    background: rgba(0,0,0,0.32);
    padding: 14px 14px 8px 14px;
    margin-bottom: 14px;
  }

  .platform-chart-title-row {
    display: flex;
    justify-content: space-between;
    gap: 14px;
    align-items: baseline;
    margin-bottom: 8px;
    color: #fff;
    font-size: 14px;
    font-weight: 950;
  }

  .platform-chart-title-row span {
    color: #8fa3b6;
    font-size: 11px;
    font-weight: 800;
  }

  .platform-period-slider-wrap {
    margin-top: 6px;
  }

  .platform-period-slider-labels {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 12px;
    color: #8fa3b6;
    font-size: 11px;
    font-weight: 850;
    margin-bottom: 8px;
  }

  .platform-period-slider-labels strong {
    color: #dff3ff;
    font-weight: 950;
  }

  .platform-period-slider-labels span:last-child {
    text-align: right;
  }

  .platform-period-slider {
    width: 100%;
    accent-color: #3fa9f5;
    margin: 0 0 8px 0;
  }

  .platform-period-rail {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    padding: 8px 2px 2px 2px;
    scrollbar-color: #3fa9f5 #111;
  }

  .platform-period-rail button {
    flex: 0 0 auto;
    height: 28px;
    border: 1px solid rgba(255,255,255,0.12);
    background: rgba(255,255,255,0.045);
    color: #aebdca;
    border-radius: 999px;
    padding: 0 10px;
    font-size: 11px;
    font-weight: 850;
    cursor: pointer;
  }

  .platform-period-rail button:hover,
  .platform-period-rail button.active {
    border-color: rgba(63,169,245,0.52);
    color: #dff3ff;
    background: rgba(63,169,245,0.08);
  }

  .hover-stat-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 7px;
    margin: 9px 0 10px 0;
  }

  .hover-stat-pill {
    border: 1px solid rgba(255,255,255,0.08);
    background: rgba(255,255,255,0.045);
    border-radius: 9px;
    padding: 7px 8px;
    min-width: 0;
  }

  .hover-stat-pill span {
    display: block;
    color: #8fa3b6;
    font-size: 10px;
    line-height: 1;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .hover-stat-pill strong {
    color: #fff;
    font-size: 14px;
    line-height: 1;
    font-weight: 950;
  }

  @container mg-viewport (max-width: 860px) {
    .platform-trends-header {
      flex-direction: column;
    }

    .hover-stat-grid {
      grid-template-columns: 1fr;
    }
  }



  /* ===== Calendar stat popout refinement ===== */

  .hover-stat-grid,
  .hover-stat-pill {
    display: none !important;
  }

  .hover-inline-stats {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin: 4px 0 9px 0;
    color: #9fb7c9;
    font-size: 12px;
    line-height: 1.25;
    font-weight: 800;
  }

  .hover-inline-stats span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    border-radius: 999px;
    background: rgba(255,255,255,0.045);
    border: 1px solid rgba(255,255,255,0.075);
    white-space: nowrap;
  }

  .hover-inline-stats strong {
    color: #dff3ff;
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.035em;
    font-weight: 950;
  }


  /* ===== Calendar v32 trend usability + chart type toggle ===== */

  .trend-year-panel {
    display: grid;
    grid-template-columns: minmax(260px, 1fr) auto auto;
    gap: 12px;
    align-items: center;
    margin: 0 0 14px 0;
    padding: 12px;
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 14px;
    background: rgba(0,0,0,0.28);
  }

  .trend-year-panel strong {
    display: block;
    color: #fff;
    font-size: 13px;
    line-height: 1.2;
    font-weight: 950;
    margin-bottom: 3px;
  }

  .trend-year-panel span {
    color: #8fa3b6;
    font-size: 11.5px;
    line-height: 1.25;
    font-weight: 800;
  }

  .trend-year-actions,
  .trend-range-loader,
  .trend-chart-mode-toggle {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    flex-wrap: wrap;
  }

  .trend-year-actions button,
  .trend-range-loader button,
  .trend-range-loader select,
  .trend-chart-mode-toggle button {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(63,169,245,0.28);
    background: rgba(8,20,31,0.84);
    color: #d7e8f5;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 850;
    cursor: pointer;
  }

  .trend-range-loader select {
    min-width: 76px;
    color: #bfeaff;
  }

  .trend-year-actions button:hover:not(:disabled),
  .trend-range-loader button:hover:not(:disabled),
  .trend-range-loader select:hover,
  .trend-chart-mode-toggle button:hover {
    border-color: rgba(126,225,255,0.72);
    background: rgba(63,169,245,0.12);
  }

  .trend-year-actions button:disabled,
  .trend-range-loader button:disabled {
    opacity: 0.42;
    cursor: wait;
  }

  .trend-chart-mode-toggle {
    justify-content: center;
    margin: 0 0 14px 0;
  }

  .trend-chart-mode-toggle button {
    min-width: 74px;
    justify-content: center;
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }

  .trend-chart-mode-toggle button.active {
    border-color: #3fa9f5;
    color: #3fa9f5;
    background: rgba(63,169,245,0.10);
    box-shadow: 0 0 0 1px rgba(63,169,245,0.12), 0 0 12px rgba(63,169,245,0.10);
  }

  .trend-icon-line {
    position: relative;
    display: inline-block;
    width: 18px;
    height: 12px;
  }

  .trend-icon-line::before {
    content: "";
    position: absolute;
    left: 1px;
    right: 1px;
    top: 6px;
    height: 2px;
    border-radius: 999px;
    background: currentColor;
    transform: rotate(-22deg);
    transform-origin: center;
  }

  .trend-icon-bar {
    display: inline-flex;
    align-items: flex-end;
    gap: 2px;
    width: 18px;
    height: 13px;
  }

  .trend-icon-bar i {
    width: 4px;
    border-radius: 2px 2px 0 0;
    background: currentColor;
  }

  .trend-icon-bar i:nth-child(1) { height: 7px; }
  .trend-icon-bar i:nth-child(2) { height: 12px; }
  .trend-icon-bar i:nth-child(3) { height: 9px; }

  @container mg-viewport (max-width: 1120px) {
    .trend-year-panel {
      grid-template-columns: 1fr;
      align-items: flex-start;
    }
  }


  /* ===== Calendar v33 trend requested layout tweaks ===== */

  .platform-chart-title-left {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .trend-chart-mode-toggle.inline {
    margin: 0 !important;
    justify-content: flex-start !important;
    gap: 6px !important;
  }

  .trend-chart-mode-toggle.inline button {
    height: 28px !important;
    min-width: 64px !important;
    padding: 0 8px !important;
    font-size: 11.5px !important;
  }

  .trend-chart-mode-toggle.inline .trend-icon-line {
    width: 16px;
    height: 10px;
  }

  .trend-chart-mode-toggle.inline .trend-icon-bar {
    width: 16px;
    height: 11px;
  }

  @container mg-viewport (max-width: 860px) {
    .platform-chart-title-row {
      align-items: flex-start;
      flex-direction: column;
    }

    .platform-chart-title-left {
      flex-wrap: wrap;
    }
  }


  /* ===== Calendar v35 trend fix: no card auto-scroll, compact no-scroll charts ===== */

  .platform-trends-view {
    height: calc(calc(var(--mg-vh) * 100) - 28px) !important;
    min-height: 0 !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 14px 18px 12px 18px !important;
    border-radius: 0 0 16px 16px !important;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
  }

  .platform-trends-header {
    flex: 0 0 auto !important;
    margin-bottom: 8px !important;
    padding-bottom: 8px !important;
  }

  .platform-trends-header h1 {
    font-size: clamp(24px, calc(var(--mg-vw) * 2.2), 36px) !important;
    margin-bottom: 3px !important;
  }

  .trend-year-panel {
    flex: 0 0 auto !important;
    grid-template-columns: minmax(190px, 0.8fr) minmax(360px, 1.4fr) auto auto !important;
    gap: 10px !important;
    margin-bottom: 8px !important;
    padding: 8px 10px !important;
    border-radius: 0 0 12px 12px !important;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
  }

  .trend-year-summary span { display: block; }

  .platform-trend-controls {
    margin: 0 !important;
    justify-content: center !important;
    gap: 6px !important;
  }

  .platform-trend-toggle {
    min-height: 28px !important;
    padding: 0 9px !important;
    font-size: 11px !important;
  }

  .trend-year-actions button,
  .trend-range-loader select,
  .trend-chart-mode-toggle button { height: 28px !important; }

  .platform-chart-card {
    flex: 1 1 0 !important;
    min-height: 0 !important;
    margin-bottom: 8px !important;
    padding: 10px 12px 6px 12px !important;
    border-radius: 0 0 14px 14px !important;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
  }

  .platform-chart-title-row { margin-bottom: 4px !important; }

  .platform-period-slider-wrap { flex: 0 0 auto !important; margin-top: 0 !important; }
  .platform-period-slider-labels { margin-bottom: 3px !important; }
  .platform-period-slider { margin-bottom: 3px !important; }

  .platform-period-rail {
    padding: 4px 2px 0 2px !important;
    max-height: 34px !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
  }

  .platform-period-rail button {
    height: 24px !important;
    padding: 0 8px !important;
    font-size: 10.5px !important;
  }

  .calendar-main:has(.platform-trends-view) {
    height: calc(var(--mg-vh) * 100) !important;
    overflow: hidden !important;
    padding: 0 18px 0 18px !important;
  }

  @container mg-viewport (max-width: 1120px) {
    .trend-year-panel { grid-template-columns: 1fr !important; }
  }


  /* ===== Calendar v36 trend fixes: current-year default, centered chips, full-height charts, no page scroll ===== */

  .calendar-page:has(.platform-trends-view) {
    height: calc(var(--mg-vh) * 100) !important;
    min-height: calc(var(--mg-vh) * 100) !important;
    overflow: hidden !important;
  }

  .calendar-main:has(.platform-trends-view) {
    height: calc(var(--mg-vh) * 100) !important;
    min-height: 0 !important;
    overflow: hidden !important;
    padding: 0 16px !important;
    box-sizing: border-box !important;
  }

  .platform-trends-view {
    height: calc(var(--mg-vh) * 100) !important;
    min-height: 0 !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 12px 18px 10px 18px !important;
    box-sizing: border-box !important;
  }

  .platform-trends-header {
    margin-bottom: 6px !important;
    padding-bottom: 7px !important;
  }

  .platform-trends-header h1 {
    font-size: clamp(23px, calc(var(--mg-vw) * 2), 34px) !important;
    margin-bottom: 2px !important;
  }

  .platform-trends-header p {
    font-size: 12px !important;
  }

  .trend-year-panel {
    grid-template-columns: minmax(250px, 1fr) auto minmax(250px, 1fr) !important;
    grid-template-areas: "summary chips actions" "summary chips range" !important;
    align-items: center !important;
    gap: 4px 12px !important;
    margin-bottom: 7px !important;
    padding: 7px 10px !important;
    border-radius: 0 !important;
  }

  .trend-year-summary { grid-area: summary !important; }
  .platform-trend-controls {
    grid-area: chips !important;
    justify-content: center !important;
    justify-self: center !important;
    margin: 0 !important;
    max-width: none !important;
  }
  .trend-year-actions { grid-area: actions !important; justify-self: end !important; }
  .trend-range-loader { grid-area: range !important; justify-self: end !important; }

  .platform-chart-card {
    flex: 1 1 0 !important;
    min-height: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 8px 10px 7px 10px !important;
    margin-bottom: 7px !important;
    border-radius: 0 0 12px 12px !important;
  }

  .platform-chart-title-row {
    flex: 0 0 auto !important;
    margin-bottom: 3px !important;
  }

  .platform-chart-body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    width: 100% !important;
  }

  .platform-period-slider-wrap {
    flex: 0 0 auto !important;
    margin-top: 0 !important;
  }

  .platform-period-slider-labels { margin-bottom: 2px !important; }
  .platform-period-slider { margin-bottom: 2px !important; }

  .platform-period-rail {
    max-height: 31px !important;
    padding-top: 3px !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
  }

  .platform-period-rail button {
    height: 23px !important;
    padding: 0 8px !important;
  }

  @container mg-viewport (max-width: 1120px) {
    .trend-year-panel {
      grid-template-columns: 1fr !important;
      grid-template-areas: "summary" "chips" "actions" "range" !important;
    }
    .trend-year-actions,
    .trend-range-loader { justify-self: center !important; }
  }


  /* ===== Calendar v37 sidebar jump controls + low-score color hover ===== */

  .sidebar-section-title {
    display: flex !important;
    align-items: center !important;
    gap: 10px !important;
    margin: 10px 0 8px 0 !important;
    color: #8a8a8a !important;
    font-size: 11px !important;
    font-weight: 900 !important;
    letter-spacing: 0.16em !important;
    text-transform: uppercase !important;
    line-height: 1 !important;
  }

  .sidebar-section-title::before,
  .sidebar-section-title::after {
    content: "" !important;
    height: 1px !important;
    flex: 1 1 auto !important;
    background: rgba(255,255,255,0.16) !important;
  }

  .sidebar-section-title span {
    flex: 0 0 auto !important;
  }

  .filters-title {
    margin-top: 12px !important;
  }

  .jump-select-row {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 8px !important;
    margin-bottom: 8px !important;
  }

  .jump-select-row .month-select {
    margin-bottom: 0 !important;
    min-width: 0 !important;
    padding-right: 8px !important;
  }

  .jump-month-select,
  .jump-year-select {
    color: #d7dde5 !important;
  }

  /* Low-score/dark cards now get the same art bulge interaction. They stay
     grayscale at rest, briefly restore color on hover/expanded preview, then
     return to grayscale when the hover collapses. */
  .release-card.low-score-release.hover-expandable-art:hover .release-img {
    filter: saturate(1.08) brightness(1.02) contrast(1) !important;
  }

  .release-card.low-score-release.hover-expandable-art:hover .release-bg {
    filter: blur(10px) brightness(0.38) saturate(1.18) !important;
  }

  .release-card.low-score-release.hover-expandable-art .release-hover-art-img {
    filter: saturate(1.08) brightness(1.04) !important;
  }

  .release-card.low-score-release.hover-expandable-art .release-hover-art-bg {
    filter: blur(13px) brightness(0.42) saturate(1.2) !important;
  }

  .release-card.low-score-release.hover-expandable-art .release-hover-art {
    border-color: rgba(170,185,198,0.60) !important;
    box-shadow:
      0 22px 52px rgba(0,0,0,0.82),
      0 0 0 1px rgba(0,0,0,0.65),
      0 0 26px rgba(170,185,198,0.18) !important;
  }



  /* ===== Calendar v38 gap year loader + modal sizing cleanup ===== */

  .calendar-year-loader-row.gap {
    padding: 4px 0 44px 0 !important;
  }

  .jump-select-row {
    grid-template-columns: 1fr 1fr !important;
  }

  .day-modal .modal-grid .release-card.modal {
    height: 132px !important;
    min-height: 132px !important;
  }


  /* ===== Calendar v39 progressive year gaps + taller cards + sidebar jump polish ===== */

  /* Slightly taller day cards so the art has more vertical breathing room. */
  .calendar-cell {
    min-height: 316px !important;
  }

  .calendar-cell.major-day:not(.gold-day) {
    min-height: 336px !important;
  }

  .no-games {
    min-height: 316px !important;
  }

  @container mg-viewport (max-width: 1500px) {
    .calendar-cell {
      min-height: 276px !important;
    }

    .calendar-cell.major-day:not(.gold-day) {
      min-height: 298px !important;
    }

    .no-games {
      min-height: 276px !important;
    }
  }

  /* Jump dropdowns: equal width, centered labels/options, and a subtle chevron
     that only appears on hover/focus so the boxes stay clean at rest. */
  .jump-select-row {
    grid-template-columns: 1fr 1fr !important;
    gap: 8px !important;
  }

  .jump-select-row .month-select {
    text-align: center !important;
    text-align-last: center !important;
    appearance: none !important;
    -webkit-appearance: none !important;
    padding-left: 26px !important;
    padding-right: 26px !important;
    background-image: none !important;
    background-repeat: no-repeat !important;
    background-position: calc(100% - 18px) 50% !important;
    background-size: 10px 10px !important;
  }

  .jump-select-row .month-select:hover,
  .jump-select-row .month-select:focus {
    background-image:
      linear-gradient(45deg, transparent 50%, #bfc4ca 50%),
      linear-gradient(135deg, #bfc4ca 50%, transparent 50%) !important;
    background-position:
      calc(100% - 20px) 50%,
      calc(100% - 14px) 50% !important;
    background-size:
      6px 6px,
      6px 6px !important;
  }

  .jump-select-row .month-select option {
    text-align: center !important;
  }

  /* When a top-row card expands, let the art preview float above the sticky
     month/platform banner instead of disappearing behind it. */
  .calendar-month-section:has(.release-card.hover-expandable-art:hover) .month-title-row {
    z-index: 120 !important;
  }

  .calendar-month-section:has(.release-card.hover-expandable-art:hover) .calendar-grid {
    z-index: 5000 !important;
  }

  .calendar-cell:hover {
    z-index: 9000 !important;
  }

  .release-card.hover-expandable-art:hover,
  .release-card.hover-expandable-art:hover .release-hover-art {
    z-index: 20000 !important;
  }

  .release-hover-art {
    z-index: 20000 !important;
  }



  /* ===== Calendar v40 tighter hover metadata layout ===== */

  .hover-title {
    margin-bottom: 4px !important;
  }

  .hover-date {
    display: none !important;
  }

  .hover-meta-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0 0 7px 0;
    color: #9fb7c9;
    font-size: 11.5px;
    line-height: 1.2;
    font-weight: 650;
  }

  .hover-meta-row i {
    color: rgba(159,183,201,0.55);
    font-style: normal;
    font-weight: 400;
  }

  .hover-platform-release-spacer {
          height: 6px;
     }

  .hover-platform-release-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin: 0 0 8px 0;
  }

  .hover-platform-release-row .platform-release-chip {
    margin: 0 !important;
    padding: 2px 6px !important;
    border-radius: 6px !important;
    font-size: 11.5px !important;
    line-height: 1.15 !important;
  }

  .hover-platform-release-row .platform-release-platform {
    font-weight: 900 !important;
  }

  .hover-platform-release-row .platform-release-date {
    font-size: 11px !important;
    font-weight: 650 !important;
  }

  .hover-platform-releases {
    display: none !important;
  }

  .hover-inline-stats {
    margin: 0 0 9px 0 !important;
  }

  .hover-desc {
    margin-top: 0 !important;
  }



  /* ===== Calendar v41 release phase labels / Early Access ===== */

  .release-phase-badge {
    position: absolute;
    top: 5px;
    left: 5px;
    z-index: 9;
    max-width: calc(100% - 10px);
    padding: 2px 5px;
    border-radius: 5px;
    background: rgba(63,169,245,0.18);
    border: 1px solid rgba(63,169,245,0.42);
    color: #dff3ff;
    font-size: 9px;
    line-height: 1;
    font-weight: 950;
    letter-spacing: 0.035em;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: none;
    box-shadow: 0 4px 12px rgba(0,0,0,0.32);
  }

  .release-phase-badge.early {
    background: rgba(255,194,57,0.16);
    border-color: rgba(255,219,111,0.55);
    color: #ffe58a;
  }

  .release-card.strip .release-phase-badge {
    display: none !important;
  }

  .release-card.modal .release-phase-badge {
    display: inline-flex !important;
  }

  .hover-release-label {
    color: #dff3ff;
    font-weight: 800;
  }

  .hover-release-label.early {
    color: #ffe58a;
  }

  .release-card.early-access-release:not(.gold-release) {
    box-shadow:
      0 6px 14px rgba(0,0,0,0.42),
      inset 0 0 0 1px rgba(255,219,111,0.20);
  }

  /* ===== Calendar v42 global game search + right-side release labels ===== */

  .global-search-block {
    position: relative !important;
    z-index: 3000 !important;
  }

  .global-search-panel {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 6px);
    z-index: 5000;
    border: 1px solid rgba(63,169,245,0.34);
    background:
      radial-gradient(360px 180px at 50% 0%, rgba(63,169,245,0.15), transparent 60%),
      rgba(13,16,22,0.985);
    border-radius: 10px;
    box-shadow: 0 18px 44px rgba(0,0,0,0.72);
    overflow: hidden;
  }

  .global-search-panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 7px 8px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    color: #9fb7c9;
    font-size: 11px;
    line-height: 1;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .global-search-panel-head button {
    width: 20px;
    height: 20px;
    border: 1px solid rgba(255,255,255,0.13);
    border-radius: 999px;
    background: rgba(255,255,255,0.045);
    color: #cfd8e3;
    cursor: pointer;
    line-height: 1;
    padding: 0;
  }

  .global-search-load-all {
    width: calc(100% - 12px);
    margin: 6px;
    height: 28px;
    border-radius: 7px;
    border: 1px solid rgba(63,169,245,0.44);
    background: rgba(63,169,245,0.12);
    color: #dff3ff;
    font-size: 11.5px;
    font-weight: 900;
    cursor: pointer;
  }

  .global-search-load-all:hover:not(:disabled) {
    background: rgba(63,169,245,0.20);
    border-color: rgba(126,225,255,0.75);
  }

  .global-search-load-all:disabled {
    opacity: 0.55;
    cursor: wait;
  }

  .global-search-results {
    max-height: 340px;
    overflow-y: auto;
    padding: 3px 6px 6px 6px;
    scrollbar-color: #3fa9f5 #111;
  }

  .global-search-result {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
    align-items: center;
    min-height: 34px;
    border: 0;
    border-bottom: 1px solid rgba(255,255,255,0.055);
    background: transparent;
    color: #d7e2ea;
    text-align: left;
    cursor: pointer;
    padding: 6px 5px;
  }

  .global-search-result:hover:not(:disabled) {
    background: rgba(63,169,245,0.10);
  }

  .global-search-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12.5px;
    line-height: 1.15;
    font-weight: 850;
  }

  .global-search-meta {
    color: #8fa3b6;
    font-size: 10.5px;
    line-height: 1;
    font-weight: 800;
    white-space: nowrap;
  }

  .global-search-empty {
    padding: 10px 8px;
    color: #8fa3b6;
    font-size: 12px;
    font-weight: 800;
  }

  .release-phase-badge {
    left: auto !important;
    right: 5px !important;
    max-width: calc(100% - 38px) !important;
  }

  .release-card.has-release-label .source-pill {
    top: 23px !important;
    right: 5px !important;
  }

  .release-card.strip .release-phase-badge {
    display: none !important;
  }

  .release-card.modal .release-phase-badge {
    right: 6px !important;
    top: 6px !important;
    display: inline-flex !important;
  }



  /* ===== Calendar v43 global search visual polish + timeline mode ===== */

  .calendar-sidebar {
    overflow: visible !important;
  }

  .side-inner {
    overflow: visible !important;
  }

  .global-search-block,
  .global-search-block * {
    font-family: Segoe UI, system-ui, -apple-system, sans-serif !important;
  }

  .global-search-panel {
    left: 0 !important;
    right: auto !important;
    width: min(620px, calc(calc(var(--mg-vw) * 100) - 324px)) !important;
    min-width: 420px !important;
    background: #111 !important;
    border: 1px solid #444 !important;
    border-radius: 6px !important;
    box-shadow: 0 18px 44px rgba(0,0,0,0.72) !important;
    overflow: hidden !important;
  }

  .global-search-panel-head {
    min-height: 34px !important;
    padding: 8px 10px !important;
    background: #111 !important;
    border-bottom: 1px solid #333 !important;
    color: #8a8a8a !important;
    font-size: 11px !important;
    font-weight: 900 !important;
    letter-spacing: 0.16em !important;
  }

  .global-search-panel-head button {
    border-color: #444 !important;
    background: #111 !important;
    color: #bbb !important;
  }

  .global-search-load-all {
    width: calc(100% - 16px) !important;
    margin: 8px !important;
    height: 34px !important;
    background: #111 !important;
    color: #bbb !important;
    border: 1px solid #444 !important;
    border-radius: 6px !important;
    font-size: 14px !important;
    font-weight: 400 !important;
  }

  .global-search-load-all:hover:not(:disabled) {
    border-color: rgba(255,255,255,0.82) !important;
    background: #111 !important;
    color: #fff !important;
  }

  .global-search-results {
    max-height: 390px !important;
    padding: 0 !important;
    background: #111 !important;
  }

  .global-search-result {
    min-height: 42px !important;
    grid-template-columns: minmax(0, 1fr) auto !important;
    gap: 16px !important;
    padding: 8px 10px !important;
    border-bottom: 1px solid #272727 !important;
    background: #111 !important;
    color: #d7dde5 !important;
  }

  .global-search-result:hover:not(:disabled) {
    background: #1b1b1b !important;
    color: #fff !important;
  }

  .global-search-name {
    font-size: 15px !important;
    font-weight: 400 !important;
    line-height: 1.2 !important;
  }

  .global-search-meta {
    color: #8fa3b6 !important;
    font-size: 12px !important;
    font-weight: 700 !important;
  }

  .global-search-empty {
    background: #111 !important;
    color: #8a8a8a !important;
    font-size: 14px !important;
    font-weight: 400 !important;
  }

  .search-view-toggle {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 8px !important;
    margin: 0 0 2px 0 !important;
  }

  .search-view-toggle button {
    height: 32px !important;
    background: #111 !important;
    color: rgba(187,187,187,0.75) !important;
    border: 1px solid #444 !important;
    border-radius: 6px !important;
    font-size: 13px !important;
    font-weight: 400 !important;
    cursor: pointer !important;
  }

  .search-view-toggle button.active {
    border-color: #3fa9f5 !important;
    color: #3fa9f5 !important;
    background: rgba(63,169,245,0.10) !important;
  }

  .search-view-toggle button:hover {
    border-color: rgba(255,255,255,0.82) !important;
  }

  .global-search-timeline-view {
    min-height: calc(calc(var(--mg-vh) * 100) - 42px);
    padding: 20px 24px 70px 24px;
    background:
      radial-gradient(800px 400px at 50% 0%, rgba(63,169,245,0.10), transparent 60%),
      #14171c;
    border-left: 1px solid rgba(255,255,255,0.04);
  }

  .global-search-timeline-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    max-width: 1180px;
    margin: 0 auto 28px auto;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba(255,255,255,0.10);
  }

  .global-search-kicker {
    color: #b58cff;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 11px;
    line-height: 1;
    font-weight: 950;
    margin-bottom: 6px;
  }

  .global-search-timeline-header h1 {
    margin: 0 0 5px 0;
    font-size: clamp(30px, calc(var(--mg-vw) * 4), 58px);
    line-height: 0.95;
    letter-spacing: -0.05em;
    font-weight: 950;
  }

  .global-search-timeline-header p {
    margin: 0;
    color: #9fb7c9;
    font-size: 13px;
    font-weight: 800;
  }

  .global-search-view-actions {
    display: inline-flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .global-search-view-actions button {
    height: 34px;
    border-radius: 8px;
    border: 1px solid rgba(181,140,255,0.38);
    background: rgba(181,140,255,0.10);
    color: #eadcff;
    padding: 0 12px;
    font-size: 12px;
    font-weight: 900;
    cursor: pointer;
  }

  .global-search-view-actions button:hover {
    border-color: rgba(213,190,255,0.82);
    background: rgba(181,140,255,0.18);
  }

  .global-search-timeline-empty {
    max-width: 860px;
    margin: 70px auto;
    color: #9fb7c9;
    text-align: center;
    font-size: 14px;
    font-weight: 800;
  }

  .global-search-timeline {
    position: relative;
    max-width: 1180px;
    margin: 0 auto;
    padding: 18px 0 40px 0;
  }

  .global-search-timeline::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    transform: translateX(-50%);
    background: linear-gradient(180deg, transparent, rgba(255,255,255,0.16), rgba(255,255,255,0.16), transparent);
  }

  .timeline-node-row {
    position: relative;
    min-height: 172px;
    display: grid;
    grid-template-columns: 1fr 88px 1fr;
    align-items: center;
    gap: 20px;
    margin: 0 0 10px 0;
  }

  .timeline-node-row::before {
    content: "";
    position: absolute;
    top: 50%;
    left: calc(50% - 1px);
    width: 34%;
    height: 1px;
    background: rgba(255,255,255,0.13);
  }

  .timeline-node-row.left::before {
    transform: translateX(-100%);
  }

  .timeline-node-row.right::before {
    transform: translateX(0);
  }

  .timeline-date-pill {
    position: relative;
    z-index: 2;
    grid-column: 2;
    justify-self: center;
    min-width: 62px;
    min-height: 38px;
    display: grid;
    place-items: center;
    border-radius: 999px;
    background: #12151a;
    border: 1px solid rgba(255,255,255,0.18);
    color: #fff;
    box-shadow: 0 10px 22px rgba(0,0,0,0.35);
  }

  .timeline-date-pill span {
    font-size: 13px;
    line-height: 1;
    font-weight: 950;
  }

  .timeline-date-pill em {
    margin-top: -3px;
    color: #8fa3b6;
    font-style: normal;
    font-size: 10px;
    line-height: 1;
    font-weight: 850;
  }

  .timeline-release-card {
    position: relative;
    z-index: 3;
    width: min(300px, 100%);
    min-height: 164px;
    border: 0;
    border-radius: 8px;
    overflow: hidden;
    background: #05070a;
    cursor: pointer;
    padding: 0;
    box-shadow: 0 20px 40px rgba(0,0,0,0.46);
  }

  .timeline-node-row.left .timeline-release-card {
    grid-column: 1;
    justify-self: end;
  }

  .timeline-node-row.right .timeline-release-card {
    grid-column: 3;
    justify-self: start;
  }

  .timeline-release-count {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 5;
    height: 26px;
    display: grid;
    place-items: center;
    background: linear-gradient(180deg, #c794ff, #9a63ee);
    color: #fff;
    font-size: 11px;
    font-weight: 950;
    text-transform: uppercase;
  }

  .timeline-release-card img,
  .timeline-missing-art {
    width: 100%;
    height: 164px;
    display: block;
    object-fit: cover;
    object-position: center center;
    background: #05070a;
  }

  .timeline-missing-art {
    display: grid;
    place-items: center;
    color: rgba(255,255,255,0.48);
    font-size: 13px;
    font-weight: 900;
  }

  .timeline-card-footer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 4;
    padding: 34px 10px 9px 10px;
    text-align: left;
    background: linear-gradient(180deg, transparent, rgba(0,0,0,0.86) 44%, rgba(0,0,0,0.94));
  }

  .timeline-card-footer strong {
    display: block;
    color: #fff;
    font-size: 13px;
    line-height: 1.05;
    font-weight: 950;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-shadow: 0 2px 8px rgba(0,0,0,0.9);
  }

  .timeline-card-footer span {
    display: block;
    margin-top: 3px;
    color: #d6c5ff;
    font-size: 11px;
    line-height: 1.1;
    font-weight: 800;
  }

  .timeline-release-card:hover {
    transform: translateY(-2px);
    box-shadow:
      0 24px 50px rgba(0,0,0,0.56),
      0 0 0 1px rgba(181,140,255,0.65),
      0 0 26px rgba(181,140,255,0.18);
  }

  @container mg-viewport (max-width: 900px) {
    .global-search-panel {
      width: min(520px, calc(calc(var(--mg-vw) * 100) - 34px)) !important;
      min-width: 0 !important;
    }

    .global-search-timeline::before {
      left: 28px;
    }

    .timeline-node-row {
      grid-template-columns: 72px 1fr;
      gap: 12px;
      min-height: 166px;
    }

    .timeline-node-row::before {
      left: 28px;
      width: 32px;
      transform: none !important;
    }

    .timeline-date-pill {
      grid-column: 1;
    }

    .timeline-node-row.left .timeline-release-card,
    .timeline-node-row.right .timeline-release-card {
      grid-column: 2;
      justify-self: start;
    }
  }


  /* ===== Calendar v44 timeline/dashboard corrections ===== */

  .calendar-sidebar,
  .side-inner,
  .global-search-block {
    z-index: 20000 !important;
  }

  .global-search-panel {
    z-index: 30000 !important;
    transform: translateZ(0) !important;
  }

  .calendar-main {
    position: relative !important;
    z-index: 1 !important;
  }

  .global-search-timeline-view {
    padding: 18px 26px 58px 26px !important;
    background:
      radial-gradient(900px 460px at 50% 0%, rgba(63,169,245,0.13), transparent 60%),
      linear-gradient(180deg, #0b0f14 0%, #10141a 54%, #080a0e 100%) !important;
    border-left: 1px solid rgba(63,169,245,0.10) !important;
  }

  .global-search-timeline-header {
    margin-bottom: 20px !important;
    border-bottom: 2px solid rgba(63,169,245,0.48) !important;
  }

  .global-search-kicker {
    color: #3fa9f5 !important;
  }

  .global-search-timeline-header h1 {
    text-shadow: 0 10px 30px rgba(0,0,0,0.55) !important;
  }

  .global-search-view-actions button {
    border: 1px solid rgba(63,169,245,0.42) !important;
    background: rgba(63,169,245,0.10) !important;
    color: #dff3ff !important;
    border-radius: 8px !important;
    box-shadow: 0 0 16px rgba(63,169,245,0.08) !important;
  }

  .global-search-view-actions button:hover:not(:disabled) {
    border-color: rgba(126,225,255,0.82) !important;
    background: rgba(63,169,245,0.18) !important;
  }

  .global-search-view-actions button:disabled {
    opacity: 0.35 !important;
    cursor: default !important;
  }

  .global-search-timeline {
    padding: 8px 0 36px 0 !important;
  }

  .global-search-timeline::before {
    width: 5px !important;
    border-radius: 999px !important;
    background:
      linear-gradient(180deg, transparent, rgba(63,169,245,0.26) 6%, rgba(63,169,245,0.70) 50%, rgba(63,169,245,0.26) 94%, transparent) !important;
    box-shadow:
      0 0 0 1px rgba(63,169,245,0.20),
      0 0 24px rgba(63,169,245,0.28) !important;
  }

  .timeline-node-row {
    min-height: 132px !important;
    grid-template-columns: 1fr 82px 1fr !important;
    gap: 18px !important;
    margin: -4px 0 !important;
  }

  .timeline-node-row::before {
    height: 3px !important;
    border-radius: 999px !important;
    background: linear-gradient(90deg, rgba(63,169,245,0.06), rgba(63,169,245,0.46)) !important;
    box-shadow: 0 0 16px rgba(63,169,245,0.18) !important;
  }

  .timeline-node-row.left::before {
    background: linear-gradient(90deg, rgba(63,169,245,0.46), rgba(63,169,245,0.06)) !important;
  }

  .timeline-date-pill {
    min-width: 66px !important;
    min-height: 42px !important;
    background:
      radial-gradient(60px 38px at 50% 0%, rgba(63,169,245,0.20), transparent 70%),
      #0d1117 !important;
    border: 1px solid rgba(63,169,245,0.48) !important;
    box-shadow:
      0 0 0 1px rgba(0,0,0,0.65),
      0 0 24px rgba(63,169,245,0.24),
      0 12px 24px rgba(0,0,0,0.45) !important;
  }

  .timeline-release-card {
    width: min(276px, 100%) !important;
    min-height: 132px !important;
    border-radius: 10px !important;
    border: 1px solid rgba(63,169,245,0.18) !important;
    box-shadow:
      0 16px 34px rgba(0,0,0,0.48),
      0 0 0 1px rgba(255,255,255,0.035) !important;
  }

  .timeline-release-card img,
  .timeline-missing-art {
    height: 132px !important;
  }

  .timeline-release-count {
    height: 24px !important;
    background: linear-gradient(180deg, #55ccff, #0a8dca) !important;
    color: #00111d !important;
    letter-spacing: 0.03em !important;
  }

  .timeline-card-footer span {
    color: #9fd8ff !important;
  }

  .timeline-release-card:hover {
    box-shadow:
      0 22px 48px rgba(0,0,0,0.58),
      0 0 0 1px rgba(63,169,245,0.72),
      0 0 30px rgba(63,169,245,0.24) !important;
  }

  .timeline-remove-card {
    position: absolute;
    top: 5px;
    right: 6px;
    z-index: 12;
    width: 20px;
    height: 20px;
    display: grid;
    place-items: center;
    border-radius: 999px;
    background: rgba(0,0,0,0.58);
    border: 1px solid rgba(255,255,255,0.18);
    color: #d7dde5;
    font-size: 16px;
    line-height: 1;
    font-weight: 700;
    opacity: 0;
    transition: opacity 0.14s ease, border-color 0.14s ease, color 0.14s ease;
  }

  .timeline-release-card:hover .timeline-remove-card {
    opacity: 1;
  }

  .timeline-remove-card:hover {
    border-color: rgba(126,225,255,0.82);
    color: #fff;
  }




  /* ===== Calendar v45 separate lazy Timeline tab ===== */

  .calendar-view-toggle {
    grid-template-columns: 1fr 1fr 1fr !important;
  }

  .timeline-search-block {
    position: relative !important;
    z-index: 30000 !important;
  }

  .calendar-main:has(.global-search-timeline-view) {
    padding-top: 0 !important;
  }


`;
